/**
 * ShareViewerChrome — minimal viewer for share-link recipients.
 *
 * Phase 6 of the share-session unification (docs/plans/
 * 2026-06-03_SHARE_SESSION_UNIFICATION.md).
 *
 * Decision 3 shape: distinct minimal chrome (NOT the full SPA Drive UI).
 * Top bar with file name + owner attribution + Save/Open-in-Drive/Download
 * buttons; body shows the rendered file in its appropriate viewer.
 *
 * The body opens a file in the SAME editor the drive would (the editor
 * registry, via `chooseShareEditor` — see renderFileViewer), capped for the
 * share: read-only where the link or the server can't write, no Run for
 * scripts. What nothing in the registry can render here falls back to a
 * sandboxed iframe of `/s/<sessionId>/<filename>` — the preview-serve surface
 * that already gates share-kind sessions.
 */
import { Fragment, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Download, Loader2, Link as LinkIcon, ExternalLink, ChevronRight, Check, User, Building2, Mail, LogOut, Star, X, MoreVertical, Copy, FolderTree, HardDriveDownload, Save, Undo2 } from 'lucide-react'
import { BrandLogo, useBrand } from '@papan/brand/react'
import { getBrandSnapshot } from '@papan/brand/client'
import { SharedFolderView, SHARE_ROW_REFUSED_EVENT } from './share/SharedFolderView'
import { ShareFileExplorer } from './share/ShareFileExplorer'
import { MountRequestedDialog } from './share/MountRequestedDialog'
import { useShareEditorRecovery } from './share/useShareEditorRecovery'
import { AttributionDisclosureHost, AttributionEditBanner, ATTRIBUTION_ACCEPTED_EVENT, type AttributionDisclosureInfo } from './share/AttributionDisclosure'
import { decodeSessionPayload, routeCompound, sessionHeader } from './share/sessionToken'
import {
  LAPSE_MARGIN_MS, LAPSE_PROBE_INTERVAL_MS, claimShareReentry, clearShareReentryGuard, locationQuery, parseLandingContext, reentryLandingFrom, reentryLandingFromServer, resolveReentryLanding,
  sessionStorageOrNull, stashReentryLanding, takeReentryLanding, type ShareLandingContext,
} from './share/reentry'
import { bindShareSession } from './share/binding'
import { PdappPanelHeader, PdappEmptyState, type SharePdappMode } from './share/PdappPanelHeader'
import { SharePdappRunView } from './share/SharePdappRunView'
import { SharePdappSettings } from './share/SharePdappSettings'
import {
  resolvePdappFolderId, pdappParamTakesEffect, shareRootIsPdapp, lookupPdappEntry, pdappCanManage, pdappGateHolds, parsePdappFace,
  PDAPP_PARAM, VIEW_PARAM, PDAPP_LIST_PAGE,
  type PdappEntryLookup, type PdappListPage, type PdappFace,
} from './share/pdapp'
import { AuthPromptModal } from '../components/AuthPromptModal'
import { useAuth } from '../hooks/useAuth'
import { announceSignedOut } from '../lib/sign-out-broadcast'
import { setAccountEndShareFrontDoor } from '../lib/reloadAfterAccountEnd'
import { revokeShareVisit } from '../lib/shareVisitRevoke'
import { useLeaveOnSignOutElsewhere } from './share/useLeaveOnSignOut'
import { useShareSessionKeptNonce } from './share/useShareSessionKeptNonce'
import { useIsPhone } from '../hooks/useIsPhone'
import { MobileSheet } from '../mobile/MobileSheet'
import { AccountSheet, LogOutCard, type IdentityRow } from '@papan/core-ui/ui'
import { endShareVisit, leaveShareView, submitShareLeave, goToShareSession, goToOpenFileTarget } from './share/shareLeave'
import { orgsApi, type TenantInfo } from '@papan/orgs/client'
import { useTheme, getThemeClass, FileIcon, avatarBg, getInitials, Tooltip, startPanelResize, useConfirm, useToast, PANEL_INSET, ThemeToggle, EditorCoverProvider, useCoverEditor, type EditorCover } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { FolderLinkedDownloadDialog } from '@papan/files/ui/pointer-download'
import { ResizeHandle, useSaveBlockedNotice } from '@papan/workspace/ui'
import { setShareSessionTokenGetter, getApiBase, sessionFetch, currentSessionSignal, sessionEndedSince, isSessionEndedError, errorText } from '@papan/api-client'
import { onSessionRotated } from '@papan/auth/session-rotation'
import { COLLAB_SESSION_REVOKED_EVENT, isMiniappFolder, defaultIconSpecFor, iconSpecHash, iconCacheKey, type MiniappIconSpec } from '@papan/types'
import { FileIOProvider, ShareWsTokenProvider, shareFileIO } from '@papan/files/client/file-io'
import {
  ReadOnlyText,
  LargeCsvGrid,
  EditorMount,
  type OpenFileTargetWire,
  type OpenPdappTargetWire,
  shareSessionContext, type ShareSessionContext,
} from '@papan/collab/ui'
// The share view opens a file with the DRIVE'S editor registry (I111): which
// editor a file gets is `chooseShareEditor` — the registry's own choice (the same
// preferred-editor + txt-fallback rule TabContent uses) with the share view's
// constraints applied as explicit CAPS on it (read-only where the link or the
// server can't write, no Run for scripts). This file holds NO extension list of its
// own; `share-view-editor-allowlist-guard.test.ts` keeps it that way. Importing the
// client barrel runs the registrations, so the registry is populated.
import { shareServeUrl, seedServeToken, serveTokenRetryDelay, useServeTokenUrl, useServeTokenFor, useServeTokenState, chooseShareEditor, shareRawNeedsProbe, shareNeedsFragmentEditsAnswer, useShareFragmentEdits, shareChoiceSaveableKind, editorFamilyForName, type ShareEditorChoice, type ShareFileProbe } from '@papan/collab/client'
import { useShareFileProbe } from './share/useShareFileProbe'
import { openFileAfterFailure, refreshRenamedSessionInfo } from './share/openFileState'
import { useShareOfficeSocket } from '@papan/realtime/client'

interface SessionInfo {
  /** What a session token used to carry readably (#791: a v2 token seals it). Absent from an
   *  older API mid rolling deploy — callers fall back to a v1 token's payload. */
  session?: {
    rootId: string
    fileId: string
    /** The id that re-opens this session's item (a reshare's route compound). */
    reopenFileId: string
    /** Admitted through the visitor's own native access (not the link's list). */
    admittedNatively?: boolean
    expiresAt: number
    identity: {
      verifiedEmail: string | null
      actingTenantId: string | null
      actingEmail: string | null
      identityKey: string
    }
  }
  /** #776: set while this restricted-link visitor has not accepted that their edits are
   *  recorded by email (and org) — editors open read-only until they do. */
  attributionDisclosure?: AttributionDisclosureInfo
  file: {
    id: string
    name: string
    isFolder: boolean
    mimeType: string | null
  }
  share: {
    /** The link's id (consent / accounts embed); absent from an older API. */
    id?: string
    /** Share-link URL token. Used for /:token/save and /:token/mount. */
    token: string
    kind: 'app' | 'quick_view'
    /** (0119) The owner disallowed Add-to-Drive on this link — the button
     *  renders disabled; the mount endpoint rejects server-side too. */
    forbidAddToDrive?: boolean
    permission: string
    liveMode: boolean
    verifiedEmail: string | null
    /** Owner-side Y.Doc collab room (set when liveMode=true). */
    liveRoom: string | null
    /** #1518 D10: the link was minted by an MCP connection — no live room; text opens read-only over /s/
     *  (masked) and the office editor opens view-only. Absent on an ordinary link / an older API. */
    connectionLink?: boolean
    /** Always present. Drives the folder-share explorer sidebar — when
     *  rootIsFolder is true and the current `file` is NOT a folder
     *  (sub-file sub-session), the chrome renders a split layout so
     *  the recipient can switch between files without going back to
     *  the listing. */
    rootFileId: string
    rootName: string | null
    rootIsFolder: boolean
    /** The root is a `.shared` / `.chain` (D27: shown under its full name with the corner badge). */
    rootIsMount?: boolean
    rootFileType?: string | null
    /** Custom launcher icon for a native `.miniapp` root (null/absent → the
     *  name-derived default tile). Drives the breadcrumb glyph + tab favicon. */
    rootIcon?: MiniappIconSpec | null
    /** (Phase 2) Name of the session's scoped folder = the app's working folder;
     *  shown in the editor "Root:" control (drive-view parity). */
    appFolderName?: string | null
    /** The version-history right on the opened file (an Edit link or native access). */
    versionHistory?: boolean
    /** (Part 3) The scoped folder's id — the CURRENT root the picker marks active.
     *  And the opened file's path relative to the serve root, so the iframe URL resolves
     *  after a re-root (deeper than the bare filename). `rootOptions` = the FIXED candidate
     *  re-root folders (file's parent → share root), so the picker never shrinks as you
     *  re-root up (drive-view parity). */
    appFolderId?: string | null
    entryPath?: string | null
    rootOptions?: Array<{ id: string; name: string }> | null
    /** The share root is a re-shared cross-tenant mount whose source the
     *  resharer can no longer reach (revoked grant / structurally dead). The
     *  chrome renders a "no longer available" state instead of an empty
     *  explorer. Server only sets this when true (omitted otherwise). */
    crossTenantMountBroken?: boolean
  }
  ownerTenantName: string | null
  /** The tenant the content is ATTRIBUTED to — for a reshared cross-tenant
   *  mount this is the SOURCE tenant the pointer targets (matching the
   *  "Shared by …" pill), not the resharer's tenant. Optional for
   *  back-compat with older API responses. */
  ownerTenantId?: string | null
}

/**
 * The share context an embedded app's consent / identity surfaces need (share id, link
 * token, acting pair, verified email) — from the session's /info, since a v2 token seals
 * it (#791). An older API (no `info.session`): a v1 token's readable payload.
 */
function shareContextOf(i: SessionInfo | null | undefined, sid: string): ShareSessionContext | null {
  if (i?.session) {
    const id = i.session.identity
    return {
      shareId: i.share.id ?? null,
      shareToken: i.share.token,
      actingTenantId: id.actingTenantId,
      actingEmail: id.actingTenantId ? id.actingEmail : null,
      verifiedEmail: id.verifiedEmail ? id.verifiedEmail.toLowerCase() : null,
    }
  }
  return shareSessionContext(sid)
}

interface ResolverInfo {
  accessibleInActiveTenant: boolean
  driveUrl?: string
  /** True when the recipient has a Better Auth session (regardless of
   *  whether they have native access to this share). Drives the
   *  clickability of the brand-mark in the chrome top bar. */
  isAuthenticated?: boolean
  /** The recipient's own drive home — `/~/folder` for personal,
   *  `/t/<id>/folder` for org. Present whenever isAuthenticated=true. */
  userActiveTenantHref?: string
  /**
   * The recipient's active tenant is identity-only (0175, §7.2 item 4) — it
   * cannot receive a copy, so "Save a copy" and "Add to my Drive" are omitted.
   *
   * Absent means FULL, matching `isIdentityOnlyTenant`: an older API omits it,
   * and this is disclosure rather than enforcement — `POST /:token/save` refuses
   * with a coded 403 regardless (C5 slice 3), and the worker refuses the job
   * again on `destTenantId` (C4).
   */
  activeTenantIdentityOnly?: boolean
  /** The recipient's native permission on the resolved file (placeholder
   *  `readcopy` for a mount-only match). Read ONLY by the PDApp Settings gate
   *  (`pdappCanManage`) — the Drive CTAs gate on accessibility, not this. */
  permission?: string
}

type ButtonState = 'idle' | 'busy' | 'success' | 'error'

/** Map a save-status worker error code to a visitor-facing message. */
/**
 * A file opened IN PLACE over a folder share's listing (`?file=<fileId>`).
 *
 * `parentSid` is provenance, not decoration: a sub-session is only valid under
 * the route session it was minted FROM, so when the route session changes (an
 * identity re-bind mints a new one) this file must be re-minted from the new
 * parent — otherwise the app keeps running as the OLD identity, which is exactly
 * what the re-bind undoes. It is part of the type so that invariant is visible
 * at every boundary this object crosses (the chrome AND ShareBody), instead of
 * being silently dropped by a narrower prop type.
 */
interface OpenFile {
  fileId: string
  /** The file's own sub-session — what the viewer authenticates and streams with. */
  sid: string
  /** The ROUTE session `sid` was minted from. */
  parentSid: string
  info: SessionInfo
}

// ── Dynamic favicon (Phase 5 SPA share-view) ────────────────────────────
// When a `.miniapp` folder is the focused share root, swap the browser-tab
// icon to the mini-app's server-rendered PNG (through the share session's
// `/s/<sid>/_papan/...` route). The original href is captured once so unmount
// / unfocus restores the Papan favicon.
let ORIGINAL_FAVICON: string | null = null
let ORIGINAL_FAVICON_CAPTURED = false

function faviconLink(): HTMLLinkElement {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  if (!ORIGINAL_FAVICON_CAPTURED) {
    ORIGINAL_FAVICON = link.getAttribute('href')
    ORIGINAL_FAVICON_CAPTURED = true
  }
  return link
}

function setFavicon(href: string) {
  const link = faviconLink()
  link.setAttribute('type', 'image/png')
  link.setAttribute('href', href)
}

function restoreFavicon() {
  if (!ORIGINAL_FAVICON_CAPTURED) return
  const link = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!link) return
  // Prefer the BRAND's favicon over the captured original. `ORIGINAL_FAVICON` is
  // whatever was on the <link> the first time faviconLink() ran, and that is a
  // race: brand bootstrap is deliberately not awaited, so if a mini-app share
  // opens before the fetch lands we captured the default `/favicon.svg` and
  // would restore PAPAN's icon onto a differently-branded host. Reading the
  // snapshot at restore time is always correct — by then the brand has settled,
  // and if it hasn't, getBrandSnapshot() still returns a valid default.
  const brandFavicon = getBrandSnapshot().assets.favicon
  const href = brandFavicon ?? ORIGINAL_FAVICON
  if (href == null) link.removeAttribute('href')
  else link.setAttribute('href', href)
  link.removeAttribute('type')
}

export function saveStatusErrorMessage(code?: string): string {
  switch (code) {
    case 'share_unavailable': return 'This share is no longer available.'
    case 'nothing_to_save': return "Nothing to save — you don't have access to any files in this share."
    case 'not_in_allow_list': return "You're not on this share's recipient list."
    // The destination drive was already full, so nothing landed. Deliberately
    // NOT the server's storageQuotaMessage(): the recipient may be saving into
    // an org drive they don't administer, and "ask an admin" is the actionable
    // half here — they cannot free space they cannot see.
    case 'storage_full': return "Your Drive is full — nothing was saved. Free up space or ask an admin to raise the limit."
    case 'uploads_paused': return 'Uploads are paused on this Drive — nothing was saved. Save into another Drive, or ask an admin.'
    case 'identity_only': return "This Drive can't hold files — nothing was saved. Switch to a Drive that can."
    // #812 — the destination was re-checked at pickup (not only at enqueue): write access
    // was removed, or the folder itself is gone, between the request and the worker picking
    // the job up.
    case 'dest_not_writable': return 'You can no longer save into that folder — nothing was saved. Pick another folder and try again.'
    default: return code || 'Save failed'
  }
}

/** A completed save's line. `storageFull` outranks the generic partial: it names a cause the recipient can act on,
 *  where "some files were skipped" reads as a permission gap and invites no next step. #1397 round 5 L1: each file
 *  left out because its stored copy is damaged follows, in the server's visitor words (`damaged[].message`, named —
 *  absent from an older server, which leaves the line as it was). */
export function saveCompletedMessage(r: { storageFull?: boolean; truncated?: boolean; damaged?: unknown }): string {
  const line = r.storageFull
    ? 'Your Drive filled up — only part of this share was saved.'
    : r.truncated
      ? 'Copied to your Drive (some files were skipped).'
      : 'Copied to your Drive.'
  const damaged = Array.isArray(r.damaged)
    ? r.damaged.flatMap((d: { message?: unknown } | null) => (typeof d?.message === 'string' && d.message ? [d.message] : []))
    : []
  return damaged.length ? `${line} ${damaged.join(' ')}` : line
}

/**
 * #776: while a restricted-link visitor has not accepted that their edits are recorded,
 * every surface treats them as VIEW — editors open read-only, the explorer offers no write
 * actions — so nothing looks editable that the server would refuse. The bar's "Start
 * editing" is the way in (it asks, then reloads writable). The server only sends the field
 * to a visitor who can edit.
 */
function viewOnlyUntilAccepted(info: SessionInfo): SessionInfo {
  return info.attributionDisclosure ? { ...info, share: { ...info.share, permission: 'view' } } : info
}

export function ShareViewerChrome() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const isPhone = useIsPhone()
  const { productName } = useBrand()
  // Signed-in user's own identity (from the Better Auth session). Used to show a
  // "you're signed in as …" pill on shares with no verified-recipient anchor
  // (quick-view), where there's no recipient email to display otherwise.
  const { user: authUser, isLoading: authLoading } = useAuth()
  // Phase 28: inject <meta name="robots" content="noindex,nofollow"> for
  // the duration of the share-view chrome render. Prevents Google etc.
  // from indexing recipient-shared URLs that may end up in public
  // channels by mistake. Cleaned up on unmount so other SPA routes
  // remain indexable.
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex,nofollow'
    document.head.appendChild(meta)
    return () => { document.head.removeChild(meta) }
  }, [])
  const [searchParams, setSearchParams] = useSearchParams()
  // (`?tenant=` / `?orgEmail=` are NOT read for the acting identity — see `effectiveTenant`.)
  // Decode the (unverified, display-only) session-token payload once. The token is
  // `base64url(payloadJSON).signature` — the payload is segment [0] (it starts
  // `eyJ…` = `{"kind":"share"…`), not a JWT header. Used purely client-side to
  // shape the chrome; the server re-verifies the signature on every request.
  // A v2 token (#791) seals its payload: the client reads only the header (expiry, whether
  // the session acts as an account, an opaque identity key) and takes everything else from
  // /info (`info.session`). `legacyPayload` is a v1 token's readable payload — the fallback
  // while a rolling deploy can still serve an older /info.
  const tokenHeader = useMemo(() => sessionHeader(sessionId), [sessionId])
  const legacyPayload = useMemo(() => decodeSessionPayload(sessionId), [sessionId])
  // Does the route session act as an account (a verified email)? From the header, so it is
  // known synchronously on every session change.
  const sessionBound = !!tokenHeader?.bound
  // The share's OWNER tenant (where the content lives). Marks the genuine "best
  // access" tenant in the account picker — a FIXED designation (the tenant the
  // recipient natively belongs to), not the currently-selected one. (v1 only; the
  // attributed `info.ownerTenantId` is preferred anyway.)
  const ownerTenantId = typeof legacyPayload?.tenantId === 'string' ? legacyPayload.tenantId : null
  // Quick-view (public/wildcard) sessions carry no verified-recipient anchor.
  // Their sign-in is a plain in-app modal (no server verify gate), so AFTER
  // sign-in we present the identity picker as a step — restricted links already
  // chose their identity at the verify gate, so they skip it.
  const isQuickView = !!tokenHeader && !sessionBound
  // Signed-in user's tenants — lifted to the chrome so the resolver and the
  // account picker share ONE source of truth for "best access". The picker's
  // ✓ marks the very tenant/email the resolver resolves against (below) rather
  // than a separate client-side guess that could silently diverge. #187
  const [tenants, setTenants] = useState<TenantInfo[]>([])
  useEffect(() => {
    // Only signed-in recipients have tenants / a best-access identity / an
    // account picker. Skip the fetch for anonymous quick-view recipients — it
    // would just 401 (no Better Auth session) and is never consumed. Keyed on
    // the user id so it (re)fetches when the recipient signs in or switches.
    if (!authUser) { setTenants([]); return }
    let cancelled = false
    orgsApi.listMyTenants()
      .then((r) => { if (!cancelled) setTenants(Array.isArray(r.tenants) ? r.tenants : []) })
      .catch(() => { /* best-effort — picker just falls back to the session identity */ })
    return () => { cancelled = true }
  }, [authUser])
  // The genuine "best access" tenant + email — FIXED, independent of the current
  // ?tenant=/?orgEmail= selection: the share's owner tenant if the recipient
  // natively belongs to it, else their personal drive; its case-matching (or
  // first) email. This is BOTH what the picker labels "(best access)" / ticks by
  // default AND — when no explicit override is set — what the resolver, Save and
  // Add-to-Drive resolve against, so the ✓ and the actual landing never drift.
  const sessionEmail = authUser?.email ?? ''
  // /info's attributed tenant — for a RESHARED cross-tenant mount the session
  // token's tenantId is the RESHARER's tenant, but the recipient's genuinely
  // best identity is the SOURCE tenant the mount targets (they may hold higher
  // native access there as a member). Same id the "Shared by …" pill names,
  // so the star and the pill can never disagree. Declared above the memo that
  // consumes it; the rest of the info-driven state lives further down.
  const [info, setInfo] = useState<SessionInfo | null>(null)
  const [infoSid, setInfoSid] = useState<string | null>(null)
  // The route session's IDENTITY (verified email, acting tenant / email) — from /info,
  // remembered per identity key so a new session of the SAME identity (a sub-session, a
  // re-mint) reuses it at once instead of falling back to the personal tenant until its
  // own /info lands. v1 tokens: the readable payload, as before.
  const identityByKey = useRef(new Map<string, NonNullable<SessionInfo['session']>['identity']>())
  if (info?.session?.identity && infoSid) {
    const k = sessionHeader(infoSid)?.identityKey
    if (k) identityByKey.current.set(k, info.session.identity)
  }
  // v2 only: a v1 token's own payload is exact (its legacy key omits `ae`, so two emails of
  // one org would share a key) and is read directly below.
  const routeIdentity = (tokenHeader?.v === 2 && identityByKey.current.get(tokenHeader.identityKey)) || null
  // A v2 session whose identity /info hasn't told us yet (just after an identity switch or a
  // bind mints a new session): the acting pair is UNKNOWN, not "the personal tenant" —
  // identity-dependent requests wait and those actions are held until it lands.
  const identityPending = tokenHeader?.v === 2 && !routeIdentity
  const sessionPayload = routeIdentity
    ? { verifiedEmail: routeIdentity.verifiedEmail ?? undefined, at: routeIdentity.actingTenantId ?? undefined, ae: routeIdentity.actingEmail ?? undefined }
    : { verifiedEmail: legacyPayload?.verifiedEmail, at: legacyPayload?.at, ae: legacyPayload?.ae }
  const { bestTenant, bestEmail } = useMemo(() => {
    const personal = tenants.find((t) => t.type === 'personal')
    const attributedTenantId = info?.ownerTenantId ?? null
    const bt = (attributedTenantId && tenants.some((t) => t.id === attributedTenantId))
      ? attributedTenantId
      : (ownerTenantId && tenants.some((t) => t.id === ownerTenantId))
        ? ownerTenantId
        : (personal?.id ?? tenants[0]?.id ?? null)
    const bto = tenants.find((t) => t.id === bt)
    const be = (
      bto?.emails?.find((e) => e.email.toLowerCase() === sessionEmail.toLowerCase())?.email
      ?? bto?.emails?.[0]?.email
      ?? sessionEmail
      ?? ''
    ).toLowerCase()
    return { bestTenant: bt, bestEmail: be }
  }, [tenants, ownerTenantId, sessionEmail, info?.ownerTenantId])
  // The tenant + acting email actually in effect on the cookie surfaces (the
  // resolver behind Add-vs-Open / Open in my Drive, Save, Add to Drive, the PDApp
  // Settings gate + "Managing as", the picker's ✓): the session's STORED pair (`at` /
  // `ae`) — the identity decided at entry — and nothing else (CLAUDE.md → "The visitor's
  // tenant is decided at ENTRY and stored on the session"). URL params never move it:
  // a leftover / hand-edited `?tenant=` / `?orgEmail=` would put the chrome on one
  // identity while the share resolves as the stored one. Switching identity mints a NEW
  // session (`submitIdentitySwitch`). No stored identity (e.g. a public link with no
  // pick) → the visitor's PERSONAL tenant, never an auto-picked org ("best access" is
  // display-only — the picker's star).
  const storedTenant = typeof sessionPayload?.at === 'string' ? sessionPayload.at : null
  const storedEmail = storedTenant && typeof sessionPayload?.ae === 'string' ? sessionPayload.ae.toLowerCase() : null
  const effectiveTenant = storedTenant ?? (tenants.find((t) => t.type === 'personal')?.id ?? null)
  const effectiveEmail = ((): string | null => {
    if (!effectiveTenant) return null
    if (effectiveTenant === storedTenant && storedEmail) return storedEmail
    const t = tenants.find((x) => x.id === effectiveTenant)
    if (!t) return null
    if (t.type === 'personal') return sessionEmail.toLowerCase() || null
    return (t.emails.find((e) => e.email.toLowerCase() === sessionEmail.toLowerCase())?.email
      ?? t.emails[0]?.email ?? null)?.toLowerCase() ?? null
  })()
  const { settings } = useTheme()
  // CLAUDE.md theming rule: components rendered outside ThemedArea don't
  // have CSS variables defined. The chrome lives at /share-view/* which
  // sits outside the workspace ThemedArea, so apply the theme class on
  // the root wrapper to make themed-* classes resolve.
  const themeClass = getThemeClass(settings.theme.workspace)
  const { t: tFiles } = useTranslation('files')
  // Save to Drive stopped to ask how pointers inside should arrive (the share-view pointer
  // dialog, preselecting CONTENTS): open while the question is on screen.
  const [savePointerAsk, setSavePointerAsk] = useState(false)
  // The folder Download stopped to ask the same question (zip wording).
  const [zipPointerAsk, setZipPointerAsk] = useState(false)
  const confirm = useConfirm()
  // (`info` state is declared above the best-access memo that consumes it.)
  // The session token `info` was actually loaded for. `sessionId` (the route
  // param) updates SYNCHRONOUSLY on a file switch, but `info` is refetched async
  // and lags. Rendering the viewer against the live `sessionId` while `info` is
  // still the previous file remounts the editor (key={sessionId}) against stale
  // data — a visible iframe reload / blink on every switch. So the body renders
  // against `bodySid` (below), which only advances once `info` catches up.
  const [error, setError] = useState<string | null>(null)
  const [resolver, setResolver] = useState<ResolverInfo | null>(null)
  const navigate = useNavigate()
  // Decision 7 / Phase 12: per-button busy + success/error feedback.
  const [saveState, setSaveState] = useState<ButtonState>('idle')
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [addState, setAddState] = useState<ButtonState>('idle')
  const [addMessage, setAddMessage] = useState<string | null>(null)
  // Unsaved-content indicator for the title-bar (the blue dot before the file
  // name). Bubbled up from the editor's onDirtyChange — only edit links wire
  // it, so a view share never goes dirty.
  // Unsaved-content state, KEYED BY THE SESSION TOKEN of the file the editor was
  // mounted for. The key is the whole point: it makes both `contentDirty` and
  // "can this file even be saved?" self-correcting, with no list of editable
  // extensions to keep in lockstep with renderFileViewer's dispatch.
  //
  // Only the saving editors (`shareEditorFor`: the text / html / script Y.Doc
  // editors and the DocsAPI office editor) are handed `onDirtyChange`, and each
  // fires it on mount with `false`. The read-only viewers (previews, sqlite,
  // Collabora, plain text) wire nothing — no onDirtyChange, no onSaveRef, no
  // onDiscardRef.
  //
  // So `editorState.sid === activeSid` means precisely "an editor that owns the
  // save/discard refs is mounted for the file you're looking at". Before this was
  // a bare boolean that nothing ever reset: dirty a .md, then open a .csv, and the
  // csv inherited the .md's dirty flag — a blue unsaved dot, an enabled Save, and
  // a Discard whose confirm threatens the owner's work, all on a file with no save
  // path at all (both refs having been nulled when the .md unmounted, so all three
  // silently no-opped). Keying on the file kills that whole class: a stale key is
  // simply not the active one.
  const [editorState, setEditorState] = useState<{ sid: string; dirty: boolean } | null>(null)
  // `activeSid` is derived below, but the editor's callback must stamp it at FIRE
  // time, not capture it — so read it through a ref, keeping the callback identity
  // stable (the editors' relay effect deps on it).
  //
  // Stamped in a LAYOUT EFFECT, never during render. A render React starts and then
  // throws away (StrictMode's double-invoke, a concurrent interruption, a Suspense
  // retry) would still have mutated the ref, leaving the gate to depend on
  // render-phase side-effect ordering. A layout effect only runs on a COMMITTED
  // tree — and parent layout effects run before any child's passive effect, so the
  // ref is always current by the time an editor's dirty relay fires.
  const activeSidRef = useRef('')
  const handleDirtyChange = useCallback((dirty: boolean) => {
    setEditorState({ sid: activeSidRef.current, dirty })
  }, [])
  // Save / Discard handles for the open editor, parked here in the CHROME rather
  // than in ShareBody — the top bar is the only surface present on EVERY path
  // (folder listing + in-place overlay, and the direct sub-file session, which
  // renders a bare viewer with no header of its own). Owning them one level up is
  // what lets the same two controls serve both, instead of the folder path
  // growing its own copy. Every Save fires through `requestSave` below (ShareBody's Ctrl+S included), never the ref
  // itself (#930 / I1).
  const editSaveRef = useRef<(() => void) | null>(null)
  const editDiscardRef = useRef<(() => void) | null>(null)
  // #930 / I1 — the open editor's own COVER (the conflict "Saved version" panel: the live document stays mounted,
  // hidden, behind the saved copy — see EditorCover in core-ui). The editor reports it through the
  // EditorCoverProvider around ShareBody; while it is up every Save route here — Cmd+S, the top bar's Save, the
  // phone sheet's, the overlay row's — is REFUSED with the cover's notice and its way back, and Discard is not
  // offered: either would act on the document the visitor is not looking at (a force-save over the change they are
  // reading). Refused, not applied: keeping your changes over the saved file is the banner's own "Keep my changes".
  const [editorCover, setEditorCover] = useState<EditorCover | null>(null)
  const editorCoverRef = useRef<EditorCover | null>(null)
  editorCoverRef.current = editorCover
  const toast = useToast()
  const noticeSaveBlocked = useSaveBlockedNotice(toast, editorCover)
  // Post-mount confirmation modal (replaces the easy-to-miss inline banner for
  // the success case). `pending` = owner must approve; `established` = instant
  // (already connected). Errors still surface inline below the toolbar.
  const [mountResult, setMountResult] = useState<'pending' | 'established' | 'no_access' | null>(null)
  // Inline sign-in modal — fires when an anon recipient clicks an
  // action that requires a Better Auth session. The pending action
  // is stashed so we can re-run it once sign-in succeeds.
  const [signInPrompt, setSignInPrompt] = useState<{
    title: string
    retry: () => void
  } | null>(null)

  // (Phase 2) An embedded SDK app runs AS THE RECIPIENT in the share-view. This
  // engages whenever the recipient is VIEWING AN HTML APP — a dedicated app share
  // OR an index.html living inside a shared folder (a quick-view-shared app; the
  // common case). NOT gated on share.kind: the doc's target is the share-view
  // embed, period. `appRunFolderId` is the app's folder = the session's scoped
  // rootId (for a folder-share sub-file, that's the subfolder the file lives in),
  // passed to the editor as `parentId` so the preview's `papan:run` stops
  // short-circuiting on `no_folder`.
  // The session the VIEWER renders against — the one `info` was loaded for, so
  // `file`, serve URLs, editor key, and app-run folder all stay mutually
  // consistent through a switch (no stale remount). Falls back to the live
  // `sessionId` before the first `/info` resolves.
  const bodySid = infoSid ?? sessionId ?? ''
  // #989 D3: the preview host never sees the browser-binding cookie, so every URL this view puts
  // there carries the session's SERVE token, never the session (null until it is known).
  const serveBody = useServeTokenFor(bodySid || null)

  // ── Dynamic favicon: focused `.miniapp` root → its icon PNG ────────────────
  // The share root is the only surface here that can be a `.miniapp` FOLDER
  // (opened sub-items are files). SessionInfo now carries the root's custom
  // spec (`rootIcon`) for a native `.miniapp`, so the hash matches the real
  // icon and cache-busts on edit; absent (mount roots) → the name-derived
  // default hash (the server still renders the real spec via `session.rootId`).
  // Restored on unfocus/unmount.
  const faviconRootName = info?.share.rootIsFolder ? info.share.rootName : null
  const faviconRootIcon = info?.share.rootIcon ?? null
  const faviconSid = bodySid
  useEffect(() => {
    if (!faviconRootName || !isMiniappFolder(faviconRootName) || !faviconSid || !serveBody) return
    // `iconCacheKey` — the same URL key the server checks (#1015).
    const hash = iconCacheKey(iconSpecHash(faviconRootIcon ?? defaultIconSpecFor(faviconRootName)))
    setFavicon(shareServeUrl(serveBody, `_papan/icon-${hash}-64.png`))
    return () => restoreFavicon()
  }, [faviconRootName, faviconRootIcon, faviconSid, serveBody])

  // ── In-place file open (SPA) ───────────────────────────────────────────────
  // Opening a file from a FOLDER share used to `window.open` a new tab (full
  // document load) or `navigate()` to /share-view/<subSid> — which swaps the
  // route param, refetches /info, and unmounts the folder listing. Both make
  // the recipient pay a whole-app boot to look at one file.
  //
  // Instead the ROUTE stays pinned to the folder's session and the open file
  // lives in `?file=<fileId>` + `openFile` state. The listing component never
  // unmounts (the viewer overlays it), so closing a file is instant with zero
  // refetch, and Back/Forward/deep-links still work because the URL carries it.
  //
  // Each opened file still needs its OWN session token: `/s/<sid>/<name>` is
  // resolved against the session's serveRootId, so the sid must be scoped to
  // the file. That's a cheap HMAC mint, and we memo it per fileId so re-opening
  // a file already seen this session costs nothing.
  const openFileId = searchParams.get('file')
  const [openFile, setOpenFile] = useState<OpenFile | null>(null)
  const [openFileError, setOpenFileError] = useState<string | null>(null)
  // Re-review fixes: a failed open is not retried by its own state change; a rename of the file on
  // screen waits until the editor holds nothing unsaved.
  const failedOpenRef = useRef<string | null>(null)
  const contentDirtyRef = useRef(false)
  const pendingRenameRef = useRef<{ fileId: string; name: string } | null>(null)
  const subSidCache = useRef<Map<string, string>>(new Map())

  // Drop the cache on a session change (see above) — but do NOT clear
  // `openFile`. Clearing it unmounts the overlay, so the recipient saw the
  // listing FLASH behind it before the re-mint brought the file back (an HTML
  // app auto-binds on open, so this fired on every app open). Leave the current
  // file on screen; the mint effect below swaps it in place once the new
  // sub-session resolves.
  useEffect(() => {
    subSidCache.current.clear()
  }, [sessionId])

  const closeFile = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('file')
      // (#757) The PDApp tab (`view=run|settings`) is a face OF the open file —
      // closing the file leaves it, or the next open would land on that tab.
      next.delete(VIEW_PARAM)
      return next
    }, { replace: false })
  }, [setSearchParams])

  const openFileInPlace = useCallback((fileId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('file', fileId)
      // (#757) A file picked from the panel opens in its EDITOR — leave the
      // PDApp tab (`view=run|settings`), even when it is the app's index.html.
      next.delete(VIEW_PARAM)
      return next
    }, { replace: false })
  }, [setSearchParams])
  /** Open `fileId` in the overlay with a session the SERVER already minted for it (a
   *  launch's entry, an openFile target) — seeded into the cache the overlay reads, so it
   *  is not minted a second time through /sub-session (#852). */
  const openFileWithSession = useCallback((fileId: string, sid: string) => {
    subSidCache.current.set(fileId, sid)
    // Already the open file (an app launching ITSELF — its home button from a page it navigated
    // to): `?file=` would not change and the open effect would keep the old session, so the
    // launch did nothing. Drop the resolved file instead — the effect re-opens it with the
    // freshly minted session (a reload), and no duplicate history entry is pushed.
    if (openFileId === fileId) {
      // Unsaved edits in the open editor (an Edit link's HTML preview launching its own app):
      // never drop them for a reload — the same guard the rename path waits on.
      if (contentDirtyRef.current) return
      failedOpenRef.current = null
      setOpenFile(null)
      return
    }
    openFileInPlace(fileId)
  }, [openFileInPlace, openFileId])

  useEffect(() => {
    if (!openFileId || !sessionId) { failedOpenRef.current = null; setOpenFile(null); setOpenFileError(null); return }
    // The failed-open skip below covers ONLY the re-run its own state change causes — asking for any
    // other file (or closing, above) forgets it, so the same file can be tried again (re-review).
    if (failedOpenRef.current && failedOpenRef.current !== `${sessionId}:${openFileId}`) failedOpenRef.current = null
    // `?file=` is ONLY meaningful when the route session is the FOLDER — that's
    // the one branch of ShareBody that renders `openFile`. Without this guard the
    // effect still minted a sub-session and re-pointed `activeSid` / `inlineUrl` /
    // `downloadUrl` / the X-Share-Session getter at it, while the non-folder
    // branch went on rendering the ROUTE file: the editor showed file A's name
    // and type while streaming file B's bytes, and sent B's session token for A's
    // /api/wopi/token and /api/sqlite calls. Reachable by hand-editing
    // `/share-view/<subFileSid>?file=<otherFileId>` (both inside the share, so
    // the mint succeeds). Ignore the param instead — the route file is the truth.
    if (!info?.file.isFolder) { setOpenFile(null); setOpenFileError(null); return }
    // Already resolved UNDER THIS PARENT (a re-render, or Back into a file we've
    // seen) — keep it. The parent check is what makes a re-bind re-mint: same
    // file, new route session, so the sub-session must be re-derived.
    if (openFile?.fileId === openFileId && openFile.parentSid === sessionId) return
    // Already FAILED for this file under this parent: clearing `openFile` on a failure re-runs this
    // effect (its id is a dep) — don't open it a second time (re-review: two requests + a flicker).
    if (failedOpenRef.current === `${sessionId}:${openFileId}`) return
    let cancelled = false
    setOpenFileError(null)
    // A failed open of a DIFFERENT file must not leave the previous one on screen
    // (header, tree highlight, Save/Discard would stay on it while the body shows
    // this file's error) — see `openFileAfterFailure`.
    const fail = (message: string) => {
      if (cancelled) return
      failedOpenRef.current = `${sessionId}:${openFileId}`
      setOpenFile(prev => openFileAfterFailure(prev, openFileId))
      setOpenFileError(message)
    }
    void (async () => {
      try {
        let sid = subSidCache.current.get(openFileId)
        if (!sid) {
          const mint = await sessionFetch(
            `${getApiBase()}/api/share-links/session/${sessionId}/sub-session`,
            {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fileId: openFileId }),
            },
          )
          if (!mint.ok) {
            const body = await mint.json().catch(() => ({}))
            // #1264 — a listed file refused (it may be gone, its delete never announced): the listing
            // that showed it re-reads itself (SharedFolderView listens).
            if (mint.status === 403 || mint.status === 404) {
              window.dispatchEvent(new CustomEvent(SHARE_ROW_REFUSED_EVENT, { detail: { fileId: openFileId } }))
            }
            fail(body.error === 'cross_tenant_mount_not_traversable'
              ? 'That file lives in another organization. Add this share to your Drive to open it.'
              : body.detail || body.error || `Couldn't open this file (${mint.status})`)
            return
          }
          const mintBody = (await mint.json()) as { sessionId?: string; navigateFolderId?: string }
          // #188 — the target is a cross-tenant `.shared`/`.chain` mount (presents as a folder):
          // BROWSE into its reshared view (drop `?file`, set `?parentId`) instead of a file overlay.
          if (mintBody.navigateFolderId) {
            if (!cancelled) {
              setOpenFileError(null)
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                next.delete('file')
                next.set('parentId', mintBody.navigateFolderId as string)
                return next
              }, { replace: true })
            }
            return
          }
          if (!mintBody.sessionId) {
            fail("Couldn't open this file")
            return
          }
          sid = mintBody.sessionId
          if (cancelled) return
          subSidCache.current.set(openFileId, sid)
        }
        const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sid}/info`, {
          credentials: 'include',
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          fail(body.error || `Couldn't open this file (${res.status})`)
          return
        }
        const raw = (await res.json()) as SessionInfo
        seedServeToken(sid, (raw as { serveToken?: unknown }).serveToken)
        const body = viewOnlyUntilAccepted(raw)
        // Swap atomically — the previous file stays on screen until this lands,
        // so a re-mint (identity re-bind) never blanks the overlay.
        if (!cancelled) { failedOpenRef.current = null; setOpenFile({ fileId: openFileId, sid, parentSid: sessionId, info: body }) }
      } catch (err) {
        if (isSessionEndedError(err)) return   // #1316: the session it was asked for ended — no error for the next one
        fail(err instanceof Error ? err.message : 'Network error')
      }
    })()
    return () => { cancelled = true }
    // `info?.file.isFolder` is a REQUIRED dep, not decoration: on a `?file=`
    // deep-link `info` is null on the first pass, so the guard above bails —
    // without this the effect would never re-run once /info lands and the file
    // would never open.
  }, [openFileId, sessionId, openFile?.fileId, openFile?.parentSid, info?.file.isFolder])

  // A rename through the explorer patches only the tree. When the renamed row is the
  // file on screen — the in-place open file, or the route's own file (a sub-file
  // session) — re-read its info so the header, the serve / download / probe URLs and
  // the editor chosen by extension follow the new name. The open-file effect above
  // returns early for an already-open id, so nothing else would refresh it.
  const handleFileRenamed = useCallback((fileId: string, name: string) => {
    // Unsaved edits in the file on screen: the refresh rebuilds its serve URL (from the name), which
    // reloads the editor and would drop them (re-review) — apply it once the editor is clean.
    if (contentDirtyRef.current && (openFile?.fileId === fileId || info?.file.id === fileId)) {
      pendingRenameRef.current = { fileId, name }
      return
    }
    const infoUrl = (sid: string) => `${getApiBase()}/api/share-links/session/${sid}/info`
    if (openFile?.fileId === fileId) {
      const target = openFile
      void refreshRenamedSessionInfo(target.sid, target.info, fileId, name, infoUrl).then(next => {
        setOpenFile(prev => (prev && prev.fileId === fileId && prev.sid === target.sid ? { ...prev, info: next } : prev))
      }, () => { /* #1316: its session ended — nothing written (it rejects only then) */ })
      return
    }
    if (info && info.file.id === fileId && infoSid) {
      const sid = infoSid
      void refreshRenamedSessionInfo(sid, info, fileId, name, infoUrl).then(next => {
        setInfo(prev => (prev && prev.file.id === fileId ? next : prev))
      }, () => { /* #1316 — as above */ })
    }
  }, [openFile, info, infoSid])

  // The session + info the FILE VIEWER renders against. When a file is open
  // in-place inside a folder share that's the file's sub-session; otherwise the
  // route's own session (a single-file share, or a sub-file session deep-link).
  // Everything the viewer touches — serve URLs, the X-Share-Session header, the
  // Yjs WS token, the app-run folder — must agree on this, or the editor
  // authenticates as one file while streaming another.
  const activeSid = openFile?.sid ?? bodySid
  // The open item's serve token. GONE (`/serve-token` 404) is never waited on — see the
  // re-check below, which re-enters, shows the error, or (an API too old for the route, its
  // session alive) falls back to the raw session URL, as phase 2's rolling-deploy fallback did.
  const serveActiveState = useServeTokenState(activeSid || null)
  const [rawServeSid, setRawServeSid] = useState<string | null>(null)
  const serveActive = serveActiveState.token ?? (rawServeSid && rawServeSid === activeSid ? activeSid : null)
  const activeInfo = openFile?.info ?? info
  const bodyPayload = useMemo(() => decodeSessionPayload(activeSid), [activeSid]) // v1 fallback
  // The ACTIVE file's permission, not the route session's — a sub-session
  // inherits the grant, so these agree today, but reading it off the file the
  // recipient is actually editing is what keeps the Save/Discard gate honest if
  // they ever diverge.
  const canEditActive = activeInfo?.share.permission === 'edit'
  // The dirty flag, and ONLY the dirty flag — saveability is no longer inferred from
  // it (see canSaveActive). The sid key still earns its keep here: it is what stops a
  // dirty .md from lending its unsaved dot to the next file you open.
  const contentDirty = !!editorState && editorState.sid === activeSid && editorState.dirty
  contentDirtyRef.current = contentDirty
  useEffect(() => {
    const pending = pendingRenameRef.current
    if (contentDirty || !pending) return
    pendingRenameRef.current = null
    handleFileRenamed(pending.fileId, pending.name)
  }, [contentDirty, handleFileRenamed])
  // See activeSidRef: committed tree only, and ordered before any child's passive
  // effect — so an editor's dirty relay always stamps the file it mounted for.
  useLayoutEffect(() => { activeSidRef.current = activeSid }, [activeSid])

  // Discard = revert the open file to its last-saved bytes. Confirmed, and NOT
  // because it's merely destructive: an `edit` share co-edits the OWNER'S LIVE
  // collab room, and `discard()` sends MSG_DISCARD, which resets that room for
  // every client in it. So a recipient hitting Discard throws away the owner's
  // unsaved work too, not just their own. The workspace's own tab menu fires
  // discard unconfirmed — correct there, where the only work at risk is your own;
  // wrong here, where it isn't yours to throw away. Hence the confirm, and hence
  // the copy naming whose changes are actually being discarded.
  const handleDiscard = useCallback(async () => {
    // #930 / I1: not under the editor's own cover (see editorCover) — the controls aren't offered then; this is the
    // backstop, asked again after the confirm (the panel can open while it is up).
    if (editorCoverRef.current) return
    const ok = await confirm({
      title: 'Discard unsaved changes?',
      message:
        "This reverts the file to its last saved version. You're editing the owner's live document, " +
        'so any unsaved changes — theirs as well as yours — are discarded for everyone currently in it. ' +
        'This cannot be undone.',
      type: 'danger',
      confirmText: 'Discard changes',
    })
    if (ok && !editorCoverRef.current) editDiscardRef.current?.()
  }, [confirm])
  // THE Save every route here fires (Cmd+S, the top bar, the phone sheet, the overlay row) — #930 / I1: under the
  // editor's own cover it is refused with the cover's notice (only when there is unsaved work — the rule lives on
  // useSaveBlockedNotice) instead of force-saving the hidden live document.
  const requestSave = useCallback(() => {
    const cover = editorCoverRef.current
    if (cover) {
      noticeSaveBlocked(contentDirtyRef.current, cover.uncover, cover)
      return
    }
    editSaveRef.current?.()
  }, [noticeSaveBlocked])
  const saveCovered = editorCover !== null
  const viewingHtmlApp = !!activeInfo?.file && !activeInfo.file.isFolder && editorFamilyForName(activeInfo.file.name) === 'html-page'
  const activeRootId = activeInfo?.session?.rootId ?? (typeof bodyPayload?.rootId === 'string' ? bodyPayload.rootId : null)
  const appRunFolderId = viewingHtmlApp ? activeRootId : null
  // (Phase 2) For an app share bound via bind-account the acting identity lives in
  // the SESSION token (`at` / `verifiedEmail`), NOT the `?tenant=`/`?orgEmail=` URL
  // params (which drive the Drive-CTA on file shares). Reflect the bound identity in
  // the AccountPicker so its ✓ follows what you picked instead of sticking to
  // "best access". Falls back to the URL params for non-app / unbound sessions.
  // The picker's ✓ is the identity the chrome ACTS as — the one (tenant, email) pair the
  // cookie surfaces use (`effectiveTenant`/`effectiveEmail`: the stored acting pair, its
  // `at` / `ae`), never the link's verified email.
  const pickerActiveTenant = effectiveTenant
  const pickerActiveEmail = effectiveEmail
  // The PERSONAL account's CHOSEN email on this session (#794) — the email it joined the link
  // as: the session's verified (link) email, which a Personal identity keys on. Display only
  // (the Personal row's ✓); the stored `ae` stays the native half every cookie surface sends.
  const personalTenantId = tenants.find((t) => t.type === 'personal')?.id ?? null
  const personalChosenEmail = storedTenant && storedTenant === personalTenantId
    ? ((typeof sessionPayload?.verifiedEmail === 'string' ? sessionPayload.verifiedEmail : null) ?? storedEmail)?.toLowerCase() ?? null
    : null
  // Switch identity (non-app shares; the app bind's fallback): a NEW session minted by the
  // existing choose-identity select, restoring where the visitor was on return.
  const switchIdentity = (tid: string, email?: string) => {
    if (!info) return
    const t = tenants.find((x) => x.id === tid)
    // The PERSONAL account with no email named: '' lets the server preselect its email by the
    // owner rule (highest grant, then the primary) — #794. An org names a membership email.
    const chosen = email
      ?? (t?.type === 'personal' ? ''
        : (t?.emails.find((e) => e.email.toLowerCase() === sessionEmail.toLowerCase())?.email ?? t?.emails[0]?.email ?? sessionEmail))
    if (chosen == null || (!chosen && t?.type !== 'personal')) return
    const marker = newPickMarker()
    stashPickContext(info.share.token, marker, bindLandingContext())
    if (!sessionId) return
    submitIdentitySwitch(info.share.token, sessionId, tid, chosen, marker)
  }

  // Which of the recipient's (tenant, email) identities THIS link admits — so the account
  // picker HIDES identities with no access (an org member who can't see the shared file, or
  // the wrong email on a sub-file reshare). Server-side mirror of bind-account's admission
  // (GET /:token/bindable-accounts → the SAME predicate bind-account uses). Best-effort: until
  // it resolves / on error the picker shows all identities (bind-account still gates the switch).
  // `personalEmails`: the PERSONAL account's verified emails this link lets it join as (#794),
  // in the server's preselection order — the Personal row's email selector.
  const [admissible, setAdmissible] = useState<{ ready: boolean; keys: Set<string>; personalEmails: string[] }>({ ready: false, keys: new Set(), personalEmails: [] })
  const shareToken = info?.share.token ?? null
  useEffect(() => {
    if (!authUser || !shareToken) { setAdmissible({ ready: false, keys: new Set(), personalEmails: [] }); return }
    let cancelled = false
    // raw (#1316): feeds the identity picker — the question is asked BEFORE the session the pick creates
    fetch(`${getApiBase()}/api/share-links/${encodeURIComponent(shareToken)}/bindable-accounts`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { accounts?: Array<{ tenantId: string; email: string; allowed: boolean; personal?: boolean }> } | null) => {
        if (cancelled || !data?.accounts) return
        setAdmissible({
          ready: true,
          keys: new Set(data.accounts.filter((a) => a.allowed).map((a) => `${a.tenantId}:${a.email.toLowerCase()}`)),
          personalEmails: data.accounts.filter((a) => a.personal && a.allowed).map((a) => a.email.toLowerCase()),
        })
      })
      .catch(() => { /* best-effort — bind-account still gates the actual switch */ })
    return () => { cancelled = true }
  }, [authUser, shareToken])
  // Filter the picker to admissible identities, ALWAYS keeping the identity you're currently on
  // (never hide the active one). Before the check resolves, show all — no hide-then-show flicker.
  const pickerTenants = useMemo(() => {
    if (!admissible.ready) return tenants
    const activeTid = pickerActiveTenant
    const activeEmail = (pickerActiveEmail ?? sessionEmail).toLowerCase()
    const ok = (tid: string, email: string) =>
      admissible.keys.has(`${tid}:${email.toLowerCase()}`) || (tid === activeTid && email.toLowerCase() === activeEmail)
    const out: TenantInfo[] = []
    for (const t of tenants) {
      if (t.type === 'personal') {
        // ONE Personal account, listing the verified emails it may join this link as (#794) —
        // not only its sign-in email (a secondary-only grant would hide it otherwise).
        const emails = admissible.personalEmails.length > 0
          ? admissible.personalEmails
          : ok(t.id, sessionEmail) ? [sessionEmail.toLowerCase()] : []
        if (emails.length > 0 || t.id === activeTid) {
          out.push({ ...t, emails: emails.map((email) => ({ email, role: 'owner' })) })
        }
        continue
      }
      const emails = t.emails.filter((e) => ok(t.id, e.email))
      if (emails.length) out.push({ ...t, emails })
    }
    return out
  }, [tenants, admissible, pickerActiveTenant, pickerActiveEmail, sessionEmail])

  // Switching identity in the AccountPicker re-binds the app session
  // (POST /bind-account → a session carrying actingTenantId), so whoami / run /
  // consent resolve as the picked identity. bind-account mints at the share ROOT,
  // so to keep the recipient ON the app they were viewing we then mint a
  // sub-session for the current file from the bound session (which inherits the
  // actingTenantId and scopes an HTML file to its parent folder) and navigate
  // there. Falls back to the bound root session, then to the Drive-CTA nav, so a
  // pick always does something even if a step fails.
  // Monotonic bind generation so a stale in-flight bind can't clobber a newer
  // one. The auto-bind (→ personal) and an explicit avatar pick (→ org) both
  // bind + navigate ASYNCHRONOUSLY; without this, whichever navigation resolves
  // LAST wins, so a pick made before the auto-bind settled would be reverted to
  // personal (observed on stagetest under latency). Each call captures its own
  // generation and bails before navigating / falling back once a newer bind has
  // started — so the last USER-initiated pick wins, not the last network reply.
  const bindGenRef = useRef(0)
  // WHERE the visitor is, in the shape the bind-success branch of bindAppIdentity lands on
  // (kept in step with it): an in-place file inside a folder share (+ PDApp mode + the
  // subfolder), a sub-file session to re-mint from the bound root, or the root itself.
  const bindLandingContext = (): ShareLandingContext => {
    if (openFileId) {
      const carry = new URLSearchParams({ file: openFileId })
      const pd = searchParams.get(PDAPP_PARAM)
      if (pd) carry.set(PDAPP_PARAM, pd)
      const parent = searchParams.get('parentId')
      if (parent) carry.set('parentId', parent)
      const face = parsePdappFace(searchParams.get(VIEW_PARAM))
      if (face) carry.set(VIEW_PARAM, face)
      return { kind: 'inplace', query: carry.toString() }
    }
    const fileId = info?.file.id
    if (info && fileId && fileId !== info.share.rootFileId) {
      const bindTargetId = info.session?.reopenFileId
        ?? (legacyPayload?.xt && legacyPayload.xtm ? routeCompound(legacyPayload, fileId) : fileId)
      const face = parsePdappFace(searchParams.get(VIEW_PARAM))
      return { kind: 'sub', fileId: bindTargetId, itemId: fileId, query: face ? `${VIEW_PARAM}=${face}` : '' }
    }
    return { kind: 'root' }
  }
  // `tid` null = no explicit pick: the SERVER decides the identity by the entry rule (a
  // member of the link's owning tenant gets it; a single identity is used; several →
  // 409 identity_pick_required → the visitor is routed to the /choose-identity picker).
  const bindAppIdentity = async (tid: string | null, email?: string, autoBind = false) => {
    if (!info) return
    // An explicit pick supersedes any abandoned picker detour's stashed context.
    if (tid) clearPickContext(info.share.token)
    const myGen = ++bindGenRef.current
    const superseded = () => bindGenRef.current !== myGen
    try {
      // raw (#1316): bind creates the NEXT session — its answer is what the new session starts from
      const res = await fetch(`${getApiBase()}/api/share-links/${encodeURIComponent(info.share.token)}/bind-account`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // `session`: the current share session — the server keeps the link grant on the
        // email verified for it; the pick becomes only the stored acting pair.
        body: JSON.stringify({ ...(tid ? { tenantId: tid, email: email ?? null } : {}), ...(sessionId ? { session: sessionId } : {}) }),
      })
      if (superseded()) return
      if (!tid && res.status === 409) {
        // The entry rule found several identities and none is auto-picked → the existing
        // "Choose your identity" picker (on the -api origin, where the session cookie is
        // visible; `?app=` hops back to this frontend after the pick).
        const errBody = (await res.json().catch(() => ({}))) as { error?: string; handoff?: string }
        const err = errBody.error
        if (err === 'identity_pick_required') {
          // The picker mints at the share ROOT, so stash WHERE the visitor is (the same
          // context the bind-success branch below carries: the in-place file + PDApp mode +
          // subfolder, or the sub-file to re-mint) and restore it once the bound session
          // lands (the restore effect below). `replace` — the unbound view is not a step
          // the visitor took, so Back must not return to it (it would auto-bind → picker
          // again).
          // A one-shot marker the picker echoes back (`&pick=`) on the session IT mints — the
          // restore fires only there, never on some later bound session of this link.
          const marker = newPickMarker()
          stashPickContext(info.share.token, marker, bindLandingContext())
          leaveShareView(
            // `ho`: bind-account's one-shot handoff nonce for the CURRENT share session — the
            // picker keeps the link grant on the email verified for it. Never the bearer
            // session id itself in a top-level GET URL (history, access logs).
            `${getApiBase()}/share/${encodeURIComponent(info.share.token)}/choose-identity?app=${encodeURIComponent(window.location.origin)}&pick=${marker}${errBody.handoff ? `&ho=${encodeURIComponent(errBody.handoff)}` : ''}`,
            'share-identity-switched', { replace: true },
          )
          return
        }
      }
      if (res.ok) {
        const boundSid = ((await res.json()) as { sessionId?: string }).sessionId
        if (superseded()) return
        // E7 PR 0b: the session ends at the NAVIGATION to the bound session (`goToShareSession`), not here — a bind
        // whose sub-session mint fails leaves the view on this one, which must keep its in-flight work
        if (boundSid) {
          // A file opened IN PLACE inside a folder share (`?file=`): the ROUTE
          // session is the folder, so `info.file.id` is the FOLDER — it equals
          // rootFileId, and the root branch below would navigate to the bound
          // root session and DROP `?file=`, closing the app the recipient is
          // looking at (the bind fires automatically for any HTML file, so this
          // silently bounced every in-place app back to the listing).
          // Carry the file across instead: the chrome re-mints its sub-session
          // from the BOUND session, so the app runs as the picked identity and
          // stays open.
          if (openFileId) {
            // (#757) …and the PDApp mode it was opened in: a nested PDApp's
            // `?pdapp=` (and the PDApp tab, `?view=run|settings`) ride along, or
            // the bind would drop the recipient out of the app's panel into the
            // parent folder's plain overlay / the entry's HTML editor.
            // (#757 review) …and the SUBFOLDER the listing is on (`?parentId=`),
            // or ✕ / "Back to folder" after the bind lands on the share root
            // instead of the folder the recipient opened the app from.
            const carry = new URLSearchParams({ file: openFileId })
            const pd = searchParams.get(PDAPP_PARAM)
            if (pd) carry.set(PDAPP_PARAM, pd)
            const parent = searchParams.get('parentId')
            if (parent) carry.set('parentId', parent)
            const face = parsePdappFace(searchParams.get(VIEW_PARAM))
            if (face) carry.set(VIEW_PARAM, face)
            // The SILENT auto-bind replaces the entry: it is the same view under
            // the recipient's identity, not a step they took — pushing it left an
            // extra history entry whose Back re-showed the unbound session. An
            // explicit avatar pick stays a push (a deliberate identity switch).
            if (autoBind) autoBindLandedRef.current.add(boundSid)
            goToShareSession(navigate, `/share-view/${boundSid}?${carry.toString()}`, { ends: 'share-identity-switched', replace: autoBind })
            return
          }
          // Preserve the current file: re-open it under the bound identity.
          const fileId = info.file.id
          // A RESHARED (xt) file's session is keyed on the SOURCE id, but a bound sub-session must
          // be re-minted from the COMPOUND `s_<mount>_<src>` (the tree-click / openFile form) so the
          // resharer-capped xt room resolves — the raw source id is out of the app's scope. The
          // compound format is stable (deriveMountFileId → `s_<mount>_<src>`); the collab gate then
          // resolves the bound member's native write on the source and admits the live room.
          const bindTargetId = info.session?.reopenFileId
            ?? (legacyPayload?.xt && legacyPayload.xtm && fileId ? routeCompound(legacyPayload, fileId) : fileId)
          if (fileId && fileId !== info.share.rootFileId) {
            // App in a SUBFOLDER: a sub-session is REQUIRED to anchor the app at
            // its OWN folder (papan_folder_id = the file's parent). Navigating to
            // the bound ROOT session instead would anchor papan_folder_id on the
            // folder-share root → the manifest/consent/scripts.run resolve at the
            // wrong folder. So on sub-session failure, DON'T fall back to the root
            // session — drop to the fallback below (explicit pick reloads the
            // current file's session + retries; auto-bind runs unbound).
            try {
              // raw (#1316): inside bind — mints the bound identity's session (the next one)
              const sub = await fetch(`${getApiBase()}/api/share-links/session/${boundSid}/sub-session`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fileId: bindTargetId }),
              })
              if (superseded()) return
              if (sub.ok) {
                const subSid = ((await sub.json()) as { sessionId?: string }).sessionId
                if (superseded()) return
                if (subSid) {
                  // (#757) Keep the PDApp tab (`?view=run|settings`) across the bind.
                  const face = parsePdappFace(searchParams.get(VIEW_PARAM))
                  if (autoBind) autoBindLandedRef.current.add(subSid)
                  goToShareSession(navigate, `/share-view/${subSid}${face ? `?${VIEW_PARAM}=${face}` : ''}`, { ends: 'share-identity-switched', replace: autoBind })
                  return
                }
              }
            } catch { /* fall through to the fallback below — do NOT mis-anchor at root */ }
          } else {
            // The app IS the share root — the bound root session is correctly anchored.
            if (autoBind) autoBindLandedRef.current.add(boundSid)
            goToShareSession(navigate, `/share-view/${boundSid}`, { ends: 'share-identity-switched', replace: autoBind }); return
          }
        }
      }
    } catch { /* fall through to the fallback below */ }
    // A newer bind superseded this one — don't run the fallback (it would clobber
    // the newer pick's navigation or reload the page out from under it).
    if (superseded()) return
    // Every success path above navigated and returned; reaching here means the
    // bind failed. For an explicit avatar pick, fall back to the Drive-CTA nav
    // (a full reload with ?tenant=). For the SILENT auto-bind, don't reload —
    // just lift the render gate (bindFailed) and let the app run unbound (the
    // same end-state as an anonymous recipient), so a transient bind failure
    // can't wedge the recipient in a reload loop.
    if (autoBind || !tid) { setBindFailed(true); return }
    switchIdentity(tid, email)
  }

  // (parity) Auto-bind a SIGNED-IN recipient (identity decided SERVER-side by the entry rule —
  // never a silent personal default) when they open
  // an anyone-with-link app share UNBOUND — so the app runs as them and the bar shows
  // their (masked) identity + Manage, identical to a view share (which is only "bound"
  // because an identity was established). They can switch via the avatar. Anonymous
  // (not signed-in) recipients stay unbound and get the "connect" prompt. Fires once per unbound route session (re-armed below);
  // the bind navigates to a session carrying verifiedEmail, so it won't re-trigger.
  const autoBoundRef = useRef(false)
  // The file twin's guard (see the file auto-bind below) — declared here so the
  // re-arm effect can reset both before either auto-bind effect runs.
  const autoBoundFileRef = useRef(false)
  // Set only when the silent auto-bind fails (network error / no session
  // minted) — lifts the render gate so the app still runs, unbound.
  const [bindFailed, setBindFailed] = useState(false)
  // Sessions the silent auto-bind itself navigated to. They carry verifiedEmail
  // by construction; if one ever came back WITHOUT it, re-arming there would
  // bind → navigate → re-arm forever, so those are never re-armed.
  const autoBindLandedRef = useRef<Set<string>>(new Set())
  // Re-arm both auto-binds when the ROUTE session changes to an UNBOUND one
  // (#757 full review #1). "Fires once" meant once per MOUNT, but the chrome
  // stays mounted across `/share-view/:sessionId` changes: after the bind
  // replaced sid0 with the bound sid1, browser Back lands on sid0 again —
  // unbound — and re-opening an app there raised `awaitingAppBind` with the
  // guard already spent, so the body was a permanent spinner. Declared BEFORE
  // the auto-bind effects so, in the commit that changes the session, the
  // guards are reset before those effects read them.
  useEffect(() => {
    if (!sessionId || sessionBound) return
    if (autoBindLandedRef.current.has(sessionId)) return
    autoBoundRef.current = false
    autoBoundFileRef.current = false
    setBindFailed(false)
  }, [sessionId, sessionBound])
  useEffect(() => {
    if (autoBoundRef.current) return
    if (!authUser?.personalTenantId) return          // anonymous → stay unbound (connect)
    if (!viewingHtmlApp || !info) return             // only app sessions establish identity
    if (sessionBound) return        // already bound
    autoBoundRef.current = true
    void bindAppIdentity(null, undefined, true)
  }, [authUser, viewingHtmlApp, info, sessionBound])

  // (parity for FILES) Auto-bind a SIGNED-IN member viewing an UNBOUND share FILE they have NATIVE
  // access to — so their native write re-resolves in the sub-session and they can EDIT IN PLACE
  // (live collab), exactly like a normal share-view. Without this, a file opened through an
  // anonymous app link (ppn.gg) stays unbound (verifiedEmail=null): /info can't promise a live room
  // the collab gate will honor for a cookie-only member, so it reports read-only. Binding re-mints a
  // verifiedEmail-carrying sub-session, which the collab gate resolves via the reliable Path A
  // (verifiedEmail) — the same reason a normal bound share-view lets a native member write. Skips
  // apps (handled above), folders (no room), and already-bound sessions. Reshared `xt` files ARE
  // bound here too (bindAppIdentity re-mints from the compound `s_<xtm>_<source>` and the collab
  // gate then admits the member's native SOURCE write) — this comment previously claimed xt was
  // skipped; the edit-in-place-for-a-reshared-file fix removed that skip. Gated on the resolver's
  // native-access signal so it fires ONLY when binding actually upgrades view→edit — a link-only
  // viewer never re-mints. Fires once; on any failure it degrades to unbound read-only (never worse).
  useEffect(() => {
    if (autoBoundFileRef.current) return
    if (!authUser?.personalTenantId) return                   // anonymous → stay unbound (read-only)
    if (!info || viewingHtmlApp) return                       // apps establish identity above
    if (info.file.isFolder) return                            // only a file needs a room (incl. reshared xt)
    if (sessionBound) return                 // already bound
    if (resolver?.accessibleInActiveTenant !== true) return   // only a NATIVE member gains edit by binding
    autoBoundFileRef.current = true
    void bindAppIdentity(null, undefined, true)
  }, [authUser, info, viewingHtmlApp, sessionBound, resolver])

  // Restore the context stashed before the /choose-identity detour (see bindAppIdentity's
  // 409 branch) — ONLY on the session that detour minted: the picker echoes the one-shot
  // marker as `?pick=` and the stash must carry the same one. The picker's `?tenant=` (the
  // identity just confirmed) rides along; `pick` itself is dropped.
  const pickMarker = searchParams.get('pick')
  // A refused identity switch (the choose-identity select returned here) — shown, not a dead end.
  const identitySwitchError = searchParams.get('identity_error')
  // Take the view to a landing on THIS session (`sid`) — shared by the two restores below.
  // `inplace` rewrites the URL; `sub` asks /sub-session for the item (the server re-checks it
  // against this session) and opens it, or stays on this session when that is refused.
  // `tenant` (the identity just decided) rides along on every landing.
  const landOn = useCallback((ctx: ShareLandingContext, sid: string, clean: boolean) => {
    const tenant = searchParams.get('tenant')
    const withTenant = (q: URLSearchParams) => { if (tenant) q.set('tenant', tenant); return q.toString() }
    const go = (target: string, query: string) => {
      const q = withTenant(new URLSearchParams(query))
      goToShareSession(navigate, `/share-view/${target}${q ? `?${q}` : ''}`, { ends: false, replace: true })  // a landing: the same identity
    }
    if (ctx.kind === 'root') { if (clean) go(sid, ''); return }
    if (ctx.kind === 'inplace') { go(sid, ctx.query); return }
    void (async () => {
      try {
        const sub = await sessionFetch(`${getApiBase()}/api/share-links/session/${sid}/sub-session`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileId: ctx.fileId }),
        })
        const subSid = sub.ok ? ((await sub.json()) as { sessionId?: string }).sessionId : undefined
        if (subSid) go(subSid, ctx.query)
        else if (clean) go(sid, '') // gone / no longer reachable: the session itself, never mis-anchored
      } catch { /* stay on this session */ }
    })()
  }, [searchParams, navigate])

  useEffect(() => {
    if (!pickMarker || !info || !sessionId || !sessionBound) return
    // `clean`: always leave the URL without `pick`, even with nothing to restore.
    landOn(takePickContext(info.share.token, pickMarker) ?? { kind: 'root' }, sessionId, true)
  }, [pickMarker, info, sessionId, sessionBound, landOn])

  // Land where a re-entered view pointed (see reenterAtFrontDoorRef) — once per page load, on
  // the first session that loads. The stash is consumed then whatever happens (never left for
  // a later, unrelated entry), and applied only while the landing is still the bare link root:
  // never over a location the URL already names, nor alongside a /choose-identity detour
  // (which owns its own landing). `infoSid === sessionId`: judged on THIS session's info.
  const reentryLandingTakenRef = useRef(false)
  useEffect(() => {
    if (reentryLandingTakenRef.current || !info || !sessionId || infoSid !== sessionId) return
    reentryLandingTakenRef.current = true
    const taken = takeReentryLanding(info.share.token, sessionStorageOrNull(), Date.now())
    if (!taken || pickMarker || locationQuery(searchParams)) return
    landOn(resolveReentryLanding(taken, info.file.id), sessionId, false)
  }, [info, infoSid, sessionId, pickMarker, searchParams, landOn])

  // A view left open past its session's expiry: what is on screen keeps working (a live
  // editor's connection is not cut by expiry), so nothing happens on a timer or on returning
  // to the tab — reloading under someone mid-work is worse than a failed click. Once the
  // session is near or past expiry (by this clock, with a margin for skew), the next
  // NAVIGATION asks /info and, only if the session is dead or stale, re-enters at the front
  // door at the location being navigated to (never while an editor holds unsaved changes —
  // that navigation stays pending). At most one probe per 30s; a live answer, or any
  // other error (a missing file…), changes nothing — the surface that failed reports it.
  const [sessionLapsed, setSessionLapsed] = useState(false)
  const sessionExpMs = typeof tokenHeader?.exp === 'number' ? tokenHeader.exp * 1000 : null
  useEffect(() => {
    setSessionLapsed(false)
    if (!sessionExpMs) return
    const t = window.setTimeout(() => setSessionLapsed(true), Math.max(0, sessionExpMs - LAPSE_MARGIN_MS - Date.now()))
    return () => window.clearTimeout(t)
  }, [sessionExpMs])
  const lastLapseProbeRef = useRef(0)
  /** False when skipped by the throttle — which only applies BEFORE expiry (a live answer is
   *  likely); once past it, every navigation asks. */
  const probeLapsedSession = useCallback((): boolean => {
    if (!sessionId) return true
    // An editor holding unsaved changes is never reloaded under: re-entry waits (pending) for
    // the next navigation after it is clean — losing the draft is worse than a failed click.
    if (contentDirtyRef.current) return false
    const beforeExpiry = sessionExpMs !== null && Date.now() < sessionExpMs
    if (beforeExpiry && Date.now() - lastLapseProbeRef.current < LAPSE_PROBE_INTERVAL_MS) return false
    lastLapseProbeRef.current = Date.now()
    // raw (#1316): DETECTS a lapse — dropping its answer at a session end would hide the lapse it reports
    void fetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/info`, { credentials: 'include' })
      .then(async (res) => {
        if (res.ok) return
        reenterAtFrontDoorRef.current(await res.json().catch(() => ({})))
      })
      .catch(() => { /* unanswerable: leave the view; its next request says what happened */ })
    return true
  }, [sessionId, sessionExpMs])
  // `lastLocationRef` detects THAT a navigation happened (always advanced); `probePendingRef`
  // remembers one the throttle skipped, so the next navigation asks even if it returns to a
  // location seen before.
  const locationKey = searchParams.toString()
  const lastLocationRef = useRef<string | null>(null)
  const probePendingRef = useRef(false)
  useEffect(() => {
    if (!sessionLapsed) { lastLocationRef.current = null; probePendingRef.current = false; return }
    const navigated = lastLocationRef.current !== null && lastLocationRef.current !== locationKey
    lastLocationRef.current = locationKey
    if (!navigated && !probePendingRef.current) return
    probePendingRef.current = !probeLapsedSession()
  }, [sessionLapsed, locationKey, probeLapsedSession])

  // A page RESTORED from the browser's back-forward cache (Back / Forward) shows the view as it
  // was when the visitor left, without asking the server. Ask now: a session ended meanwhile
  // (logged out in another tab, expired, made stale) re-enters at the front door or shows why,
  // instead of leaving the old contents on screen.
  const errorRef = useRef(error)
  errorRef.current = error
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted || !sessionId || errorRef.current) return
      // raw (#1316): a bfcache restore's check that the session still lives — it detects the end, never dropped by it
      void fetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/info`, { credentials: 'include' })
        .then(async (res) => {
          if (res.ok) return
          const body = await res.json().catch(() => ({}))
          if (!reenterAtFrontDoorRef.current(body)) setError(body.error || `Session unavailable (${res.status})`)
        })
        .catch(() => { /* unanswerable: the view's next request says what happened */ })
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [sessionId])

  // Plumb the share session into the global API client so server routes
  // that opt into the share principal (/api/sqlite/*, /api/wopi/token) can
  // authorize the recipient. The ref tracks the live sessionId so the
  // getter doesn't capture a stale closure on route-param navigation.
  //
  // The installation must happen BEFORE child effects mount —
  // CollabDocModifier's mount effect calls /api/wopi/token, and
  // without the getter installed first that request goes out
  // without x-share-session and requireAuth 401s. `useLayoutEffect`
  // gives us the right ordering: it fires synchronously after DOM
  // mutations but before child effects, and isn't double-invoked
  // by StrictMode the way a render-time side effect would be.
  //
  // It tracks the ACTIVE session (the open file's sub-session when one is open
  // in place, else the route's). A file opened in-place is authorized by its own
  // sid — pinning this to the route's folder session would send the folder's
  // token for the file's /api/sqlite and /api/wopi/token calls, and the server
  // would (correctly) refuse.
  const sessionIdRef = useRef<string | null>(activeSid || null)
  sessionIdRef.current = activeSid || null
  useLayoutEffect(() => {
    setShareSessionTokenGetter(() => sessionIdRef.current)
    return () => {
      setShareSessionTokenGetter(() => null)
    }
  }, [])

  // U1: the save-status poll loop runs detached from React; this lets it stop
  // (and skip setState) once the component unmounts / the visitor navigates away.
  //
  // The effect MUST re-arm the flag on mount, not just clear it on unmount.
  // StrictMode runs mount → cleanup → mount in dev, so a cleanup-only effect
  // left `mountedRef.current === false` on a component that was very much
  // mounted — and the poll loop bails on its first tick against exactly that
  // flag. Symptom: "Save a copy" span forever showing "Saving…" while the job
  // had already completed, with not one /save-status request ever sent.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // A dead session (expired after 4h — a bookmark, or a tab left open — or stale after the
  // link's settings changed) re-enters at the link's FRONT DOOR, `/share/<token>`, where
  // every admission decision is taken again: a public link mints a fresh session, a
  // restricted one admits a signed-in recipient again or asks its own gate. Where the view
  // pointed (folder, file, PDApp face) is stashed in this tab and restored on landing.
  // `replace`: the dead URL leaves history, so Back can't loop. Once per link per minute.
  const reenteringRef = useRef(false)
  const reenterAtFrontDoorRef = useRef<(body: { error?: unknown; shareToken?: unknown }) => boolean>(() => false)
  reenterAtFrontDoorRef.current = (body) => {
    if (reenteringRef.current) return true // already leaving: no second claim, no error flash
    const storage = sessionStorageOrNull()
    const href = claimShareReentry(body, storage, Date.now())
    if (!href || typeof body.shareToken !== 'string') return false
    stashReentryLanding(
      body.shareToken,
      reentryLandingFromServer((body as { landing?: { fileId?: unknown; itemId?: unknown } }).landing, searchParams)
        ?? reentryLandingFrom(legacyPayload, searchParams), // an older API (no `landing`): a v1 token's payload
      storage, Date.now(),
    )
    reenteringRef.current = true
    leaveShareView(href, 'share-session-lapsed', { replace: true })
    return true
  }

  // The open item's session answered 404 for its serve token: ask /info what happened instead
  // of spinning on a token that will never come (#989 review). Gone (404 / 409) → the link's
  // front door (re-entry) or the error page; alive → an API without /serve-token: serve on the
  // session (phase 2's rolling-deploy fallback — that window only exists mid-deploy, so a visit
  // bound on a newer instance meanwhile may see a blank frame until reload, accepted). A
  // transient answer (a network error, 5xx, 429) is retried with backoff, never shown as gone.
  useEffect(() => {
    // Only once the view has loaded (bound + read) its own session — before that its own /info
    // decides, and a second /info would race the bind.
    if (!serveActiveState.gone || !activeSid || !info) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    // #1316: asked (and retried) only within the session it started in — this chrome is not remounted on a session
    // change, so a retry after the end would go out as the NEXT session; nothing is written then either
    const start = currentSessionSignal()
    const attempt = async (n: number): Promise<void> => {
      if (sessionEndedSince(start)) return
      const retry = () => { if (!cancelled) timer = setTimeout(() => { void attempt(n + 1) }, serveTokenRetryDelay(n)) }
      let res: Response
      try {
        res = await sessionFetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(activeSid)}/info`, { credentials: 'include' })
      } catch (err) {
        if (sessionEndedSince(start, err)) return
        retry()
        return
      }
      if (cancelled) return
      if (res.ok) { setRawServeSid(activeSid); return }
      if (res.status >= 500 || res.status === 429) { retry(); return }
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      if (cancelled) return
      if (reenterAtFrontDoorRef.current(body)) return
      setError(body.error || 'session_invalid')
    }
    void attempt(0)
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [serveActiveState.gone, activeSid, !!info])

  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    void (async () => {
      try {
        // #989: bind this session to this browser before reading it. Another browser goes to
        // the link's front door; the session's own account there gets a session of its own
        // (D1), landing where this one pointed — as a re-entry does.
        const bound = await bindShareSession(sessionId)
        if (cancelled) return
        if (bound.kind === 'reenter' && reenterAtFrontDoorRef.current(bound.body)) return
        if (bound.kind === 'swap') {
          stashReentryLanding(bound.shareToken, reentryLandingFromServer(bound.landing, searchParams) ?? { kind: 'root' }, sessionStorageOrNull(), Date.now())
          // this browser's own session replaces the one it was handed: the handed one ends first
          goToShareSession(navigate, `/share-view/${bound.sessionId}`, { ends: 'share-session-replaced', replace: true })
          return
        }
        const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/info`, {
          credentials: 'include',
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          if (cancelled) return
          // A bookmarked view whose session expired / went stale re-enters at the link's
          // front door, back to where it pointed (see reenterAtFrontDoor).
          if (reenterAtFrontDoorRef.current(body)) return
          setError(body.error || `Session unavailable (${res.status})`)
          return
        }
        const raw = (await res.json()) as SessionInfo
        seedServeToken(sessionId, (raw as { serveToken?: unknown }).serveToken)
        const body = viewOnlyUntilAccepted(raw)
        clearShareReentryGuard(body.share.token, sessionStorageOrNull())
        // Advance `info` and the session it belongs to together, so the viewer
        // never renders `info`'s file against a newer, mismatched session.
        if (!cancelled) { setInfo(body); setInfoSid(sessionId) }
      } catch (err) {
        // #1316: a dropped answer is no error — the session it was asked for ended (the view is leaving)
        if (!cancelled) { const text = errorText(err, 'Network error'); if (text !== null) setError(text) }
      }
    })()
    return () => { cancelled = true }
  }, [sessionId])

  // A password change on this browser revokes a share session that acts as the
  // changing account — one carrying a verified email the account holds (#601).
  // Ask the server whether THIS session was refused before leaving: a verified
  // email the account does not hold was never revoked, and re-opening would throw
  // away the view and send its recipient back through the email gate. Refused:
  // re-open the link at its front door — a signed-in recipient is admitted again
  // on the new session (listed, native or bound), anyone else gets the link's own
  // gate — instead of a view whose every request now fails.
  const shareTokenForReopen = info?.share.token ?? null
  const sessionActsAsAccount = sessionBound

  // #809: a live editor's socket closed because its session was REVOKED — a "Log out" in
  // another tab (it ends every session of this visit). Ask the server whether THIS session
  // is gone; if so, go to the link's front door instead of "Reconnecting…" for ever.
  useEffect(() => {
    if (!shareTokenForReopen || !sessionId) return
    const onRevoked = () => {
      // E7 PR 0b: the session-ended helper FIRST — the visit may be over; its in-flight work is dropped NOW, before
      // the check (an answer under a revoked session would only race the navigation)
      endShareVisit('share-revoked')
      // raw (#1316): asked AFTER the visit ended, to decide whether to leave — a session-bound fetch would drop it
      void fetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/info`, { credentials: 'include' })
        .then((res) => {
          if (res.status === 404) leaveShareView(`/share/${encodeURIComponent(shareTokenForReopen)}`, 'share-revoked')
        })
        .catch(() => { /* unanswerable: leave the view; its next request says what happened */ })
    }
    window.addEventListener(COLLAB_SESSION_REVOKED_EVENT, onRevoked)
    return () => window.removeEventListener(COLLAB_SESSION_REVOKED_EVENT, onRevoked)
  }, [shareTokenForReopen, sessionId])
  // #961: a Log out in another tab of this browser ended every visit the browser has open.
  useLeaveOnSignOutElsewhere(sessionId, shareTokenForReopen)
  // #1316 review round 2 (owner, final): a DRIVE account end (a sign-out, signed out in another tab, another account,
  // a session that died) loads this link's front door, so the visit restarts under whoever is signed in now —
  // `accountEndReloadTarget` reads it from here (the one place that decides the target).
  useEffect(() => {
    if (!shareTokenForReopen) return
    return setAccountEndShareFrontDoor(`/share/${encodeURIComponent(shareTokenForReopen)}`)
  }, [shareTokenForReopen])
  useEffect(() => {
    if (!shareTokenForReopen || !sessionActsAsAccount || !sessionId) return
    // raw (#1316): the check below decides whether to leave for a session the rotation may have ended — never dropped by it
    return onSessionRotated(() => {
      void fetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/info`, { credentials: 'include' })
        .then((res) => {
          if (res.status === 404) leaveShareView(`/share/${encodeURIComponent(shareTokenForReopen)}`, 'share-revoked')
        })
        .catch(() => { /* unanswerable: leave the view; its next request says what happened */ })
    })
  }, [shareTokenForReopen, sessionActsAsAccount, sessionId])

  // ── PDApp mode (#757) ─────────────────────────────────────────────────────
  // A shared `.pdapp` (the share ROOT, or a nested one opened from the listing
  // as `?pdapp=<id>`) renders as an app, not a flat listing of its internals:
  // Explorer + viewer, the panel labelled "PDApp" with Run /
  // Settings, landing on the app tab (the running app). The pure decisions live in
  // ./share/pdapp.ts; this block only wires them to state.
  const pdappParam = searchParams.get(PDAPP_PARAM)
  // The CANDIDATE PDApp — a PDApp root, or whatever `?pdapp=` names. A nested
  // candidate is only CONFIRMED once the entry lookup below has seen its name
  // (review #4): a hand-edited `?pdapp=` naming a plain folder falls back to the
  // normal folder view instead of being dressed up as an app.
  const pdappCandidateId = resolvePdappFolderId(info, pdappParam)
  const pdappIsRoot = shareRootIsPdapp(info)
  // The PDApp's entry (index.html), looked up through the share session's own
  // scope-checked /list — the same call the explorer makes — paging until it is
  // found (a big app root must not hide it past page one). Keyed on the PDApp
  // alone: the route sid changes on every file switch in a direct sub-file
  // session, but the app's entry doesn't, so the lookup must not refire (and
  // flash Run disabled) each time. The live sid is read through a ref.
  const [pdappEntry, setPdappEntry] = useState<({ folderId: string } & PdappEntryLookup) | null>(null)
  const [pdappEntryNonce, setPdappEntryNonce] = useState(0)
  const pdappListSidRef = useRef(bodySid)
  pdappListSidRef.current = bodySid
  /** #1316: the sid of a lookup its session's end dropped — asked again only once the view reads a DIFFERENT session
   *  (`bodySid` moves with the new session's `/info`), never on the old sid as the new session. */
  const pdappReaskRef = useRef<string | null>(null)
  useEffect(() => {
    if (!pdappCandidateId) { setPdappEntry(null); return }
    const sid = pdappListSidRef.current
    if (!sid) return
    let cancelled = false
    const folderId = pdappCandidateId
    const start = currentSessionSignal()
    void lookupPdappEntry(folderId, async (offset, cursor) => {
      const r = await sessionFetch(
        `${getApiBase()}/api/share-links/session/${sid}/list?parentId=${encodeURIComponent(folderId)}&limit=${PDAPP_LIST_PAGE}&${typeof cursor === 'number' ? `cursor=${cursor}` : `offset=${offset}`}`,
        { credentials: 'include' },
      )
      if (!r.ok) throw new Error(`list ${r.status}`)
      return (await r.json()) as PdappListPage
    }, { verifyIsPdapp: !pdappIsRoot }).then((result) => {
      if (cancelled) return
      // #1316: the lookup's session ended (its answer dropped — `result` is no real 'error'): write nothing. The chrome
      // outlives a share-session change (no remount on a `:sessionId` change); the effect below asks again once the
      // view reads the new session.
      if (sessionEndedSince(start)) {
        // the new session's /info already landed (the view moved first): ask now — the effect below won't run again
        if (pdappListSidRef.current && pdappListSidRef.current !== sid) setPdappEntryNonce((n) => n + 1)
        else pdappReaskRef.current = sid
        return
      }
      setPdappEntry({ folderId, ...result })
    })
    return () => { cancelled = true }
  }, [pdappCandidateId, pdappIsRoot, pdappEntryNonce])
  useEffect(() => {
    if (pdappReaskRef.current === null || !bodySid || bodySid === pdappReaskRef.current) return
    pdappReaskRef.current = null
    setPdappEntryNonce((n) => n + 1)
  }, [bodySid])
  const pdappLookup = pdappCandidateId && pdappEntry?.folderId === pdappCandidateId ? pdappEntry : null
  const pdappFolderId = pdappLookup?.status === 'not-pdapp' ? null : pdappCandidateId
  const pdappEntryReady = !!pdappFolderId && pdappLookup?.status === 'ready'
  const pdappEntryError = !!pdappFolderId && pdappLookup?.status === 'error'
  const pdappEntryId = pdappLookup?.status === 'ready' ? pdappLookup.entryId : null
  const retryPdappEntry = useCallback(() => {
    setPdappEntry(null)
    setPdappEntryNonce((n) => n + 1)
  }, [])
  // A `?pdapp=` that turned out not to be a PDApp: drop it from the URL
  // (replace — it was never a real state) so Back / reload / share-the-URL
  // don't carry a dead param.
  const pdappRejected = pdappLookup?.status === 'not-pdapp'
  useEffect(() => {
    if (!pdappRejected) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(PDAPP_PARAM)
      next.delete(VIEW_PARAM)
      return next
    }, { replace: true })
  }, [pdappRejected, setSearchParams])

  // Land on the running app — once per PDApp per mount, mirroring the
  // workspace's auto-open (WorkspaceView: MiniAppEditor opens on 'run' when no
  // tab was restored). REPLACE, so Back from the app returns to where the
  // recipient came from (the folder listing for a nested PDApp) rather than to
  // an intermediate "no file" state. The ref is the close-trap guard: closing
  // the app does not immediately reopen it. A deep link that already names a
  // file keeps that file. Folder session only — a direct sub-file session IS a
  // file already.
  const autoOpenedPdappRef = useRef<string | null>(null)
  const isFolderSession = !!info?.file.isFolder
  useEffect(() => {
    // Leaving PDApp mode re-arms the landing (review #1): the guard is "once per
    // VISIT", not once per mount — re-entering the same nested PDApp from the
    // listing must land on the running app again, like the first time.
    if (!pdappFolderId) { autoOpenedPdappRef.current = null; return }
    if (!isFolderSession || !pdappEntryReady) return
    if (autoOpenedPdappRef.current === pdappFolderId) return
    autoOpenedPdappRef.current = pdappFolderId
    if (openFileId || !pdappEntryId) return
    const entryId = pdappEntryId
    // …on the APP TAB (`view=run`), not the entry's HTML editor — unless the
    // URL asked for the SETTINGS tab (a reload / deep link to Settings with no
    // file open): keep it, with the app mounted underneath. The gate still
    // demotes `settings` to `run` for a viewer who can't manage the app.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('file', entryId)
      next.set(VIEW_PARAM, prev.get(VIEW_PARAM) === 'settings' ? 'settings' : 'run')
      return next
    }, { replace: true })
  }, [pdappFolderId, isFolderSession, pdappEntryReady, pdappEntryId, openFileId, setSearchParams])
  // The landing is still to come (entry lookup in flight, or the auto-open
  // hasn't fired yet) — the overlay shows its spinner, not the "nothing open"
  // state, so a PDApp never flashes an empty panel on its way to running.
  const pdappLandingPending = !!pdappFolderId && isFolderSession && !openFileId &&
    !pdappEntryError &&
    (!pdappEntryReady || (!!pdappEntryId && autoOpenedPdappRef.current !== pdappFolderId))

  // Settings — the recipient's NATIVE access to the PDApp folder, resolved
  // separately from the CTA resolver below (which reports on what's in view and
  // answers for a `.shared` mount with a non-write placeholder). Only for
  // signed-in recipients: an anonymous one can't hold native access, so the
  // call would be wasted.
  //
  // Asked under the share chrome's EFFECTIVE identity (`effectiveTenant` +
  // `effectiveEmail`: the `?tenant=` / stored (tenant, email) pair decided at entry,
  // else the personal tenant — never an auto-picked org), and the Settings tab then
  // makes its /api/pdapp calls as that same identity (SharePdappSettings scopes it
  // onto the api-client) — so the gate that SHOWS the tab and the identity the server
  // ENFORCES on can't disagree. An org admin manages an org-owned app by acting AS
  // that org (the entry rule auto-picks a member of the link's owning tenant; the
  // account picker switches). Not the api-client's own getter: on `/share-view/…`
  // TenantProvider resolves the viewer's PERSONAL tenant regardless of the session.
  const [pdappResolver, setPdappResolver] = useState<{ folderId: string; tenantId: string | null; orgEmail: string | null; r: ResolverInfo } | null>(null)
  const recipientSignedIn = resolver?.isAuthenticated === true
  const apiTenant = effectiveTenant
  const apiOrgEmail = effectiveEmail
  useEffect(() => {
    // Cleared FIRST on any identity change: the tab must not keep acting as the
    // new identity on the OLD identity's approval while the re-ask is in flight.
    setPdappResolver(null)
    if (!pdappFolderId || !shareToken || !recipientSignedIn || identityPending) return
    let cancelled = false
    const headers: Record<string, string> = {}
    if (apiTenant) headers['x-tenant-id'] = apiTenant
    if (apiOrgEmail) headers['x-org-email'] = apiOrgEmail
    sessionFetch(
      `${getApiBase()}/api/share-links/${encodeURIComponent(shareToken)}/resolve-for-recipient?fileId=${encodeURIComponent(pdappFolderId)}`,
      { credentials: 'include', headers },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((body: ResolverInfo | null) => { if (!cancelled) setPdappResolver(body ? { folderId: pdappFolderId, tenantId: apiTenant ?? null, orgEmail: apiOrgEmail ?? null, r: body } : null) })
      .catch((err) => { if (!cancelled && !isSessionEndedError(err)) setPdappResolver(null) })
    return () => { cancelled = true }
  }, [pdappFolderId, shareToken, recipientSignedIn, apiTenant, apiOrgEmail, identityPending])
  // The answer counts only for the folder AND identity it was asked under — in
  // the very render an identity changes, the old approval must not let the
  // Settings tab mount (and fire manage-gated reads) as the new identity.
  const pdappManageable = !identityPending && !!pdappFolderId &&
    pdappGateHolds(pdappResolver, pdappFolderId, apiTenant ?? null, apiOrgEmail ?? null) &&
    pdappCanManage(pdappResolver!.r, pdappFolderId)
  // Is the app's entry (index.html) the file open right now? `null` while the
  // entry lookup is still in flight — unknown, so nothing is demoted on it yet.
  // A folder session opens files IN PLACE (`?file=`); a direct sub-file session
  // IS its file.
  const pdappEntryOpen: boolean | null = !pdappFolderId || !pdappEntryId || !info ? null
    : info.file.isFolder ? openFileId === pdappEntryId : info.file.id === pdappEntryId
  // The PDApp tab in view (`?view=run|settings`). Settings is honoured only
  // while the gate holds — a hand-edited `view=settings` for a recipient who
  // can't manage the app (or before the gate resolves) is demoted: to the APP
  // TAB when the entry is what's open (the app is what runs beneath it), else
  // to NO face — a plain file's editor, with its Save / Discard (#757 full
  // review #4: demoting to 'run' over a non-entry file hid Save and pressed Run
  // while the viewer showed that file's editor).
  const rawPdappFace = pdappFolderId ? parsePdappFace(searchParams.get(VIEW_PARAM)) : null
  const pdappFace: PdappFace | null = rawPdappFace === 'settings' && !pdappManageable
    ? (pdappEntryOpen === false ? null : 'run')
    : rawPdappFace
  // What the viewer slot BENEATH the tabs shows (#757 full review #2). Settings
  // is stacked over whatever was open when it was pressed — the running app, or
  // index.html's EDITOR — and must keep it: swapping the editor for the app
  // unmounted the editor, booted the app hidden, and closing Settings then
  // landed on Run. `settingsOver` remembers the face Settings was opened from;
  // a reload / deep link to Settings (nothing remembered) keeps the landing's
  // app beneath it.
  const [settingsOver, setSettingsOver] = useState<'run' | 'editor'>('run')
  const pdappViewerFace: 'run' | null = pdappFace === 'settings'
    ? (settingsOver === 'run' ? 'run' : null)
    : pdappFace === 'run' ? 'run' : null
  // On the PDApp's own face (the app tab, or Settings) with its entry open, the
  // open "file" is index.html only as the means of running the app — the thing
  // in view is the APP. The top bar's CTA and Download follow it (#757 full
  // review #3), as they did before PDApp mode opened the entry on landing.
  const onPdappFace = !!pdappFace && pdappEntryOpen === true
  const ctaFileId: string | null = onPdappFace
    ? pdappFolderId
    : (openFileId || searchParams.get('parentId') || null)
  // I33 — an office editor open here follows its document's session events on the share
  // visitor's own office socket (the drive editor's signals, without a tenant socket).
  // The editor choice for the ACTIVE file, and the byte probe it may depend on (an
  // unregistered extension's binary sniff, an editor's size limit). The probe URL is
  // the same serve URL the body loads (`inlineUrl` below), so the body's own
  // `useShareFileProbe` reads this very result — one fetch, one answer for both.
  //
  // Not asked when nothing reads it: on the PDApp's APP tab (or Settings over it) the
  // entry runs as the app (SharePdappRunView) — no editor is chosen, no Save is shown,
  // and each probe is one more GET on the serve URL (I111 follow-up). Declared down here,
  // after the PDApp face is known, for that reason.
  const activeRunsAsApp = pdappViewerFace === 'run' && pdappEntryOpen === true
  const activeNeedsProbe = !activeRunsAsApp && !!activeInfo && chooseShareEditor(activeInfo.file, null, { connectionLink: activeInfo.share.connectionLink === true }).kind === 'probe'
  const activeProbe = useShareFileProbe(
    activeNeedsProbe && activeInfo && serveActive ? shareServeUrl(serveActive, encodeURIComponent(activeInfo.file.name)) : null,
  )
  const activeEditorKind = activeInfo ? shareEditorFor(activeInfo.file, activeProbe, activeInfo.share.connectionLink === true) : null
  // #1316 review round 1: keyed on the share session it serves — a share identity switch keeps this view and reopens it.
  // Review round 2: an end that KEEPS the share session (the sid stays) reopens it too (`useShareSessionKeptNonce`).
  const shareSessionKept = useShareSessionKeptNonce()
  useShareOfficeSocket(activeEditorKind === 'office-docsapi', activeSid || null, shareSessionKept)
  // THE single gate for Save/Discard on every surface.
  //
  // Two independent facts, each read from the thing that actually knows it:
  //   • may this recipient write?  → the ACTIVE file's permission (not the ROUTE
  //     session's — the overlay used to read that one, so a sub-session resolving
  //     narrower than its folder grant would render Save with no refs behind it);
  //   • CAN this file be saved?    → `shareEditorFor`, the dispatch's own first
  //     decision. It is NOT inferred from the editor's lifecycle any more. That
  //     inference ("an editor relayed dirty state for this file") is what produced
  //     both bugs on this branch — Save on a .csv that has no save path, and Save
  //     vanishing off a .md whose editor was reconciled in place and never re-relayed.
  const canSaveActive = canEditActive && activeEditorKind !== null
  // Save / Discard belong to an EDITOR. The PDApp tabs render none (the app
  // tab is the running app, not index.html's editor), so an `edit` link must
  // not be offered a Save with nothing wired behind it.
  const canSaveShown = canSaveActive && !pdappFace

  // Bumped to re-run an app that is ALREADY the open file (Run pressed again):
  // keys the viewer, so it remounts — the same reset a refresh gives.
  const [runNonce, setRunNonce] = useState(0)

  // #776: the visitor accepted the restricted-link disclosure — from the banner OR from the
  // dialog a file action (rename, upload, …) raised. Re-read the session(s) on screen so the
  // view stops being view-only, and remount the editor: it joined read-only, and only a
  // fresh join is writable. No page reload — a write the dialog was raised for is still
  // being retried.
  const acceptSessionsRef = useRef<{ root: string | null; open: OpenFile | null }>({ root: null, open: null })
  acceptSessionsRef.current = { root: sessionId ?? null, open: openFile }
  useEffect(() => {
    const onAccepted = () => {
      const { root, open } = acceptSessionsRef.current
      const read = async (sid: string) => {
        const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sid)}/info`, { credentials: 'include' })
        return res.ok ? viewOnlyUntilAccepted((await res.json()) as SessionInfo) : null
      }
      void (async () => {
        const [rootInfo, openInfo] = await Promise.all([root ? read(root) : null, open ? read(open.sid) : null])
        if (rootInfo && root && acceptSessionsRef.current.root === root) {
          // Advance the sid with it: the identity remembered from `info` is keyed on `infoSid`.
          setInfo((prev) => (prev ? rootInfo : prev))
          setInfoSid(root)
        }
        if (openInfo && open) setOpenFile((prev) => (prev && prev.sid === open.sid ? { ...prev, info: openInfo } : prev))
        setRunNonce((n) => n + 1)
      })().catch(() => { /* the next write asks again */ })
    }
    window.addEventListener(ATTRIBUTION_ACCEPTED_EVENT, onAccepted)
    return () => window.removeEventListener(ATTRIBUTION_ACCEPTED_EVENT, onAccepted)
  }, [])

  // Resolver runs after session info is loaded. Uses the chrome's
  // currently-active tenant (URL ?tenant= override → header → user default)
  // to determine whether the recipient already has the file in their
  // drive. Re-runs when the tenant picker changes OR the recipient
  // navigates into a subfolder — drives the CTA swap between "Add to
  // Drive" and "Open in Drive" AND keeps the driveUrl pointed at the
  // file/folder currently in view (so Open in Drive lands on the
  // active subfolder, not the share root).
  useEffect(() => {
    if (!info) return
    // Identity still loading: drop the previous identity's answer rather than show it.
    if (identityPending) { setResolver(null); return }
    let cancelled = false
    void (async () => {
      try {
        const headers: Record<string, string> = {}
        if (effectiveTenant) headers['x-tenant-id'] = effectiveTenant
        if (effectiveEmail) headers['x-org-email'] = effectiveEmail
        // fileId — what the user is actively viewing, most-specific first:
        //   ?file=   a file opened IN PLACE over the listing
        //   ?parentId= the subfolder they've navigated into
        //   else     the share root / single-file share's own file
        //
        // `openFileId` is load-bearing and easy to miss: the in-place open keeps
        // the ROUTE session pinned to the FOLDER, so `info.file` is the folder
        // for the whole visit. Before in-place open, opening a sub-file swapped
        // the route session and `info.file` WAS the open file, so this resolved
        // correctly by accident. Without it, a recipient looking at a file gets
        // an "Open in my Drive" that lands on the folder, and an Add-vs-Open CTA
        // computed from the folder's accessibility rather than the file's.
        //
        // (#757 full review #3) …except on a PDApp's own face (the app tab, or
        // Settings): the open "file" there is the app's index.html only as the
        // means of running it — what the recipient is looking at is the APP, so
        // "Open in my Drive" must land on the `.pdapp` folder, not index.html.
        const fileIdParam = ctaFileId ?? info.file.id
        // Split-origin (#234): must hit the -api origin where the session cookie
        // lives — a relative URL hits the cookie-less frontend host and the server
        // returns isAuthenticated:false, mis-rendering a signed-in recipient as
        // anonymous (Account picker / Open-in-Drive / Add-vs-Open CTA all gate off).
        const url = `${getApiBase()}/api/share-links/${encodeURIComponent(info.share.token)}/resolve-for-recipient?fileId=${encodeURIComponent(fileIdParam)}`
        const res = await sessionFetch(url, {
          credentials: 'include',
          headers,
        })
        if (!res.ok) {
          if (!cancelled) setResolver({ accessibleInActiveTenant: false })
          return
        }
        const body = (await res.json()) as ResolverInfo
        if (!cancelled) setResolver(body)
      } catch (err) {
        if (!cancelled && !isSessionEndedError(err)) setResolver({ accessibleInActiveTenant: false })   // #1316: a drop is no answer
      }
    })()
    return () => { cancelled = true }
  }, [info, effectiveTenant, effectiveEmail, ctaFileId, identityPending])

  // #796 — an embedded app's host (HtmlViewer) asks for SIGN-IN when the server answers
  // `reentry_required`: the app REQUIRES identity and this share visit has none. The chrome
  // answers with its OWN sign-in — the Sign in button's action (sign in, then the identity
  // picker) — or, for a visitor already signed in on an identity-less session, straight to
  // the identity picker. One sign-in flow; the app never drives it.
  const shareSignInRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    const onSignIn = () => shareSignInRef.current?.()
    // The literal is SHARE_SIGN_IN_EVENT (@papan/collab/ui) — pinned equal by a test.
    window.addEventListener('papan:share-sign-in-required', onSignIn)
    return () => window.removeEventListener('papan:share-sign-in-required', onSignIn)
  }, [])

  // A Save / Add clicked while the identity was pending, replayed through the CURRENT handler
  // (the one that reads the identity that landed) — never the stale closure that was clicked.
  const queuedActionRef = useRef<(() => void) | null>(null)
  const saveCopyRef = useRef<(bypassAuthGate?: boolean, expandPointers?: boolean) => Promise<void>>(async () => {})
  const addToDriveRef = useRef<(bypassAuthGate?: boolean) => Promise<void>>(async () => {})
  useEffect(() => {
    if (identityPending || !queuedActionRef.current) return
    const run = queuedActionRef.current
    queuedActionRef.current = null
    run()
  }, [identityPending])

  if (error) {
    // Decision 10 / Phase 38: authenticated no-match denial.
    // When an authenticated user's verified emails don't match the
    // link's recipient_grants AND no "*" wildcard is present, the
    // server rejects session creation. Chrome shows a clear denial
    // page rather than the generic "share isn't available" wording.
    const isNoAccess = error === 'verification_required' || error === 'recipient_not_listed'
    return (
      <div className={`${themeClass} themed-bg-primary h-screen flex flex-col items-center justify-center p-8`}>
        <LinkIcon size={32} className="themed-text-muted mb-3" />
        <h1 className="text-base font-semibold themed-text mb-2">
          {isNoAccess
            ? "You don't have access to this share"
            : error === 'session_unverifiable'
              ? "Couldn't check your access"
              : error === 'logged_out'
                ? "You've logged out of this share"
                : "This share isn't available"}
        </h1>
        <p className="text-sm themed-text-muted max-w-md text-center leading-relaxed">
          {isNoAccess
            ? "The owner shared this with specific people — your account's emails aren't on the list. Ask the owner to add your email, or sign in with the account it was shared with and re-open the link."
            : error === 'logged_out'
              ? 'Open the share link again to continue.'
              : error === 'session_invalid'
              ? 'The session expired or was revoked. Try re-opening the share link.'
              : error === 'share_unavailable'
                ? 'This share has been revoked or is no longer accessible.'
                : error === 'share_config_stale'
                  ? 'Your access to this share has changed. Re-open the share link to continue with the new permissions.'
                  : error === 'session_unverifiable'
                    // The server could not check this session just now (#601) — not a
                    // dead session, so no "re-open the link": a reload retries it.
                    ? "We couldn't check your access just now. Reload the page to try again."
                    : error}
        </p>
        {error === 'session_unverifiable' && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="themed-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap mt-4"
          >
            Reload
          </button>
        )}
        {/* B3/A4 (#187): the no-access copy used to say "sign out and verify a
            different email" with no control to do it (the bar's sign-out is
            gone). Account management lives in the main app — link to the
            top-level sign-in page so the recipient can sign in with the listed
            account, then re-open the share link. */}
        {isNoAccess && (
          <a
            href="/login"
            className="themed-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap mt-4"
          >
            Sign in with a different account
          </a>
        )}
      </div>
    )
  }

  if (!info) {
    return (
      <div className={`${themeClass} themed-bg-primary h-screen flex items-center justify-center`}>
        <Loader2 size={20} className="themed-text-muted animate-spin" />
      </div>
    )
  }

  // The share viewer's ONE sign-in action (the Sign in button, and #796's app re-entry): after
  // sign-in, a quick-view link routes to the server "Choose your identity" dialog (the SAME one
  // restricted links hit after verify) so the user picks which account — and, for their
  // Personal account, which email — to view + save with. Restricted links already chose at the
  // verify gate, so they just reload.
  //
  // Target the API origin (getApiBase) — under the clean-A split the Better Auth session cookie
  // is host-only on the `-api` host, so a relative nav to the frontend origin would send no
  // cookie and the server would see an anonymous caller (→ gate → wildcard mints anon → picker
  // skipped). Pass our own origin as `?app=` so the server's post-pick mint can hop back to the
  // frontend `/share-view` (the SPA route the `-api` host doesn't serve). getApiBase() is '' on
  // same-origin deploys → the URL stays relative, unchanged there.
  const chooseIdentityHref = `${getApiBase()}/share/${encodeURIComponent(info.share.token)}/choose-identity?app=${encodeURIComponent(window.location.origin)}`
  // The identity picker mints at the share ROOT, so stash WHERE the visitor is (an app open in
  // place, its PDApp mode + subfolder, or a sub-file) under a one-shot `pick` marker the picker
  // echoes back — the restore effect reopens it on the session THAT pick minted (#796: signing
  // in from an app's re-entry panel lands back in the app, not at the folder root). The same
  // mechanism bindAppIdentity's picker detour uses.
  const goChooseIdentity = () => {
    const marker = newPickMarker()
    stashPickContext(info.share.token, marker, bindLandingContext())
    leaveShareView(`${chooseIdentityHref}&pick=${marker}`, 'share-identity-switched')
  }
  const openShareSignIn = () => setSignInPrompt({
    title: `Sign in to ${productName}`,
    retry: isQuickView ? goChooseIdentity : () => window.location.reload(),
  })
  // Signed out → the prompt; signed in (an identity-less session) → the identity picker.
  // The resolver's answer when it has one; while it is still loading (null) or its fetch
  // failed (no `isAuthenticated`), the auth session decides — never "signed in" by default,
  // which sent an anonymous visitor to /choose-identity and back to an anonymous session.
  const visitorSignedIn = typeof resolver?.isAuthenticated === 'boolean' ? resolver.isAuthenticated : !!authUser
  shareSignInRef.current = visitorSignedIn ? goChooseIdentity : openShareSignIn

  // A re-shared cross-tenant mount whose source is no longer reachable (the
  // resharer lost their grant, or the chain is structurally dead). The /info
  // call still succeeds — the session and link are valid — but the content
  // behind the mount is gone, so render a clear "no longer available" state
  // rather than the empty explorer the folder path would otherwise show.
  if (info.share.crossTenantMountBroken) {
    return (
      <div className={`${themeClass} themed-bg-primary h-screen flex flex-col items-center justify-center p-8`}>
        <LinkIcon size={32} className="themed-text-muted mb-3" />
        <h1 className="text-base font-semibold themed-text mb-2">
          This shared content is no longer available
        </h1>
        <p className="text-sm themed-text-muted max-w-md text-center leading-relaxed">
          The person who shared this with you no longer has access to the original, so it can&rsquo;t be opened here. Ask them to share it again if you still need it.
        </p>
      </div>
    )
  }

  // Withhold the app iframe while the silent auto-bind (see the auto-bind
  // effect above) is in flight: mounting it against the still-unbound session
  // would let the app's first papan.whoami()/papan.run() resolve as
  // no-principal before the bind swaps in the identity-carrying session.
  //
  // Also hold while auth is STILL LOADING: `authUser` starts null (useAuth's
  // isLoading=true) and can stay null after /info resolves for a genuinely
  // signed-in recipient, so gating on `authUser` alone would mount the iframe
  // unbound during that window, then tear it down when auth lands — the exact
  // no-principal first paint this gate prevents. Once auth resolves: an
  // anonymous recipient (no personalTenantId) runs unbound by design, a
  // signed-in one holds until the bind lands (verifiedEmail) or fails
  // (bindFailed → run unbound rather than spin forever).
  const awaitingAppBind =
    viewingHtmlApp && !sessionBound && !bindFailed &&
    (authLoading || !!authUser?.personalTenantId)

  // (parity for FILES) Withhold the editor while a signed-in native member's
  // silent FILE auto-bind (the file auto-bind effect above) is in flight —
  // otherwise the editor first mounts against the UNBOUND session (read-only),
  // then the bind re-mints a verifiedEmail-carrying sub-session and REMOUNTS it
  // editable: a visible read-only→edit flip on every ppn.gg open. Hold ONLY
  // while a bind can still happen — signed-in, unbound, a plain file (an app is
  // handled above; a folder has no room) — and EITHER auth is still resolving OR
  // the resolver hasn't yet ruled on native access / has confirmed it (so a bind
  // WILL fire). A link-only viewer (resolver loaded, not accessible), an
  // anonymous recipient (no personalTenantId), or a failed bind (bindFailed)
  // falls through to the read-only editor instead of spinning. Mirrors
  // awaitingAppBind; the file auto-bind's fire condition is the resolver gate.
  const awaitingFileBind =
    !viewingHtmlApp && !info.file.isFolder &&
    !sessionBound && !bindFailed &&
    (
      authLoading ||
      (!!authUser?.personalTenantId &&
        (resolver === null || resolver.accessibleInActiveTenant === true))
    )

  const showOpenInDrive = resolver?.accessibleInActiveTenant === true && !!resolver.driveUrl
  /**
   * Hide the two controls that write into the recipient's OWN drive (§7.2
   * item 4). Download is untouched — it is not a write — and "Open in my Drive"
   * needs no branch: C5's resolver already excludes an identity-only tenant
   * from the candidates, so `accessibleInActiveTenant` cannot be true for one.
   *
   * The flag is read once here rather than at each button, so the phone sheet
   * and the desktop row cannot disagree about which affordances exist.
   */
  const canSaveToOwnDrive = resolver?.activeTenantIdentityOnly !== true
  // Share-serve content loads from the sandboxed preview origin (#235) —
  // off the Pages Function, cross-origin to the SPA, sid-in-path is the auth.
  // Serve URLs resolve `<name>` against the session's serveRoot, so they must be
  // built from the ACTIVE session + ITS file — for a file opened in place inside
  // a folder share that's the sub-session, not the route's folder session.
  const viewerFile = activeInfo?.file ?? info.file
  // Download follows what's in view (#757 full review #3): on a PDApp ROOT's own
  // face (the app tab / Settings) in the folder session, that is the app, not
  // the index.html opened to run it — so it targets the route's folder (the
  // `.pdapp` itself) through the route's session, exactly what the PDApp root
  // offered before PDApp mode opened the entry on landing. A PDApp NESTED in a
  // shared folder has no session scoped to the app folder to serve it from (the
  // route folder is the share ROOT), so it keeps the open file's download rather
  // than silently widening it to the whole share.
  const downloadOfFolder = onPdappFace && pdappIsRoot && info.file.isFolder
  const downloadFile = downloadOfFolder ? info.file : viewerFile
  // A FOLDER in view downloads as the session's zip (the serve route serves files only —
  // it answered a folder with a 404). Download is VIEW access (owner ruling 2026-09-27);
  // the zip holds exactly what /list shows, pointers by the share-view rule. The desktop
  // button asks first when a pointer the visitor may keep is inside (handleDownloadFolderZip).
  // Zippable = a NATIVE folder of the link's own tree: never a compound id inside a mount,
  // a pointer root, or an app link (the route refuses each — no error body saved as a .zip).
  const shareInfo = info.share as { rootIsMount?: boolean; kind?: string }
  const zippableFolder = downloadFile.isFolder && !!bodySid && !downloadFile.id.startsWith('s_')
    && !(shareInfo.rootIsMount && downloadFile.id === info.share.rootFileId) && shareInfo.kind !== 'app'
  const folderZipUrl = zippableFolder
    ? `${getApiBase()}/api/share-links/session/${bodySid}/download-folder?folderId=${encodeURIComponent(downloadFile.id)}`
    : null
  // A folder that cannot be zipped has no working download (the serve route serves files).
  const hideDownload = downloadFile.isFolder && !folderZipUrl
  const downloadUrl = folderZipUrl
    // The phone sheet has no dialog — it takes the share view's default, contents.
    ? `${folderZipUrl}&expandPointers=1`
    : downloadOfFolder
      ? (serveBody ? shareServeUrl(serveBody, encodeURIComponent(info.file.name), 'download=1') : '')
      : (serveActive ? shareServeUrl(serveActive, encodeURIComponent(viewerFile.name), 'download=1') : '')
  const startFolderZip = (expandPointers: boolean) => {
    if (!folderZipUrl) return
    const a = document.createElement('a')
    a.href = `${folderZipUrl}${expandPointers ? '&expandPointers=1' : ''}`
    a.download = `${downloadFile.name}.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
  const handleDownloadFolderZip = async (e: React.MouseEvent) => {
    if (!folderZipUrl) return
    e.preventDefault()
    try {
      const r = await sessionFetch(`${folderZipUrl}&anyPointers=1`, { credentials: 'include' })
      if (!r.ok) {
        // Refused (scope / access): say so — never save the error body as a .zip.
        setSaveState('error')
        setSaveMessage("This folder can't be downloaded.")
        return
      }
      const b = (await r.json()) as { anyPointers?: boolean }
      if (b?.anyPointers) { setZipPointerAsk(true); return }
    } catch (err) {
      // #1316: the session it was asked for ended — nothing starts for the next one
      if (isSessionEndedError(err)) return
      /* else no pre-flight answer — download with the default (contents) */
    }
    startFolderZip(true)
  }
  // '' until the serve token is known — the body waits for it (below).
  const inlineUrl = serveActive ? shareServeUrl(serveActive, encodeURIComponent(viewerFile.name)) : ''

  // ── PDApp controls (#757) ─────────────────────────────────────────────────
  const editParams = (mut: (next: URLSearchParams) => void, replace = false) =>
    setSearchParams((prev) => { const next = new URLSearchParams(prev); mut(next); return next }, { replace })

  // Run — the APP TAB (`?view=run`): the app's index.html through the share
  // session's EXISTING app path — a sub-session scoped to the `.pdapp` folder, so
  // the preview server runs it Mode B under the app principal, and the chrome's
  // silent bind-account supplies the recipient identity (consent is per-email,
  // server-side) — rendered full-bleed by SharePdappRunView instead of in the
  // HTML editor. No new authority: the same session the share viewer already
  // minted when a recipient opened the app's index.html in a shared folder.
  const runPdapp = () => {
    const entryId = pdappEntryId
    if (!pdappFolderId || !entryId) return
    const onEntry = info.file.isFolder ? openFileId === entryId : info.file.id === entryId
    if (onEntry) {
      // Already on the app tab: pressing Run again re-runs it (remount — the
      // reset a refresh gives). From Settings / the entry's editor: just switch
      // to the app tab — the entry stays open, no re-mint.
      if (pdappFace === 'run') { setRunNonce((n) => n + 1); return }
      editParams((next) => next.set(VIEW_PARAM, 'run'))
      return
    }
    if (info.file.isFolder) {
      editParams((next) => {
        next.set('file', entryId)
        next.set(VIEW_PARAM, 'run')
      })
      return
    }
    // A DIRECT sub-file session (a deep link to a file inside a PDApp-root
    // share): switch the route to the entry's own sub-session, as the explorer
    // sidebar does on that path.
    void (async () => {
      try {
        const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/sub-session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileId: entryId }),
        })
        if (!res.ok) { console.warn('[share-pdapp] run: sub-session mint failed', res.status); return }
        const b = (await res.json()) as { sessionId?: string }
        if (b.sessionId) goToShareSession(navigate, `/share-view/${b.sessionId}?${VIEW_PARAM}=run`, { ends: false })  // a sub-session: the same identity
      } catch (err) { console.warn('[share-pdapp] run: network error', err) }
    })()
  }

  const pdappName = (pdappIsRoot ? info.share.rootName : null) ??
    (pdappLookup?.status === 'ready' ? pdappLookup.name : null)
  const pdappMode: SharePdappMode | null = pdappFolderId ? {
    folderId: pdappFolderId,
    name: pdappName,
    isRoot: pdappIsRoot,
    entryReady: pdappEntryReady,
    entryId: pdappEntryId,
    entryError: pdappEntryError,
    onRetryEntry: retryPdappEntry,
    face: pdappFace,
    viewerFace: pdappViewerFace,
    onRun: runPdapp,
    // The SETTINGS TAB, in place (`?view=settings`) — MiniAppEditor's settings
    // face. The open file (the running app) stays mounted underneath, so going
    // back to Run doesn't restart it. Only when the gate resolved native write.
    // It remembers what it was opened over (the app tab, or a file's editor) so
    // the viewer beneath keeps it and closing Settings goes back to it.
    onSettings: pdappManageable
      ? () => {
          if (pdappFace === 'settings') return
          setSettingsOver(pdappFace === 'run' ? 'run' : 'editor')
          editParams((next) => next.set(VIEW_PARAM, 'settings'))
        }
      : null,
    // The identity Settings acts as — the same one its gate asked under: the
    // chrome's EFFECTIVE identity (the stored / `?tenant=` pair — see above). The
    // tab NAMES the identity it manages as (#757 full review #5).
    settingsIdentity: {
      tenantId: effectiveTenant ?? null,
      orgEmail: effectiveEmail ?? null,
      tenantName: tenants.find((t) => t.id === effectiveTenant)?.name ?? null,
    },
    // A nested PDApp can be left back to the listing it was opened from; a
    // PDApp root has nothing behind it.
    onExit: pdappIsRoot ? null : () => editParams((next) => {
      next.delete(PDAPP_PARAM)
      next.delete('file')
      next.delete(VIEW_PARAM)
    }),
  } : null
  // Leave the Settings tab: back to the face it was opened over — the app tab
  // when the running app is what's beneath it, else whatever is (index.html's
  // editor, another file, or the PDApp empty state).
  const closePdappSettings = () => editParams((next) => {
    const entryOpen = info.file.isFolder ? next.get('file') === pdappEntryId : info.file.id === pdappEntryId
    if (entryOpen && pdappEntryId && settingsOver === 'run') next.set(VIEW_PARAM, 'run')
    else next.delete(VIEW_PARAM)
  })
  // Open a nested PDApp from the listing / tree: PUSH (Back returns to the
  // listing); the auto-open above then lands it on the running app.
  const openPdapp = (folderId: string) => editParams((next) => {
    next.set(PDAPP_PARAM, folderId)
    next.delete('file')
    next.delete(VIEW_PARAM)
  })

  const handleSaveCopy = async (bypassAuthGate = false, expandPointers?: boolean) => {
    // The acting identity is still loading: never act as another — run it once it lands.
    if (identityPending) { queuedActionRef.current = () => { void saveCopyRef.current(bypassAuthGate, expandPointers) }; return }
    // Anon recipients can't save — prompt sign-in inline instead of
    // a dead-end error toast. The modal closes on success and re-fires
    // this handler with bypassAuthGate=true so the stale resolver
    // (still showing isAuthenticated:false from before sign-in)
    // doesn't re-open the modal in a render-batch race.
    if (!bypassAuthGate && resolver && !resolver.isAuthenticated) {
      setSignInPrompt({ title: 'Sign in to save a copy', retry: () => { void handleSaveCopy(true) } })
      return
    }
    // #1316 (the retry rule): the session this save starts in. Once it ends — the visitor left for another share
    // session (the chrome is not remounted on that) or signed out — the flow sends nothing more (no save, no further
    // status poll: it would go out as the NEXT session) and shows nothing; only the busy state is released.
    const start = currentSessionSignal()
    // A pure check — it writes nothing: by then the listener below has released the busy state, and a save started
    // since (as the next session) must not be reset by this one's late end.
    const ended = (err?: unknown) => sessionEndedSince(start, err)
    // The busy state goes WITH the session, at once — not when the held request or the next tick settles.
    const onSessionEnd = () => { if (mountedRef.current) setSaveState((s) => (s === 'busy' ? 'idle' : s)) }
    start?.addEventListener('abort', onSessionEnd, { once: true })
    const saveInSession = async () => {
      // A CROSS-TENANT save of a folder holding a pointer this visitor may keep (Edit through
      // the link, or native) asks first — the zip's dialog, contents preselected. The server
      // decides (it knows the destination tenant): a save into the share's own tenant keeps
      // pointers and never asks; a view-only visitor always gets contents.
      if (expandPointers === undefined && info.share.rootIsFolder) {
        try {
          const preHeaders: Record<string, string> = { 'Content-Type': 'application/json' }
          if (effectiveTenant) preHeaders['x-tenant-id'] = effectiveTenant
          if (effectiveEmail) preHeaders['x-org-email'] = effectiveEmail
          const pre = await sessionFetch(`${getApiBase()}/api/share-links/${encodeURIComponent(info.share.token)}/save`, {
            method: 'POST', credentials: 'include', headers: preHeaders,
            body: JSON.stringify({ ...(sessionId ? { sessionId } : {}), preflight: true }),
          })
          const body = pre.ok ? (await pre.json()) as { anyPointers?: boolean } : null
          if (body?.anyPointers) { setSavePointerAsk(true); return }
        } catch (err) {
          if (ended(err)) return
          /* else no pre-flight answer — save with the server's default */
        }
      }
      setSaveState('busy')
      setSaveMessage(null)
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (effectiveTenant) headers['x-tenant-id'] = effectiveTenant
        if (effectiveEmail) headers['x-org-email'] = effectiveEmail
        const token = encodeURIComponent(info.share.token)
        const res = await sessionFetch(`${getApiBase()}/api/share-links/${token}/save`, {
          method: 'POST',
          credentials: 'include',
          headers,
          // The share session the save is made from: its verified email + STORED identity
          // (acting tenant + email) are what the server's per-node gate resolves.
          // `expandPointers`: the visitor's answer (absent ⇒ the server's default, contents).
          body: JSON.stringify({ ...(sessionId ? { sessionId } : {}), ...(expandPointers === undefined ? {} : { expandPointers }) }),
        })
        const body = await res.json().catch(() => ({}))
        if (ended()) return
        if (!res.ok) {
          if (res.status === 401) {
            // Resolver said authed but the request still 401'd — token
            // raced an expiry. Fall back to the modal.
            setSignInPrompt({ title: 'Sign in to save a copy', retry: () => { void handleSaveCopy(true) } })
            setSaveState('idle')
            return
          }
          setSaveState('error')
          setSaveMessage(body.detail || body.error || `Save failed (${res.status})`)
          return
        }
        // The save now runs in a background worker (the saver may be an outsider
        // with no realtime channel), so poll for the terminal result. If the
        // response carried the result inline (no jobId), accept it directly.
        if (!body.jobId) {
          setSaveState('success')
          setSaveMessage('Copied to your Drive.')
          return
        }
        const deadline = Date.now() + 10 * 60 * 1000
        let delay = 300
        for (;;) {
          await new Promise(r => setTimeout(r, delay))
          if (!mountedRef.current) return  // U1: visitor left — stop polling, no setState
          if (ended()) return              // #1316: the session ended while waiting — no poll as the next one
          delay = Math.min(delay * 1.5, 2000)

          // U1: bound each poll with an abort timeout so a stalled CF-edge fetch
          // can't hang the loop indefinitely (raw fetch has no timeout). A blip on
          // one tick just continues until the deadline.
          const ctrl = new AbortController()
          const abortT = setTimeout(() => ctrl.abort(), 15_000)
          let sres: Response
          try {
            sres = await sessionFetch(`${getApiBase()}/api/share-links/${token}/save-status/${encodeURIComponent(body.jobId)}`, {
              credentials: 'include',
              headers,
              signal: ctrl.signal,
            })
          } catch (err) {
            if (ended(err)) return         // #1316: a dropped poll is never retried
            if (Date.now() > deadline) {
              if (mountedRef.current) {
                setSaveState('error')
                setSaveMessage('Save is taking longer than expected — it will keep running. Check your Drive shortly.')
              }
              return
            }
            continue  // transient network/abort blip — keep polling
          } finally {
            clearTimeout(abortT)
          }
          if (!mountedRef.current || ended()) return

          if (sres.status === 404) {
            // Job completed and was evicted (removeOnComplete) before this poll.
            // Safe as success: FAILED jobs are retained by age (save-queue
            // removeOnFail:{age}), so a real failure is always readable as 'failed'
            // here, never a 404 (F30).
            setSaveState('success')
            setSaveMessage('Copied to your Drive.')
            return
          }
          // F4.3: a 401/403 on a poll tick means we can no longer verify access to
          // the result — an expired session, a revoked org membership, or an
          // ambiguous identity. Don't treat it as "still running" and spin to the
          // deadline; surface a cause-agnostic message (the save itself keeps
          // running in the background regardless).
          if (sres.status === 401 || sres.status === 403) {
            setSaveState('error')
            setSaveMessage("Couldn't verify access to check the result — reopen the share link, or check your Drive shortly.")
            return
          }
          const sbody = sres.ok ? await sres.json().catch(() => ({})) : {}
          if (!mountedRef.current || ended()) return  // F4.2: unmount can race the body read (#1316: so can a session end)
          if (sbody.state === 'completed') {
            setSaveState('success')
            setSaveMessage(saveCompletedMessage(sbody))
            return
          }
          if (sbody.state === 'failed') {
            // U1/U3: map the worker's error code to a clear message (instead of
            // showing the raw code or a misleading "still running").
            setSaveState('error')
            setSaveMessage(saveStatusErrorMessage(sbody.error))
            return
          }
          if (Date.now() > deadline) {
            setSaveState('error')
            setSaveMessage('Save is taking longer than expected — it will keep running. Check your Drive shortly.')
            return
          }
        }
      } catch (err) {
        if (ended(err)) return
        setSaveState('error')
        setSaveMessage(errorText(err, 'Network error') ?? '')   // `ended` above took a dropped answer
      }
    }
    try { await saveInSession() } finally { start?.removeEventListener('abort', onSessionEnd) }
  }

  const handleAddToDrive = async (bypassAuthGate = false) => {
    // The acting identity is still loading: never act as another — run it once it lands.
    if (identityPending) { queuedActionRef.current = () => { void addToDriveRef.current(bypassAuthGate) }; return }
    // (0119) Owner disallowed Add-to-Drive on this link. The buttons render
    // disabled, but secondary entry points (folder dialog CTAs) funnel here
    // too — surface the refusal instead of firing a doomed request.
    if (info.share.forbidAddToDrive) {
      setAddState('error')
      setAddMessage('The owner has not allowed adding this share to your Drive.')
      return
    }
    if (!bypassAuthGate && resolver && !resolver.isAuthenticated) {
      setSignInPrompt({ title: 'Sign in to add to your Drive', retry: () => { void handleAddToDrive(true) } })
      return
    }
    setAddState('busy')
    setAddMessage(null)
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (effectiveTenant) headers['x-tenant-id'] = effectiveTenant
      if (effectiveEmail) headers['x-org-email'] = effectiveEmail
      const res = await sessionFetch(`${getApiBase()}/api/share-links/${encodeURIComponent(info.share.token)}/mount`, {
        method: 'POST',
        credentials: 'include',
        headers,
        // The share session the mount is made FROM (as Save does): the server takes the
        // identity from it — the email chosen for the link (#794), judged for a block (#795).
        body: JSON.stringify(sessionId ? { sessionId } : {}),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        if (res.status === 401) {
          setSignInPrompt({ title: 'Sign in to add to your Drive', retry: () => { void handleAddToDrive(true) } })
          setAddState('idle')
          return
        }
        if (res.status === 404 && body.error === 'mount_endpoint_pending') {
          setAddState('error')
          setAddMessage('Endpoint not yet wired (Phase 13).')
          return
        }
        setAddState('error')
        setAddMessage(body.detail || body.error || `Couldn't start the dance (${res.status})`)
        return
      }
      setAddState('success')
      // Surface the outcome in a focused modal rather than a thin banner the
      // recipient is likely to miss — they need to understand an approval is
      // pending (or that it landed instantly).
      setMountResult(body.contactNoAccess === 'all' ? 'no_access' : body.state === 'established' ? 'established' : 'pending')
    } catch (err) {
      // #1316: the answer landed after its session ended — nothing shows; the button is usable again
      const text = errorText(err, 'Network error')
      if (text === null) { setAddState('idle'); return }
      setAddState('error')
      setAddMessage(text)
    }
  }

  // "Log out" from the share view. Per the chosen model (drop the share session
  // too): first revoke THIS share session server-side so a restricted view stops
  // resolving immediately, then end the Better Auth account session, then send
  // the recipient back to the share's front door — the verify gate (`/share/
  // <token>`) for a restricted link, or an instant re-mint for a wildcard link.
  // Order matters: revoke before the redirect so the next page load can't reuse
  // the now-dead session token.
  saveCopyRef.current = handleSaveCopy
  addToDriveRef.current = handleAddToDrive

  const handleSignOut = async () => {
    // E7 PR 0b: the visit ends — a restore waiting on "still verifying" sends nothing more
    endShareVisit('share-signed-out')
    // sent AFTER the visit ended (endShareVisit above) — it is what ends it server-side; best-effort, the
    // Better Auth sign-out + redirect still run
    if (sessionId) await revokeShareVisit(sessionId)
    try {
      // raw (#1316): the sign-out itself, after the visit ended
      await fetch(`${getApiBase()}/api/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
    } catch { /* best-effort */ }
    // The account is signed out in this browser: its drive tabs re-check now (#961).
    announceSignedOut()
    // Swap the view for the "logged out" screen BEFORE leaving: the browser keeps this page in
    // its back-forward cache as it is at navigation time, so Back must restore that screen —
    // never the folder the visitor just logged out of.
    flushSync(() => setError('logged_out'))
    leaveShareView(`/share/${encodeURIComponent(info.share.token)}`, 'share-signed-out')
  }

  return (
    <div className={`${themeClass} themed-bg-primary themed-text h-screen flex flex-col`}>
      {identitySwitchError && (
        <div role="alert" data-testid="identity-switch-error" className="px-4 py-2 text-xs themed-text bg-amber-500/15 flex items-center gap-2">
          <span className="flex-1">{identitySwitchErrorText(identitySwitchError)}</span>
          <button type="button" className="themed-text-muted" onClick={() => editParams((next) => next.delete('identity_error'))}>Dismiss</button>
        </div>
      )}
      {/* Topbar — Papan-styled "minimal in components" chrome. Holds
          file metadata (name, owner, verified-email pill) AND the
          share-link action buttons. Same single bar regardless of
          which editor renders below — the chrome owns share-action UI
          unconditionally; editors stay focused on rendering content.

          Brand-mark sits at the far left. Clickable for authenticated
          recipients (jumps to their own drive); non-interactive icon
          for anonymous recipients (resolver returns isAuthenticated=false,
          which on the same tick fires when the chrome first mounts —
          tiny render delay before the link wraps in for authed users,
          imperceptible in practice). */}
      {/* `ws-clear` + no bottom rule — the taskbar shape, not a titlebar's.
          It used to paint `themed-bg-secondary` with a `border-b` on top of the
          root's `themed-bg-primary`, which reads as an opaque bar clamped to the
          page. Now that the panels below are floating cards in a gutter, the bar
          has to be part of the SAME ambient background they float on, or the
          cards look like they're hanging off a chrome strip. Mirrors the app
          shell, where the taskbar is ws-clear over the ambient stage. */}
      <div className={`flex items-center gap-2 ws-clear shrink-0 ${isPhone ? 'px-2 py-2' : 'px-4 py-2.5'}`}>
        <a
          href={resolver?.isAuthenticated && resolver.userActiveTenantHref
            ? resolver.userActiveTenantHref
            : '/login'}
          title={resolver?.isAuthenticated ? 'Open my Drive' : `Sign in to ${productName}`}
          className="shrink-0 inline-flex items-center justify-center hover:opacity-80 transition-opacity"
        >
          {/* decorative: the anchor's own title already names the product. */}
          <BrandLogo decorative className="w-5 h-5" />
        </a>
        {/* Title path. For folder shares, render the root as a clickable
            "back to root" crumb followed by a chevron + the currently-
            opened file/folder name. The breadcrumb component inside
            SharedFolderView is dropped — the title bar carries the
            location signal now. For single-file shares (no folder
            root), just the file's own name. */}
        {info.share.rootIsFolder && info.share.rootName ? (
          <>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams)
                next.delete('parentId')
                // …and close any file open over the listing, or the crumb would
                // "go back to the folder" while the viewer stayed on top of it.
                next.delete('file')
                // …and leave a nested PDApp's mode (#757) — it overlays the
                // listing just like an open file does.
                next.delete(PDAPP_PARAM)
                next.delete(VIEW_PARAM)
                setSearchParams(next, { replace: false })
              }}
              className="inline-flex items-center gap-2 px-1.5 py-0.5 -mx-1.5 rounded cursor-pointer hover:bg-white/5 transition-colors"
              title="Back to shared folder"
            >
              <FileIcon name={info.share.rootName} isFolder={true} size={14} miniappIconName={info.share.rootName} miniappIconSpec={info.share.rootIcon ?? null} {...(info.share.rootIsMount ? { fileType: info.share.rootFileType ?? null } : {})} />
              <span className="text-sm font-medium themed-text truncate max-w-[20rem]">
                {info.share.rootName}
              </span>
            </button>
            {/* The file crumb tracks the ACTIVE file — the one opened in place
                over the listing (`viewerFile`), or the route session's own file
                when the recipient deep-linked straight to a sub-file. Without
                this the bar would keep showing the bare folder name while the
                viewer (and Download) were on a file.

                Suppressed whenever a file is open IN PLACE (either viewport): the
                overlay's own row right below already carries the name, the unsaved
                dot and the Save/Discard controls, so repeating the name here was
                printing it twice on one screen — and on a phone it squeezed both
                crumbs down to an initial ("Q… › R…"). The bar keeps the FOLDER
                crumb, which is the one thing the overlay row doesn't show.

                Still rendered for a DIRECT sub-file session (no overlay, no second
                row) — there the bar is the only place the file name can appear. */}
            {viewerFile.id !== info.share.rootFileId && !openFile && (
              <>
                <ChevronRight size={12} className="themed-text-muted shrink-0" />
                {contentDirty && <DirtyDot />}
                <FileIcon name={viewerFile.name} isFolder={viewerFile.isFolder} size={14} />
                <span className="text-sm font-medium themed-text truncate max-w-[20rem]">
                  {viewerFile.name}
                </span>
              </>
            )}
          </>
        ) : (
          <>
            {contentDirty && <DirtyDot />}
            <FileIcon name={info.file.name} isFolder={info.file.isFolder} size={14} />
            <span className="text-sm font-medium themed-text truncate max-w-[28rem]">
              {info.file.name}
            </span>
          </>
        )}
        {/* Save / Discard — ONLY for a direct sub-file session, which renders a
            bare viewer with no header of its own, so this bar is the only surface
            that can carry them (a single-file edit share, or a deep link straight
            to a file; without this that path has Ctrl+S and no way to revert).

            When a file is open IN PLACE the overlay HAS a row of its own — its ✕
            and file name — and that is where these belong, next to the dot, on
            both viewports. Putting them up here as well meant the header carried
            controls for a file whose name it was already printing twice. */}
        {!openFile && !isPhone && canSaveShown && (
          <EditControls
            dirty={contentDirty}
            coverMessage={editorCover?.message ?? null}
            onSave={requestSave}
            onDiscard={() => { void handleDiscard() }}
          />
        )}
        {info.ownerTenantName && (
          // Owner pill — matches the source-org pill DriveView uses
          // on `.shared` / `.chain` rows in the chain-primitive branch:
          // green background, tenant NAME as the pill content (not
          // "SHARED"), "Shared by …" in the tooltip. Recipients see
          // the same affordance on their own drive once a mount lands,
          // so the recognition carries across surfaces.
          <span
            // Attribution is a trust signal, so it stays on phones — but as a
            // truncating pill, not a nowrap one that shoves the file name off
            // the row.
            className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${
              isPhone ? 'max-w-[5rem] truncate' : 'shrink-0'
            }`}
            style={{ backgroundColor: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }}
            title={`Shared by ${info.ownerTenantName}`}
          >
            {info.ownerTenantName}
          </span>
        )}
        <span className="flex-1" />
        {/* Account affordances — native, top-level (un-frameable) chrome (#187).
            Sign-in (anon), tenant switch (multi-tenant recipients), and email
            narrowing live HERE, not in the frameable embed bar (which is now
            display-only). Sign-out is intentionally dropped from the share view
            — recipients manage their account in the main app. */}
        {resolver && !resolver.isAuthenticated && (
          <button
            type="button"
            onClick={openShareSignIn}
            className="themed-btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
          >
            Sign in
          </button>
        )}
        {/* Theme toggle — the SAME control the app shell's top bar carries, not a
            share-view lookalike. Recipients read shared docs for as long as they read
            their own, and a share-view stuck on someone else's theme is just a page you
            squint at.

            It works for anonymous recipients too: settings live server-side, so their
            PUT 401s — useSettings mirrors the theme to localStorage precisely so the
            choice survives a reload here (see writeLocalTheme). Shown to everyone, since
            the people most likely to land on a share link are the ones with no account. */}
        <ThemeToggle area="workspace" />
        {resolver?.isAuthenticated && (
          <AccountPicker
            userName={authUser?.name ?? ''}
            userEmail={authUser?.email ?? ''}
            tenants={pickerTenants}
            activeTenant={pickerActiveTenant}
            activeEmail={pickerActiveEmail}
            personalEmail={personalChosenEmail}
            bestTenant={bestTenant}
            bestEmail={bestEmail}
            onSignOut={handleSignOut}
            onPickIdentity={viewingHtmlApp ? (tid, email) => { void bindAppIdentity(tid, email) } : switchIdentity}
          />
        )}
        {/* Decision 7 / Phase 12 — three recipient affordances:
            • Save a copy: fork (existing /save endpoint, no dance)
            • Add to my Drive: recipient-initiated dance (Phase 13)
            • Open in my Drive: if the file is already in the recipient's
              active tenant (resolver said yes), skip the other two.

            On a phone these three `whitespace-nowrap` buttons can't share a
            375px row with the brand + crumb + owner pill — they used to shove
            the title off-screen. Collapse them into a ⋯ sheet there; the account
            picker stays inline (it's the identity signal, worth the space). */}
        {isPhone ? (
          <ShareActionsMenu
            showOpenInDrive={showOpenInDrive}
            driveUrl={resolver?.driveUrl}
            downloadUrl={hideDownload ? '' : downloadUrl}
            downloadName={downloadFile.name}
            saveState={saveState}
            addState={addState}
            canSaveToOwnDrive={canSaveToOwnDrive}
            forbidAddToDrive={info.share.forbidAddToDrive}
            onSaveCopy={() => { void handleSaveCopy() }}
            onAddToDrive={() => { void handleAddToDrive() }}
            // Only when there's no overlay to carry them — a folder share's file
            // row already has Save + Discard, and duplicating them into the sheet
            // would give a phone two places to save the same file.
            canSave={!openFile && canSaveShown}
            dirty={contentDirty}
            saveCovered={saveCovered}
            onSaveFile={requestSave}
            onDiscardFile={() => { void handleDiscard() }}
          />
        ) : (
          <>
            {showOpenInDrive ? (
              <a
                href={resolver!.driveUrl}
                className="themed-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
              >
                Open in my Drive
                <ExternalLink size={12} />
              </a>
            ) : canSaveToOwnDrive ? (
              <>
                <button
                  type="button"
                  disabled={saveState === 'busy' || saveState === 'success'}
                  onClick={() => { void handleSaveCopy() }}
                  title="Copy the file to your own Drive. Owner edits won't reach the copy."
                  className="themed-btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saveState === 'busy' && <Loader2 size={12} className="animate-spin" />}
                  {saveState === 'busy' ? 'Saving…' : saveState === 'success' ? 'Saved' : 'Save a copy'}
                </button>
                <button
                  type="button"
                  disabled={info.share.forbidAddToDrive || addState === 'busy' || addState === 'success'}
                  onClick={() => { void handleAddToDrive() }}
                  title={info.share.forbidAddToDrive
                    ? 'The owner has not allowed adding this share to your Drive.'
                    : "Ask the owner to mount this in your Drive. You'll see live edits."}
                  className="themed-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {addState === 'busy'
                    ? <Loader2 size={12} className="animate-spin" />
                    : <HardDriveDownload size={12} />}
                  {addState === 'busy' ? 'Requesting…' : addState === 'success' ? 'Requested' : 'Add to my Drive'}
                </button>
              </>
            ) : null}
            {!hideDownload && <a
              href={downloadUrl}
              download={folderZipUrl ? `${downloadFile.name}.zip` : downloadFile.name}
              onClick={folderZipUrl ? (e) => { void handleDownloadFolderZip(e) } : undefined}
              className="themed-btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
            >
              <Download size={12} />
              Download
            </a>}
          </>
        )}
      </div>

      {(saveMessage || addMessage) && (
        // ws-clear, like the header above it. It used to paint themed-bg-secondary
        // with a border-b, which was fine when the header did too — but once the
        // header cleared, a "Save a copy" success made this the ONLY opaque
        // bordered band on the page, sitting between a transparent header and the
        // floating cards. Exactly the chrome-strip look the header change removed.
        <div className="flex gap-3 px-4 py-1.5 ws-clear text-xs">
          {saveMessage && (
            <span className={saveState === 'error' ? 'text-red-400' : 'text-emerald-400'}>
              {saveMessage}
            </span>
          )}
          {addMessage && (
            <span className={addState === 'error' ? 'text-red-400' : 'text-emerald-400'}>
              {addMessage}
            </span>
          )}
        </div>
      )}

      {/* The banner follows the FILE on screen (its own sub-session may need the disclosure
          when the root does not — another source org, or edit only on that file), else the root. */}
      {openFile?.info.attributionDisclosure ? (
        <AttributionEditBanner sessionId={openFile.sid} disclosure={openFile.info.attributionDisclosure} />
      ) : info.attributionDisclosure && sessionId ? (
        <AttributionEditBanner sessionId={sessionId} disclosure={info.attributionDisclosure} />
      ) : null}
      <AttributionDisclosureHost themeClass={themeClass} />

      <div className="flex-1 min-h-0 overflow-auto themed-bg-primary flex flex-col">
        {awaitingAppBind || awaitingFileBind || !inlineUrl ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 size={20} className="themed-text-muted animate-spin" />
          </div>
        ) : (
          // #930 / I1 — collects the open editor's own cover (see editorCover). A provider of its own here because
          // the share view mounts no editor host (TabContent / SingleFileView): without one a report goes nowhere.
          <EditorCoverProvider onChange={setEditorCover} covered={saveCovered}>
          <ShareBody
            sessionId={bodySid}
            file={info.file}
            share={info.share}
            shareContext={shareContextOf(info, bodySid)}
            inlineUrl={inlineUrl}
            themeClass={themeClass}
            onAddToDrive={handleAddToDrive}
            addState={addState}
            addMessage={addMessage}
            onDirtyChange={handleDirtyChange}
            // The chrome owns the dirty flag (the top bar's DirtyDot reads it),
            // but on a phone the top-bar file crumb is suppressed — so the
            // overlay needs it to show its own dot + Save. Same single source.
            contentDirty={contentDirty}
            canSave={canSaveShown}
            saveCoverMessage={editorCover?.message ?? null}
            saveRef={editSaveRef}
            onRequestSave={requestSave}
            discardRef={editDiscardRef}
            onDiscard={() => { void handleDiscard() }}
            appRunFolderId={appRunFolderId}
            openFile={openFile}
            openFileError={openFileError}
            onOpenFile={openFileInPlace}
            onOpenFileWithSession={openFileWithSession}
            onCloseFile={closeFile}
            onFileRenamed={handleFileRenamed}
            openFileRequested={!!openFileId || pdappLandingPending}
            pdapp={pdappMode}
            onOpenPdapp={openPdapp}
            onClosePdappSettings={closePdappSettings}
            runNonce={runNonce}
          />
          </EditorCoverProvider>
        )}
      </div>

      {mountResult && (
        <MountRequestedDialog
          outcome={mountResult}
          themeClass={themeClass}
          rootName={info.share.rootName}
          ownerName={info.ownerTenantName}
          driveUrl={resolver?.driveUrl}
          onClose={() => setMountResult(null)}
        />
      )}

      {zipPointerAsk && (
        <FolderLinkedDownloadDialog
          folderName={downloadFile.name}
          themeClass={themeClass}
          t={tFiles}
          defaultChoice="contents"
          onCancel={() => setZipPointerAsk(false)}
          onConfirm={(choice) => { setZipPointerAsk(false); startFolderZip(choice === 'contents') }}
        />
      )}
      {savePointerAsk && (
        <FolderLinkedDownloadDialog
          folderName={info.share.rootName ?? ''}
          themeClass={themeClass}
          t={tFiles}
          action="copy"
          defaultChoice="contents"
          onCancel={() => setSavePointerAsk(false)}
          onConfirm={(choice) => { setSavePointerAsk(false); void handleSaveCopy(true, choice === 'contents') }}
        />
      )}
      {signInPrompt && (
        <AuthPromptModal
          signInTitle={signInPrompt.title}
          themeClass={themeClass}
          onClose={() => setSignInPrompt(null)}
          onSuccess={() => {
            const retry = signInPrompt.retry
            setSignInPrompt(null)
            // Re-trigger the resolver so the chrome flips out of the
            // anon-CTA shape (Open in Drive / brand-mark href / etc.)
            // before the retry posts.
            setResolver(null)
            retry()
          }}
        />
      )}

    </div>
  )
}

/**
 * Per-file-type dispatcher. The pivot point for Phase 15b's
 * single-file editor model:
 *
 *   • Refactored types (currently .md / .markdown) mount the canonical
 *     editor from features/collab/ui, wrapped in FileIOProvider so
 *     `filesApi.getFileContent` calls inside the editor route through
 *     the share session's preview-serve URL.
 *
 *   • Everything else falls back to a sandboxed iframe pointed at
 *     preview-serve. As more editors are refactored to consume
 *     `useFileIO()`, they move from this fallback into the dispatch
 *     above. No parallel ShareXxxViewer components — Drive and share
 *     use the same editor.
 *
 * Share-link action buttons (Save a copy / Add to my Drive /
 * Download) live unconditionally in the chrome top bar above —
 * the chrome owns share-action UI; editors stay focused on
 * rendering content.
 *
 * Folder shares dispatch into SharedFolderView — a view-only drive
 * view that mirrors the owner's DriveView visual shape minus all
 * write affordances. Edit-perm becomes a banner hint; `.shared`
 * mounts pop a click-gate dialog instead of traversing.
 */
function ShareBody({
  sessionId,
  file,
  share,
  inlineUrl,
  themeClass,
  onAddToDrive,
  addState,
  addMessage,
  onDirtyChange,
  contentDirty,
  canSave,
  saveCoverMessage = null,
  saveRef,
  onRequestSave,
  discardRef,
  onDiscard,
  appRunFolderId,
  shareContext = null,
  openFile,
  openFileError,
  onOpenFile,
  onOpenFileWithSession,
  onCloseFile,
  onFileRenamed,
  openFileRequested = false,
  pdapp = null,
  onOpenPdapp,
  onClosePdappSettings,
  runNonce = 0,
}: {
  sessionId: string
  file: SessionInfo['file']
  share: SessionInfo['share']
  inlineUrl: string
  /** The route session's share context (see shareContextOf). */
  shareContext?: ShareSessionContext | null
  themeClass: string
  onAddToDrive: () => void
  addState: ButtonState
  addMessage: string | null
  onDirtyChange?: (dirty: boolean) => void
  /** The chrome's live dirty flag. Read (not owned) so the phone overlay can
   *  render its own unsaved dot + Save — the top-bar crumb that normally carries
   *  them is suppressed on a phone. */
  contentDirty?: boolean
  /** THE Save/Discard gate, from the chrome: `edit` permission on the ACTIVE file
   *  AND an editor that actually owns the refs mounted for it. Do not re-derive it
   *  from `share.permission` here — that's the ROUTE session, and it says nothing
   *  about whether the OPEN file has a save path. */
  canSave: boolean
  /** #930 / I1 — the editor covers itself (the conflict "Saved version" panel): the cover's notice, else null. The
   *  overlay row's Save is disabled under it and its Discard not offered; the chrome's `requestSave` refuses too. */
  saveCoverMessage?: string | null
  /** Save / Discard handles for the open editor. Owned by the CHROME (its top bar
   *  mounts the desktop controls, and it holds the confirm for discard); the
   *  editor parks its callbacks in them. */
  saveRef: React.MutableRefObject<(() => void) | null>
  /** The chrome's ONE Save (Ctrl+S, the overlay row) — never `saveRef` directly: it is what refuses under the
   *  editor's own cover (#930 / I1). */
  onRequestSave: () => void
  discardRef: React.MutableRefObject<(() => void) | null>
  /** Confirmed discard, from the chrome — see handleDiscard for why it's confirmed. */
  onDiscard: () => void
  /** (Phase 2) The app's owner-side run/manifest folder for a kind:'app' share —
   *  passed to the HTML editor as parentId so the preview's papan:run resolves a
   *  folder (else `no_folder`). Null for non-app shares. */
  appRunFolderId?: string | null
  /** The file opened IN PLACE inside a folder share, or null when the recipient
   *  is on the folder listing. Carries its `parentSid` provenance — see OpenFile. */
  openFile: OpenFile | null
  openFileError: string | null
  onOpenFile: (fileId: string) => void
  /** Open `fileId` in the overlay with a session the server already minted for it. */
  onOpenFileWithSession: (fileId: string, sid: string) => void
  onCloseFile: () => void
  /** A row was renamed in the explorer — the chrome refreshes the open file's info
   *  when it is the renamed one (header, serve URLs, editor by extension). */
  onFileRenamed?: (fileId: string, name: string) => void
  /** A file is on its way (a `?file=` is set, or a PDApp's landing is about to
   *  set one) — the overlay shows a spinner rather than its empty state. */
  openFileRequested?: boolean
  /** (#757) PDApp mode — the share root, or a nested `?pdapp=`, is a `.pdapp`.
   *  Null outside one. */
  pdapp?: SharePdappMode | null
  /** (#757) Open a nested PDApp from the listing / tree (sets `?pdapp=`). */
  onOpenPdapp?: (folderId: string) => void
  /** (#757) Leave the PDApp Settings tab (back to the app tab when it's open). */
  onClosePdappSettings?: () => void
  /** Keys the viewer so a repeated Run remounts (re-runs) the app. */
  runNonce?: number
}) {
  const { t: tFiles } = useTranslation('files')
  // What an app launch decides against, read AFTER its round trip (see
  // handleOpenPdappTarget) — the render the request was made from may be stale.
  // Assigned after COMMIT (a discarded render must not publish its props), and
  // carries the route session so a response for another session is dropped.
  const launchCtxRef = useRef({ sessionId, pdapp, file, share, onOpenPdapp, onOpenFileWithSession })
  useLayoutEffect(() => { launchCtxRef.current = { sessionId, pdapp, file, share, onOpenPdapp, onOpenFileWithSession } })
  // Each launch takes a number; only the LATEST one may act on its response.
  const launchSeqRef = useRef(0)
  // Share-side FileIO impl — load goes through the preview-serve URL
  // the share session already authorizes; save will route through a
  // permission-gated share-save endpoint once that's wired (until
  // then it throws, which the editor surfaces as a save error rather
  // than silently dropping edits).
  const isPhone = useIsPhone()
  const fileIO = useMemo(
    () => shareFileIO({
      inlineUrl,
      shareToken: share.token,
      permission: share.permission,
    }),
    [inlineUrl, share.token, share.permission],
  )

  const navigate = useNavigate()

  // Ctrl/Cmd+S inside the share-view editor. The Drive's save hotkey lives in
  // the workspace chrome (WorkspaceView), which the share-view doesn't mount —
  // so without this the keystroke falls through to the browser's "Save page"
  // dialog. An `edit` link co-edits the owner's live doc (changes already
  // auto-persist through the collab room); this just gives the expected manual
  // flush + suppresses the browser dialog. `view` links have nothing to save,
  // so we don't intercept. Mirrors WorkspaceView.tsx's handler.
  // Through the chrome's `requestSave`, never `saveRef` itself: under the editor's own cover (#930 / I1) the key is
  // refused with the cover's notice — `preventDefault` stays outside it (the browser's Save-page dialog is not what
  // was meant either).
  const canEdit = share.permission === 'edit'
  const onRequestSaveRef = useRef(onRequestSave)
  onRequestSaveRef.current = onRequestSave
  useEffect(() => {
    if (canEdit !== true) return
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        onRequestSaveRef.current()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [canEdit])

  // Sub-file inside a folder share → render the file viewer with a
  // compact SharedFolderView on the left so the recipient can switch
  // between files without going back to the folder listing. The
  // sidebar's onFileSelect mints a fresh sub-session and navigates the
  // chrome in place (no new tab). For single-file shares (rootIsFolder
  // === false), skip the sidebar entirely.
  // The explorer tree needs ~256px it doesn't have on a phone (and it's a
  // right-click/F2 surface anyway) — a sub-file session there renders the viewer
  // full-bleed instead. Recipients who land on a folder share get the listing +
  // in-place overlay above, which is the touch-native way back to other files.
  const showSidebar = share.rootIsFolder && share.rootName && !isPhone

  const switchToFile = async (targetFileId: string) => {
    try {
      const res = await sessionFetch(
        `${getApiBase()}/api/share-links/session/${sessionId}/sub-session`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileId: targetFileId }),
        },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        console.warn('[share-sidebar] sub-session mint failed', body)
        // #1264 — a listed file refused (it may be gone): the trees that show it re-read their folder
        if (res.status === 403 || res.status === 404) window.dispatchEvent(new CustomEvent(SHARE_ROW_REFUSED_EVENT, { detail: { fileId: targetFileId } }))
        return
      }
      const body = (await res.json()) as { sessionId: string }
      goToShareSession(navigate, `/share-view/${body.sessionId}`, { ends: false })  // a sub-session: the same identity
    } catch (err) {
      console.warn('[share-sidebar] sub-session mint network error', err)
    }
  }

  // #openfile — the typed SDK `openFileBy*` request, relayed from an app running
  // inside this share's chrome. Resolves the target WITHIN the share (server-side,
  // link-scoped — the app never supplies the base), mints a sub-session, and
  // navigates the chrome IN-PLACE to it (#9 in-context default). The app gets
  // NOTHING back (no oracle): we never post a result to the iframe.
  //
  // Two sessions are relayed on purpose (#188): the route `sessionId` in the URL
  // sets the SCOPE (the whole shared subtree), while `appSessionId` — the app's own
  // active sub-session — tells the server the app's ANCHOR folder so a relative path
  // resolves from where the app actually lives. Without it, an app running INSIDE a
  // folder (quick_view) share resolves relative paths from the shared folder itself
  // (the route session's fileId is the folder, not the app's HTML), not from the app's
  // own folder inside it. Only sent when an app sub-session is distinct from the route session (an
  // app-anchored share already has the right base); the server re-validates the
  // token (same share, in-scope), so the app can't spoof its own anchor.
  /** #788 — `papan.openPdappByFolderId()` from an app running in this share.
   *  LAUNCH the named app WITHIN what this session covers. The server answers:
   *   • `pdappFolderId` (+ a sub-session) — it is inside this share: open it in
   *     PDApp mode in place (#757) when this chrome can, else run the sub-session;
   *   • `spaPath` — the recipient's own drive, when their NATIVE read is what
   *     reaches it (the same max(native, link) split `openFile` takes);
   *   • `appUrl` — on pd.app, the target's own public address;
   *   • a refusal — the PERSON is told (the launch strip); the APP never is: the
   *     relay is fire-and-forget. */
  const handleOpenPdappTarget = async (target: OpenPdappTargetWire) => {
    const seq = ++launchSeqRef.current
    // A response is acted on only if nothing moved while it was in flight: no newer
    // launch, and the same route session it was resolved against. Otherwise it would
    // open or navigate somewhere the person is no longer asking about.
    const stale = () => seq !== launchSeqRef.current || launchCtxRef.current.sessionId !== sessionId
    try {
      const res = await sessionFetch(
        `${getApiBase()}/api/share-links/session/${sessionId}/open-pdapp`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target }),
        },
      )
      if (stale()) return
      if (!res.ok) {
        // The PERSON is told; the app is not (this runs in the chrome, and nothing
        // is posted back over the bridge). A launch button is meant to reach
        // viewers who cannot open the target, so "nothing happened" is the one
        // outcome this path must not produce.
        console.warn('[share-openpdapp] refused', res.status, await res.json().catch(() => ({})))
        notify(tFiles('shareView.launchPdapp.unavailable'))
        return
      }
      const body = (await res.json()) as { sessionId?: string; spaPath?: string; appUrl?: string; pdappFolderId?: string; entryFileId?: string }
      if (stale()) return
      // Inside THIS share: open it the way the listing does (#757 PDApp mode) —
      // the app tab under the PDApp header, and the route session keeps its
      // scope, so a launch from that app to a sibling still resolves in-share.
      // Read AFTER the round trip: the person may have switched tab or left the
      // PDApp while it was in flight, and the decision is about what is on screen NOW.
      const now = launchCtxRef.current
      const isShowing = !!body.pdappFolderId && now.pdapp?.folderId === body.pdappFolderId
      if (isShowing && now.pdapp) {
        // The app already showing was launched (its own "home" button): run it —
        // on a PDApp root as on a nested one. `?pdapp=` would not change, the
        // landing would not re-fire, and the app would close into an empty panel.
        // On the SETTINGS tab it is already open: pulling the person out of
        // Settings (and its staged edits) is not what a launch asks for.
        if (now.pdapp.face === 'settings') return
        if (now.pdapp.entryId) {
          now.pdapp.onRun()
          return
        }
        // No entry on the client yet (lookup pending or failed): the server's
        // sub-session below still opens it, or the person is told.
      }
      // Only where `?pdapp=` takes effect (`pdappParamTakesEffect`, the rule
      // `resolvePdappFolderId` applies): anywhere else setting it would do
      // NOTHING, so those fall through to the sub-session below.
      if (!isShowing && body.pdappFolderId && now.onOpenPdapp && pdappParamTakesEffect({ file: now.file, share: now.share })) {
        now.onOpenPdapp(body.pdappFolderId)
        return
      }
      // An APP-kind session (this chrome hosts one whenever a pd.app link's
      // openFile minted a sub-session here) can be answered with the target's own
      // PUBLIC address. Without honouring it the launch always refused, for a
      // target that is open to anyone with the link. Shape-checked before use —
      // it is server-minted from the operator's own share domains, and this is the
      // second of the two checks (the BFF makes the same one).
      if (body.appUrl) {
        // Server-minted from the operator's configured share domains — this is the
        // second of the two checks (the BFF makes the same one), so it is about
        // what may become a NAVIGATION: absolute http(s), no credentials in the
        // authority (`user@host` reads as the wrong host to a person), nothing
        // that could break out of an attribute, and a sane length.
        const ok = body.appUrl.length <= 2048
          && /^https?:\/\/[^\s"'<>\\@]+$/.test(body.appUrl)
        const win = ok ? window.open(body.appUrl, '_blank', 'noopener') : null
        // A blocked popup is the "nothing happened" this handler exists to avoid.
        if (!win) notify(tFiles('shareView.launchPdapp.popupBlocked'))
        return
      }
      // #852 — on a FOLDER session (a folder share, or a PDApp-root share) the launched
      // app's entry opens in the OVERLAY, as the listing opens a file: the route session —
      // and so its scope — is kept, and the next launch from that app still reaches its
      // siblings. Navigating to the entry's own sub-session replaced the route with one
      // scoped to the launched app. (The overlay mints the same scope through /sub-session.)
      if (body.sessionId && body.entryFileId && now.file.isFolder) {
        now.onOpenFileWithSession(body.entryFileId, body.sessionId)
        return
      }
      if (!goToOpenFileTarget(navigate, body)) notify(tFiles('shareView.launchPdapp.unavailable'))
    } catch (err) {
      // #1316: the session it was asked for ended — no notice for the next one
      if (isSessionEndedError(err)) return
      console.warn('[share-openpdapp] network error', err)
      notify(tFiles('shareView.launchPdapp.network'))
    }
  }

  // `seq` because the SAME message twice is the common case (two refused clicks),
  // and React bails out on an identical value — the auto-clear effect would not
  // re-run and the second notice would inherit the first one's remaining time.
  const [launchNotice, setLaunchNotice] = useState<{ text: string; seq: number } | null>(null)
  const noticeSeq = useRef(0)
  const notify = (text: string) => setLaunchNotice({ text, seq: ++noticeSeq.current })

  const handleOpenFileTarget = async (target: OpenFileTargetWire) => {
    try {
      const appSessionId = openFile?.sid && openFile.sid !== sessionId ? openFile.sid : undefined
      const res = await sessionFetch(
        `${getApiBase()}/api/share-links/session/${sessionId}/open-file`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(appSessionId ? { target, appSessionId } : { target }),
        },
      )
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}))
        console.warn('[share-openfile] resolve+mint failed', res.status, errBody)
        return
      }
      const body = (await res.json()) as { sessionId?: string; spaPath?: string; openFileId?: string; fileId?: string }
      // #188 — a target reached by resolving the path, possibly THROUGH an in-share `.shared`/
      // `.chain` mount (a bare mount row, or a compound `s_<mount>_<src>` child id). Open it
      // in-place: set `?file`, and the chrome's own openFile effect mints its sub-session via
      // /sub-session — a mount folder BROWSES in, a file OPENS, a cross-tenant compound rides the
      // `xt` path. One code path with the tree-click, so the mount-aware gating lives in one place.
      if (body.openFileId) { onOpenFile(body.openFileId); return }
      // A `_`/`.`/folder the recipient owns NATIVELY → `spaPath` (open in their OWN drive, SPA mode),
      // NOT a share sub-session — mirror the ppn.gg BFF so this second consumer of /open-file doesn't
      // navigate to `/share-view/undefined`. Decision (incl. same-origin guard) in `openFileNavPath`;
      // `null` → silent (no oracle, like couldNotOpen).
      // #852 — the same rule as a launch: on a FOLDER session the opened file shows in the
      // overlay and the route session (its scope) is kept; navigating to the file's own
      // single-file sub-session narrowed every later openFile from it.
      // Opened by id and minted by the overlay itself (/sub-session): /open-file's own session
      // is scoped to the FILE alone, and an HTML file needs its FOLDER (relative css/js/SDK).
      if (body.sessionId && body.fileId && file.isFolder) { onOpenFile(body.fileId); return }
      goToOpenFileTarget(navigate, body)
    } catch (err) {
      console.warn('[share-openfile] network error', err)
    }
  }

  // (Part 3) Re-root the app to an ancestor folder WITHIN the share subtree — the
  // editor "Root:" picker (edit shares only). Mint a sub-session scoped to the chosen
  // root (server validates it's an ancestor of the file AND at-or-under the share root
  // — the single audited gate) and navigate; the `key={sessionId}` remount gives a
  // clean re-handshake at the new root.
  //
  // The re-rooted file is the ACTIVE one. This handler predates the in-place
  // overlay, when ShareBody's `file` prop always WAS the open file — but in the
  // folder branch `file` is the FOLDER, and the overlay hands this same handler
  // to the open file's editor. Left as `file.id` it asked the server to re-root
  // the folder instead of the app, and then navigated to a bare
  // `/share-view/<sid>` that dropped `?file=` — so an edit-share recipient using
  // the app's own root picker was silently thrown out of the app.
  //
  // Navigating away is still correct AFTER this fix: the minted session IS the
  // file (scoped to the chosen root), so the route lands on the non-folder branch
  // and renders the app there, which is exactly what a re-root means.
  // A transient strip for a refused launch. Deliberately NOT the overlay's
  // `openFileError`, which only exists while a file overlay is mounted — a launch
  // can be refused from any share surface.
  useEffect(() => {
    if (!launchNotice) return
    const t = setTimeout(() => setLaunchNotice(null), 6000)
    return () => clearTimeout(t)
  }, [launchNotice])   // keyed by the whole object, so `seq` re-arms it

  const rootChangeFileId = openFile?.fileId ?? file.id
  const onShareRootChange = async (rootId: string) => {
    try {
      const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/sub-session`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId: rootChangeFileId, rootId }),
      })
      if (!res.ok) { console.warn('[share-root] re-root rejected', await res.json().catch(() => ({}))); return }
      const b = (await res.json()) as { sessionId?: string }
      if (b.sessionId) goToShareSession(navigate, `/share-view/${b.sessionId}`, { ends: false })  // a re-root: the same identity
    } catch (err) { console.warn('[share-root] re-root network error', err) }
  }

  // ── Folder share ───────────────────────────────────────────────────────────
  // The listing is the BASE surface and stays mounted for the whole visit; a
  // file opens as an overlay ON TOP of it (its own sub-session — see the chrome's
  // openFile state). Closing just unmounts the overlay, so the recipient lands
  // back on the listing instantly — same folder, same scroll, no refetch, no
  // document load. This is why the listing is not in an `else`: unmounting it
  // to show a file is exactly the churn we're removing.
  /** The refusal strip — portalled, so every layout branch below carries it
   *  without repeating it. Themed explicitly: a portal is outside ThemedArea. */
  const launchStrip = launchNotice
    ? createPortal(
      <div className={`${themeClass} fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg themed-bg-secondary border themed-border text-sm themed-text`} role="status">
        {launchNotice.text}
      </div>,
      document.body,
    )
    : null
  // The panel header over the explorer: "Explorer", or in PDApp mode the
  // "PDApp" label + Run / Settings (#757).
  const panelHeader = <PdappPanelHeader pdapp={pdapp} />
  // (#757) The PDApp tabs. `face` run → the APP TAB: the entry rendered by
  // SharePdappRunView (the app full-bleed), not index.html's HTML editor.
  // `face` settings → the SETTINGS TAB stacked over whatever it was opened over
  // (`viewerFace`: the running app, or the file's editor — kept mounted beneath
  // it, so Settings → back neither restarts the app nor swaps the editor out).
  const face = pdapp?.face ?? null
  const settingsFace = pdapp
    ? (face === 'settings' ? <SharePdappSettings folderId={pdapp.folderId} folderName={pdapp.name} identity={pdapp.settingsIdentity} /> : null)
    : undefined

  if (file.isFolder) {
    // PDApp mode (#757): the explorer is ROOTED AT THE APP (its files are the
    // panel's content, as in the workspace), and the overlay is up for the whole
    // visit — a PDApp never shows as the flat listing of its internals. The
    // listing stays mounted beneath it, so leaving a NESTED PDApp drops the
    // recipient straight back on the folder they opened it from.
    const explorerRootId = pdapp ? pdapp.folderId : share.rootFileId
    const fileIsOpen = !!(openFile || openFileError)
    // What ✕ / Escape does. A file open → close it. In a nested PDApp with no
    // file → leave the app for the listing. A PDApp root with no file has
    // nothing to close (undefined hides the control).
    // The Settings tab closes back to the app tab (or what's beneath it).
    const overlayClose = face === 'settings' ? onClosePdappSettings
      : fileIsOpen ? onCloseFile : pdapp?.onExit ?? undefined
    const entryOpen = !!pdapp && !!openFile && openFile.fileId === pdapp.entryId
    const appName = pdapp?.name ?? null
    // What the overlay's row names: the app on the app tab (it is the app, not
    // "index.html"), "Settings" on the settings tab, else the open file.
    const rowLabel = face === 'settings' ? `${appName ?? 'PDApp'} · Settings`
      : face === 'run' && entryOpen ? (appName ?? undefined)
      : undefined
    return (
      <div className="relative flex-1 min-h-0 flex flex-col">
        {launchStrip}
        <SharedFolderView
          sessionId={sessionId}
          rootFileId={file.id}
          rootName={file.name}
          rootFileType={share.rootIsMount ? share.rootFileType ?? null : null}
          themeClass={themeClass}
          onAddToDrive={onAddToDrive}
          addState={addState}
          addMessage={addMessage}
          onOpenFile={onOpenFile}
          activeFileId={openFile?.fileId ?? null}
          onOpenPdapp={onOpenPdapp}
        />
        {(fileIsOpen || pdapp) && (
          <SharedFileOverlay
            fileId={openFile?.fileId ?? null}
            fileName={openFile?.info.file.name ?? null}
            error={openFileError}
            onClose={overlayClose}
            closeLabel={face === 'settings' ? 'Close settings' : fileIsOpen ? 'Close file' : 'Back to folder'}
            rowLabel={rowLabel}
            panelHeader={panelHeader}
            settingsFace={settingsFace}
            emptyLabel={pdapp ? 'PDApp' : undefined}
            emptyState={pdapp && !fileIsOpen && !openFileRequested
              ? <PdappEmptyState pdapp={pdapp} />
              : undefined}
            // Unsaved-changes dot + Save/Discard. On desktop these live up in the
            // top-bar crumb, but that crumb is suppressed on a phone (it squeezed
            // both crumbs to initials) and there is no Ctrl+S on a phone — so an
            // edit-share recipient could type with no indicator, no way to flush,
            // and no way to revert. Same refs the desktop controls fire.
            dirty={!!contentDirty}
            canSave={canSave}
            coverMessage={saveCoverMessage}
            onSave={onRequestSave}
            onDiscard={onDiscard}
            // The file tree — a sibling switcher, so the recipient can jump
            // between files without bouncing back to the listing each time.
            // Desktop docks it as a column; phone opens it as a drawer over the
            // viewer (256px of permanent sidebar is most of a 390px screen).
            // Selecting a file re-targets the overlay IN PLACE (onOpenFile), so
            // it costs a sub-session mint, not an app reload.
            explorer={(
              <ShareFileExplorer
                // Re-rooting (entering / leaving a nested PDApp) is a different
                // tree — remount rather than reconcile stale expand/child state.
                key={explorerRootId}
                sessionId={sessionId}
                rootFileId={explorerRootId}
                themeClass={themeClass}
                onFileOpen={onOpenFile}
                activeFileId={openFile?.fileId ?? ''}
                onFileRenamed={onFileRenamed}
                onAddToDrive={onAddToDrive}
                addState={addState}
                addMessage={addMessage}
                canEdit={share.permission === 'edit'}
                // A PDApp root has no `?pdapp=` to switch to — its nested
                // folders browse normally. Everywhere else a nested PDApp in the
                // tree opens as an app, same as in the listing.
                onOpenPdapp={pdapp?.isRoot ? undefined : onOpenPdapp}
              />
            )}
          >
            {openFile && <Fragment key={runNonce}>{pdapp?.viewerFace === 'run' && entryOpen ? (
              <SharePdappRunView
                sessionId={openFile.sid}
                shareContext={shareContextOf(openFile.info, openFile.sid)}
                file={openFile.info.file}
                entryPath={openFile.info.share.entryPath}
                appRunFolderId={appRunFolderId ?? null}
                onOpenFileTarget={handleOpenFileTarget}
                onOpenPdappTarget={handleOpenPdappTarget}
              />
            ) : renderFileViewer({
              fileIO,
              file: openFile.info.file,
              share: openFile.info.share,
              sessionId: openFile.sid,
              shareContext: shareContextOf(openFile.info, openFile.sid),
              inlineUrl,
              onSaveRef: saveRef,
              onDiscardRef: discardRef,
              onDirtyChange,
              onOpenFileTarget: handleOpenFileTarget,
              onOpenPdappTarget: handleOpenPdappTarget,
              appRunFolderId,
              onShareRootChange,
            })}</Fragment>}
          </SharedFileOverlay>
        )}
      </div>
    )
  }

  // A direct sub-file session inside a PDApp-root share (#757): the entry on a
  // PDApp tab is the APP TAB (SharePdappRunView), and Settings stacks over it —
  // the same two faces the folder overlay shows, under the same PDApp header.
  const onEntryTab = !!pdapp && pdapp.viewerFace === 'run' && file.id === pdapp.entryId
  const fileViewer = onEntryTab ? (
    <SharePdappRunView
      sessionId={sessionId}
      shareContext={shareContext}
      file={file}
      entryPath={share.entryPath}
      appRunFolderId={appRunFolderId ?? null}
      onOpenFileTarget={handleOpenFileTarget}
      onOpenPdappTarget={handleOpenPdappTarget}
    />
  ) : renderFileViewer({
    fileIO, file, share, sessionId, shareContext, inlineUrl,
    onSaveRef: saveRef,
    onDiscardRef: discardRef,
    onDirtyChange,
    onOpenFileTarget: handleOpenFileTarget,
    onOpenPdappTarget: handleOpenPdappTarget,
    appRunFolderId,
    onShareRootChange,
  })

  const keyedViewer = settingsFace === undefined
    ? <Fragment key={runNonce}>{fileViewer}</Fragment>
    : (
      <PdappFaceStack
        onClose={onClosePdappSettings}
        settings={settingsFace && (
          <>
            <div className="flex h-10 shrink-0 items-center gap-1.5 border-b themed-border px-2">
              <button
                type="button"
                onClick={onClosePdappSettings}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded themed-icon hover:themed-bg-tertiary"
                aria-label="Close settings"
              >
                <X size={14} />
              </button>
              <span className="min-w-0 flex-1 truncate text-sm themed-text">{`${pdapp?.name ?? 'PDApp'} · Settings`}</span>
            </div>
            {settingsFace}
          </>
        )}
      >
        <Fragment key={runNonce}>{fileViewer}</Fragment>
      </PdappFaceStack>
    )

  if (showSidebar) {
    return (
      <>
      {launchStrip}
      <ShareBento
        sidebarHeader={panelHeader}
        sidebar={(
          <ShareFileExplorer
            sessionId={sessionId}
            rootFileId={share.rootFileId}
            themeClass={themeClass}
            onFileOpen={(id) => { void switchToFile(id) }}
            activeFileId={file.id}
            onFileRenamed={onFileRenamed}
            onAddToDrive={onAddToDrive}
            addState={addState}
            addMessage={addMessage}
            canEdit={share.permission === 'edit'}
          />
        )}
        main={keyedViewer}
      />
      </>
    )
  }

  // A single-FILE share on desktop: no tree to dock, but it still gets the
  // workspace bento (the hero card just spans the full width). Returning the
  // viewer bare here is what used to make the SAME editor look like a different
  // product depending on whether you reached it through a folder share or a file
  // one. Phones keep the full-bleed viewer — the card + gutter there would spend
  // 24px of a 390px screen on a border.
  if (!isPhone) return <>{launchStrip}<ShareBento main={keyedViewer} /></>

  // (#757 full review #6) A phone gets no sidebar, and a direct sub-file session
  // has no overlay drawer either — so the PDApp header (Run / Settings) would
  // have no home at all. Pin it as a compact row above the viewer instead.
  if (pdapp) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid="share-pdapp-phone">
        {launchStrip}
        {panelHeader}
        {keyedViewer}
      </div>
    )
  }

  return <>{launchStrip}{keyedViewer}</>
}

/**
 * (#757) The PDApp Settings tab stacked over the viewer. The element tree is
 * the SAME whether or not Settings is showing — the viewer's slot is only
 * hidden (display:none) while Settings covers it — so opening and closing
 * Settings never remounts the running app underneath (MiniAppEditor keeps its
 * run face alive across the Run/Settings toggle for the same reason).
 *
 * #930 / I2 — so it is a COVER (see EditorCover in core-ui): an editor kept mounted beneath Settings is hidden, and
 * Cmd+S saved it unseen. While Settings is up it reports the cover; the chrome's `requestSave` then refuses with
 * "close the settings to save" (only when there is unsaved work) and its way back, `onClose`.
 */
function PdappFaceStack({ settings, onClose, children }: {
  settings: React.ReactNode | null
  /** Closes Settings back to what is beneath it — the cover's way back. */
  onClose?: () => void
  children: React.ReactNode
}) {
  const { t } = useTranslation('collab')
  useCoverEditor(settings && onClose ? {
    message: t('pdappSettings.saveBlocked'),
    actionLabel: t('pdappSettings.close'),
    uncover: onClose,
  } : null)
  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="absolute inset-0 flex flex-col" style={settings ? { display: 'none' } : undefined}>{children}</div>
      {settings ? <div className="absolute inset-0 flex flex-col">{settings}</div> : null}
    </div>
  )
}

/**
 * The share actions (Save a copy · Add to my Drive / Open in my Drive ·
 * Download), collapsed behind a ⋯ on phones — the desktop bar renders them
 * inline, but three nowrap buttons don't fit a 375px row next to the brand,
 * crumb and owner pill.
 *
 * It also carries the open file's Save / Discard on the ONE phone path that has
 * nowhere else to put them: a DIRECT single-file session (a single-file edit
 * share, or a deep link straight to a file). That path renders a bare viewer —
 * no overlay, so no file row — and the top-bar controls are desktop-only because
 * this bar has no room for them at 390px. The result was that a phone recipient
 * of a single-file `edit` share could type into the editor and have NO way to
 * save: no Ctrl+S on a phone, and no button anywhere. (Folder shares were fine —
 * their overlay row carries both.) The ⋯ sheet is already this bar's overflow
 * surface, so they go here rather than growing new chrome.
 */
function ShareActionsMenu({
  showOpenInDrive, driveUrl, downloadUrl, downloadName,
  saveState, addState, canSaveToOwnDrive = true, forbidAddToDrive, onSaveCopy, onAddToDrive,
  canSave, dirty, saveCovered = false, onSaveFile, onDiscardFile,
}: {
  showOpenInDrive: boolean
  driveUrl?: string
  downloadUrl: string
  downloadName: string
  saveState: ButtonState
  addState: ButtonState
  /** The recipient's tenant can receive a copy. False in an identity-only
   *  tenant (§7.2 item 4) — both write CTAs are omitted, Download stays. */
  canSaveToOwnDrive?: boolean
  forbidAddToDrive?: boolean
  onSaveCopy: () => void
  onAddToDrive: () => void
  /** The chrome's single Save/Discard gate — see `canSaveActive`. */
  canSave?: boolean
  dirty?: boolean
  /** #930 / I1 — the editor covers itself (see EditControls): Discard not offered; Save stays tappable (a phone has
   *  no tooltip and no Cmd+S) and `onSaveFile` — the chrome's `requestSave` — answers with the cover's notice. */
  saveCovered?: boolean
  onSaveFile?: () => void
  onDiscardFile?: () => void
}) {
  const [open, setOpen] = useState(false)

  // Same sheet shell + swipe-to-dismiss as the account picker and the Drive's
  // file sheet — a corner dropdown would be the desktop affordance again.
  const row = 'flex w-full items-center gap-3.5 rounded-xl px-3 py-3 text-left text-[15px] themed-text active:themed-bg-tertiary disabled:opacity-50'

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full themed-icon active:themed-bg-tertiary"
        aria-label="Share actions"
      >
        <MoreVertical size={18} />
      </button>
      <MobileSheet open={open} onClose={() => setOpen(false)}>
        {/* The FILE's actions come first and are separated by a rule: they act on
            the document you're editing, while everything below acts on the SHARE.
            Discard only appears once there's something to discard. */}
        {canSave && onSaveFile && onDiscardFile && (
          <>
            <button
              type="button"
              disabled={!dirty}
              aria-disabled={saveCovered || undefined}
              onClick={() => { setOpen(false); onSaveFile() }}
              className={row}
            >
              <Save size={19} className="shrink-0" />
              {dirty ? 'Save' : 'Saved'}
            </button>
            {dirty && !saveCovered && (
              <button
                type="button"
                onClick={() => { setOpen(false); onDiscardFile() }}
                className={row}
              >
                <Undo2 size={19} className="shrink-0" />
                Discard changes
              </button>
            )}
            <div className="mx-3 my-1 border-t themed-border" />
          </>
        )}
        {showOpenInDrive && driveUrl ? (
          <a href={driveUrl} className={row} onClick={() => setOpen(false)}>
            <ExternalLink size={19} className="shrink-0" />
            Open in my Drive
          </a>
        ) : canSaveToOwnDrive ? (
          <>
            <button
              type="button"
              disabled={saveState === 'busy' || saveState === 'success'}
              onClick={() => { setOpen(false); onSaveCopy() }}
              className={row}
            >
              {saveState === 'busy'
                ? <Loader2 size={19} className="shrink-0 animate-spin" />
                : <Copy size={19} className="shrink-0" />}
              {saveState === 'busy' ? 'Saving…' : saveState === 'success' ? 'Saved' : 'Save a copy'}
            </button>
            <button
              type="button"
              disabled={forbidAddToDrive || addState === 'busy' || addState === 'success'}
              onClick={() => { setOpen(false); onAddToDrive() }}
              className={row}
            >
              {addState === 'busy'
                ? <Loader2 size={19} className="shrink-0 animate-spin" />
                : <HardDriveDownload size={19} className="shrink-0" />}
              {addState === 'busy' ? 'Requesting…' : addState === 'success' ? 'Requested' : 'Add to my Drive'}
            </button>
          </>
        ) : null}
        {/* Download is deliberately OUTSIDE that branch: it reads bytes the
            visitor was already granted and writes nothing, so an identity-only
            recipient keeps it. */}
        {downloadUrl && <a href={downloadUrl} download={downloadName} className={row} onClick={() => setOpen(false)}>
          <Download size={19} className="shrink-0" />
          Download
        </a>}
      </MobileSheet>
    </>
  )
}

/**
 * The in-place file viewer for a FOLDER share — an overlay over the still-mounted
 * folder listing (see ShareBody's folder branch).
 *
 * On phones it slides up from the bottom and back down on close, matching the
 * Drive's mobile file view (same `mobile-file-in` / `mobile-file-out` keyframes,
 * so the two surfaces feel like one app). On desktop it just appears.
 */
function SharedFileOverlay({
  fileId, fileName, error, onClose, dirty, canSave, coverMessage = null, onSave, onDiscard, explorer, children,
  closeLabel = 'Close file', panelHeader, rowLabel, settingsFace, emptyState, emptyLabel,
}: {
  /** The OPEN file's id — the identity the drawer-close effect keys on. Names
   *  are not unique (`index.html` at the root and in `_scripts/`), so keying that
   *  effect on the name silently no-ops between same-named files. */
  fileId: string | null
  fileName: string | null
  error: string | null
  /** ✕ / Escape. Undefined ⇒ nothing to close (a PDApp root with no file open):
   *  the control is hidden and Escape only closes the drawer. */
  onClose?: () => void
  /** Accessible name of the ✕ — "Close file", or "Back to folder" when it
   *  leaves a nested PDApp (#757). */
  closeLabel?: string
  /** The explorer panel's header (desktop column + phone drawer) — "Explorer",
   *  or the PDApp header with Run / Settings (#757). */
  panelHeader?: React.ReactNode
  /** (#757) What the file row NAMES, when it isn't the open file's name — the
   *  app's name on the PDApp app tab, "… · Settings" on the settings tab. */
  rowLabel?: string
  /** (#757) PDApp mode's Settings tab: a node stacks it over the viewer, `null`
   *  = PDApp mode with Settings closed. Undefined outside PDApp mode. Passing
   *  null vs a node keeps ONE element tree (PdappFaceStack), so toggling
   *  Settings never remounts the running app beneath it. */
  settingsFace?: React.ReactNode | null
  /** (#757) Shown in the viewer when NO file is open (PDApp mode keeps the
   *  overlay up without one). Absent ⇒ the loading spinner. */
  emptyState?: React.ReactNode
  /** Row label while no file is open (e.g. "PDApp"). */
  emptyLabel?: string
  /** Unsaved changes in the open file. */
  dirty?: boolean
  /** The recipient may write (an `edit` link) — gates the Save/Discard controls. */
  canSave?: boolean
  /** #930 / I1 — the editor's own cover's notice (see EditControls), else null. */
  coverMessage?: string | null
  onSave?: () => void
  /** Confirmed discard (reverts to last saved) — see the chrome's handleDiscard. */
  onDiscard?: () => void
  /** The share's file tree. Docked as a column on desktop; a drawer on phone. */
  explorer?: React.ReactNode
  children?: React.ReactNode
}) {
  const isPhone = useIsPhone()
  const [closing, setClosing] = useState(false)
  const [drawer, setDrawer] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(closeTimer.current), [])

  // Let the exit animation play before unmounting; desktop closes immediately.
  // The animation is only for a close that UNMOUNTS the overlay — closing a file
  // inside PDApp mode (#757) keeps it up, so it must not slide away.
  const close = () => {
    if (!onClose) return
    if (!isPhone || emptyLabel) { onClose(); return }
    setClosing(true)
    closeTimer.current = setTimeout(onClose, 260)
  }

  // Escape peels one layer: the file-tree drawer first, then the file itself.
  //
  // Subscribed ONCE. The handler needs fresh `drawer`/`close`, and the naive way
  // to get that is to omit the dep array — but this overlay re-renders whenever
  // ShareBody does, and the editor's onDirtyChange bubbles up to the chrome's
  // setContentDirty, so on an `edit` share that meant tearing down and rebuilding
  // a document-level keydown listener on EVERY KEYSTROKE. Read the live values
  // through a ref instead.
  const escRef = useRef<{ drawer: boolean; close: () => void; settings: boolean }>({ drawer, close, settings: !!settingsFace })
  escRef.current = { drawer, close, settings: !!settingsFace }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (escRef.current.drawer) { setDrawer(false); return }
      // The PDApp Settings tab is a form, not a layer: Escape there (dismissing
      // autocomplete, a trigger editor, a picker — often portalled outside this
      // overlay) must not tear the tab down and drop unsaved drafts. Same as the
      // in-app MiniAppEditor, which never closes Settings on Escape; ✕ does.
      if (escRef.current.settings) return
      // A key typed into a field or a dialog belongs to it.
      const t = e.target
      if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || t.closest('[role="dialog"]'))) return
      escRef.current.close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Switching files from the drawer re-targets this overlay in place, so close
  // the drawer as soon as the file changes — otherwise it stays open over the
  // file the recipient just asked to see.
  //
  // Keyed on the file ID, NOT the name: names repeat constantly in real trees
  // (`index.html` at the root and in `_scripts/`, a `README.md` per folder), and a
  // name-keyed effect simply never re-runs between two same-named files — the
  // drawer stayed open, covering the file they just picked.
  useEffect(() => { setDrawer(false) }, [fileId])

  const anim = !isPhone ? '' : closing ? 'mobile-file-out' : 'mobile-file-in'

  // `absolute inset-0` (same trick as SingleFileView): take the editor OUT of
  // flow so a wide sandbox/preview can't push its column open — it fills the
  // flex-sized box instead, and shrinks with the viewport.
  const innerBody = error ? (
    <div className="flex flex-1 flex-col items-center justify-center p-8">
      <FileIcon name="" type="file" isFolder={false} fileType={null} size={28} />
      <p className="mt-3 max-w-md text-center text-sm themed-text-muted">{error}</p>
    </div>
  ) : !fileName && emptyState ? (
    <div className="flex flex-1 flex-col items-center justify-center p-8">{emptyState}</div>
  ) : !fileName ? (
    <div className="flex flex-1 items-center justify-center">
      <Loader2 size={18} className="themed-text-muted animate-spin" />
    </div>
  ) : (
    <div className="relative min-h-0 min-w-0 flex-1">
      <div className="absolute inset-0 flex flex-col">{children}</div>
    </div>
  )
  const viewerBody = settingsFace === undefined
    ? innerBody
    : <PdappFaceStack settings={settingsFace} onClose={onClose}>{innerBody}</PdappFaceStack>
  const shownName = rowLabel ?? fileName ?? (emptyState ? emptyLabel : undefined)

  // On phone the editor sits in the SAME rounded `ws-card` the workspace uses
  // (MobileWorkspaceScreen's editor section) — 14px radius + border, and its
  // `overflow: hidden` is what actually clips the sandbox iframe to the curve.
  // The explorer drawer already rides in an identical card, so the two surfaces
  // share one shape. (Desktop gets its card from SplitSidebarLayout instead —
  // `ws-card-hero`, which also has to wrap the file row above the editor.)
  const phoneViewer = <div className="ws-card flex min-h-0 min-w-0 flex-1 flex-col">{viewerBody}</div>

  // The open file's OWN row: ✕ · unsaved dot · name · Save/Discard. Every
  // affordance belonging to the FILE lives here, not in the chrome's top bar —
  // that bar is share-level (folder crumb, owner pill, account, Add to Drive).
  //
  // It renders INSIDE the editor card (as `main`'s first child), exactly where
  // WorkspaceView puts its tab bar: the card's `overflow: hidden` then clips this
  // row to the 16px radius, so the rounded top belongs to the editor rather than
  // being a square strip sitting above a rounded box.
  const desktopFileRow = (
    <div className="flex h-10 shrink-0 items-center gap-1.5 border-b themed-border px-2">
      {onClose ? (
        <button
          type="button"
          onClick={close}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded themed-icon hover:themed-bg-tertiary"
          aria-label={closeLabel}
        >
          <X size={14} />
        </button>
      ) : (
        <span className="w-1 shrink-0" />
      )}
      {dirty && <DirtyDot />}
      <span className="min-w-0 flex-1 truncate text-sm themed-text">{shownName ?? 'Opening…'}</span>
      {canSave && onSave && onDiscard && (
        <EditControls dirty={!!dirty} coverMessage={coverMessage} onSave={onSave} onDiscard={onDiscard} />
      )}
    </div>
  )

  return (
    <div className={`absolute inset-0 z-10 flex min-w-0 flex-col themed-bg-primary ${anim}`}>
      {isPhone && (
        // The workspace's mobile control row, minus the pieces a share doesn't
        // have: no tab-count (a share opens ONE file at a time) and no AI chat.
        <div className="relative flex shrink-0 items-center gap-1 px-2 pt-1.5 pb-1">
          {explorer && (
            <button
              type="button"
              onClick={() => setDrawer((d) => !d)}
              aria-label="File explorer"
              aria-current={drawer ? 'true' : undefined}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg active:themed-bg-tertiary ${
                drawer ? 'text-blue-500' : 'themed-icon'
              }`}
            >
              <FolderTree size={19} />
            </button>
          )}
          {/* Sits LEFT, just after the explorer icon (ml-2 for the gap) — not
              centered in the row. It also SHRINK-WRAPS (no flex-1 / w-full), so
              the ✕ lands right after the filename instead of at the far edge.
              `min-w-0 + truncate` still bounds a long name. */}
          <div
            role="button"
            tabIndex={0}
            // Tapping the label returns to the file (mirrors the workspace,
            // where it drops you back on the editor from the explorer).
            //
            // EXPLICIT aria-label: it wraps the ✕, so without one its
            // accessible name is computed from its contents — "hello.txt
            // Close file" — and it answers to "Close file" alongside the ✕
            // itself. Naming it directly keeps "Close file" pointing at
            // exactly one control (for screen readers, and for the e2e).
            aria-label={fileName ? `Current file: ${fileName}` : 'Current file'}
            onClick={() => setDrawer(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDrawer(false) }}
            // Bare — no `ws-pill` chrome. The workspace pill is a TAB (one of
            // several, and a dropdown target), so it needs a border + fill to
            // read as pressable. Here it's the only file there can be, and it
            // opens nothing — the border was drawing a box around a label.
            className={`ml-2 flex h-9 min-w-0 items-center gap-2 text-base ${
              drawer ? 'themed-text-secondary' : 'themed-text'
            }`}
          >
            <FileIcon name={fileName ?? ''} isFolder={false} fileType={null} />
            <span className="min-w-0 truncate">{shownName ?? 'Opening…'}</span>
            {/* Unsaved-changes dot — the same signal the desktop top bar shows
                next to the file crumb, which is suppressed on a phone. */}
            {dirty && <DirtyDot />}
            {onClose && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); close() }}
                aria-label={closeLabel}
                className="shrink-0 rounded p-0.5 opacity-60 active:themed-bg-tertiary"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Save + Discard — a phone has no Ctrl+S, so without these an
              `edit`-link recipient could type into the editor with no way to
              flush it and no way to back out. The SAME component the desktop top
              bar mounts, firing the SAME refs; `compact` just shrinks them to fit
              a 390px row — both keep their label. Absent entirely on a view-only
              link, where there is nothing to save or discard. */}
          {canSave && onSave && onDiscard && (
            <EditControls dirty={!!dirty} coverMessage={coverMessage} onSave={onSave} onDiscard={onDiscard} compact />
          )}
        </div>
      )}

      {!isPhone ? (
        <ShareBento
          sidebar={explorer}
          sidebarHeader={panelHeader}
          main={<>{desktopFileRow}{viewerBody}</>}
        />
      ) : (
        // Phone: the workspace's exact explorer drawer (MobileWorkspaceScreen) —
        // a LEFT slide-in INSIDE the content area, so it does NOT cover the
        // control row above (the Files icon + file pill stay visible and the
        // Files icon stays lit). 75% wide; the 25% peek is a frosted fog over
        // the file, and tapping it slides the drawer back.
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {phoneViewer}
          {explorer && (
            // ALWAYS MOUNTED, only translated off-screen — remounting would
            // reset the tree's expand + scroll state every time it's opened
            // (and refetch the listing). Same reason the workspace keeps it up.
            //
            // z-50, NOT the workspace's z-30: an HTML app's accounts bar is an
            // `absolute; z-index: 40` iframe whose ancestors never open a
            // stacking context, so it competes in the SAME context as the drawer
            // and painted straight over it — the tree slid in with the "Manage
            // permissions" strip still sitting on top of it.
            <div className={`absolute inset-0 z-50 ${drawer ? '' : 'pointer-events-none'}`}>
              {drawer && (
                <div
                  className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0"
                  onClick={() => setDrawer(false)}
                  aria-hidden
                />
              )}
              <div className={`absolute inset-y-0 left-0 flex w-[75%] flex-col transition-transform duration-300 ease-out ${
                drawer ? 'translate-x-0' : '-translate-x-full'
              }`}>
                {/* No solid backing: the card's rounded RIGHT corners reveal the
                    fog blur behind; the LEFT corners are squared (flush to the
                    screen edge). */}
                <div className="ws-card flex-1 flex min-h-0 flex-col" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                  {/* (#757) The PDApp header (Run / Settings) rides the drawer
                      on a phone — the desktop column's header has no other home. */}
                  {panelHeader}
                  {explorer}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Resizable sidebar layout (mirrors features/workspace/ui/WorkspaceView.tsx)
//
// Same column-drag pattern the workspace uses for its Explorer panel:
// `<sidebar / ResizeHandle / main>` with a recipient-persisted width in
// localStorage. Iframe pointer-events are blocked during the drag so
// embedded editors don't steal the mouse mid-move. Header bar uses the
// same `h-10 border-b themed-border` shape + uppercase label as
// WorkspaceView's "Workspace" header for visual consistency.

const SIDEBAR_LS_KEY = 'share-view.sidebar-width'
const SIDEBAR_MIN = 180
const SIDEBAR_MAX = 600
const SIDEBAR_DEFAULT = 256

/**
 * The share-view's bento row. Dispatches on whether there's a tree to dock:
 *
 *   with a sidebar → SplitSidebarLayout (resizable explorer card + editor card)
 *   without one    → the editor card alone, spanning the row
 *
 * The two are separate because the no-sidebar case has NOTHING to resize: folding
 * it into SplitSidebarLayout meant that path still ran the localStorage read, the
 * width state, the drag refs and the resize handler — none of which could ever
 * fire, and all of which imply a resizable panel that isn't there.
 */
function ShareBento({ sidebar, sidebarHeader, main }: { sidebar?: React.ReactNode; sidebarHeader?: React.ReactNode; main: React.ReactNode }) {
  if (sidebar) return <SplitSidebarLayout sidebar={sidebar} header={sidebarHeader} main={main} />
  return (
    <div className="flex h-full ws-row ws-row-1col">
      <div
        style={{ marginLeft: PANEL_INSET, marginRight: PANEL_INSET }}
        className="ws-card-hero flex-1 min-w-0 flex flex-col"
      >
        {main}
      </div>
    </div>
  )
}

function SplitSidebarLayout({ sidebar, header, main }: { sidebar: React.ReactNode; header?: React.ReactNode; main: React.ReactNode }) {
  const [width, setWidth] = useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(SIDEBAR_LS_KEY)
      const n = raw ? parseInt(raw, 10) : NaN
      if (Number.isFinite(n) && n >= SIDEBAR_MIN && n <= SIDEBAR_MAX) return n
    } catch { /* ignore */ }
    return SIDEBAR_DEFAULT
  })

  // The sidebar element gets its width written directly during a drag (shared
  // startPanelResize loop) — no setState per pointermove, which used to
  // re-render the whole layout (and the embedded editor) 60×/sec.
  const sidebarRef = useRef<HTMLDivElement>(null)
  const blockerRef = useRef<HTMLStyleElement | null>(null)

  const handleResize = (e: React.PointerEvent) => startPanelResize(e, {
    elRef: sidebarRef,
    startWidth: width,
    min: SIDEBAR_MIN,
    max: SIDEBAR_MAX,
    dir: 1,
    onCommit: (w) => {
      setWidth(w)
      try { window.localStorage.setItem(SIDEBAR_LS_KEY, String(w)) } catch { /* ignore */ }
    },
    // Block iframe pointer events so the drag doesn't get stolen mid-move.
    onStart: () => {
      const blocker = document.createElement('style')
      blocker.textContent = 'iframe{pointer-events:none!important}'
      document.head.appendChild(blocker)
      blockerRef.current = blocker
    },
    onEnd: () => {
      if (blockerRef.current) { document.head.removeChild(blockerRef.current); blockerRef.current = null }
    },
  })

  // The workspace's bento, not a hand-rolled lookalike: `ws-row` supplies the
  // 12px insets, the 4px inter-panel gap and the ambient halo; the explorer is a
  // `ws-card` and the editor a `ws-card-hero` (both 14/16px radius, 1px ring,
  // `overflow: hidden` — which is what actually clips a square iframe to the
  // curve). The share-view had been carrying flat `themed-bg-secondary border-r`
  // panels — square, flush, no gutter — so the same editor looked like a
  // different product depending on which surface you reached it through.
  //
  // The 12px side insets live on the PANELS' margins (matching WorkspaceView), not
  // on the row, so the gutter belongs to the cards rather than the container.
  return (
    <div className="flex h-full ws-row ws-row-2col">
      <div
        ref={sidebarRef}
        style={{ width, minWidth: SIDEBAR_MIN, marginLeft: PANEL_INSET }}
        className="ws-card flex flex-col shrink-0"
      >
        {/* "Explorer", or the PDApp header (label + Run / Settings, #757). */}
        {header ?? <PdappPanelHeader pdapp={null} />}
        {sidebar}
      </div>
      {/* The workspace's ACTUAL ResizeHandle, not a lookalike. The inlined copy
          here had drifted (zinc grip vs purple, different hover width, no vertical
          inset) — and the comment justifying it, "avoid adding @papan/workspace as
          a dep", was simply false: apps/web already depends on it. Importing the
          real one is the whole point of putting these two surfaces on one bento. */}
      <ResizeHandle onPointerDown={handleResize} />
      <div
        style={{ marginRight: PANEL_INSET }}
        className="ws-card-hero flex-1 min-w-0 flex flex-col"
      >
        {main}
      </div>
    </div>
  )
}

// File-viewer dispatcher — extracted so the sidebar layout can wrap it
// without re-stating each editor branch.
/**
 * Which SAVING editor (if any) the share-view mounts for a file — and therefore
 * whether that file can be SAVED at all.
 *
 * ONE choice, read by the two places that must never disagree:
 *   • ShareFileBody's dispatch, which mounts the editor and wires
 *     onSaveRef / onDiscardRef / onDirtyChange;
 *   • the chrome's `canSaveActive`, which decides whether to render Save/Discard.
 * Both derive it from `chooseShareEditor` (the drive's editor registry + the share
 * view's caps, I111) over the same file and the same byte probe.
 *
 * The chrome used to INFER saveability from a side-channel — "an editor fired
 * onDirtyChange for this file" — which encoded an unwritten rule (every saveable
 * editor must relay dirty state on mount) that nothing typed, asserted or tested.
 * BOTH bugs on this branch were that rule quietly breaking: a stale dirty flag put an
 * enabled Save (and a destructive Discard) on a .csv that has no save path, and an
 * unkeyed editor that didn't re-relay took Save away from a .md. Deriving the button
 * and the refs behind it from one pure function makes them agree by construction.
 *
 * Dispatch is by EXTENSION through the registry (never by stored mime) — the
 * share-view's old mime-first matching is what downloaded a `.mov` whose mime wasn't
 * `video/*`.
 */
type ShareEditorKind = NonNullable<ReturnType<typeof shareChoiceSaveableKind>>
function shareEditorFor(file: { name: string; isFolder?: boolean }, probe: ShareFileProbe | null, connectionLink = false): ShareEditorKind | null {
  return shareChoiceSaveableKind(chooseShareEditor(file, probe, { connectionLink }))
}

interface FileViewerArgs {
  fileIO: ReturnType<typeof shareFileIO>
  file: SessionInfo['file']
  share: SessionInfo['share']
  sessionId: string
  /** The share context for this session (see shareContextOf). */
  shareContext?: ShareSessionContext | null
  inlineUrl: string
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  /** Revert the open file to its last-saved bytes. Same ref contract as
   *  `onSaveRef` — the editor parks its `discard()` here and the chrome fires it. */
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  onDirtyChange?: (dirty: boolean) => void
  onOpenFileTarget?: (target: OpenFileTargetWire) => void
  /** #788 — LAUNCH another PDApp (`openPdappByFolderId`). */
  onOpenPdappTarget?: (target: OpenPdappTargetWire) => void
  /** (Phase 2) App run/manifest folder → HtmlEditor parentId (clears `no_folder`). */
  appRunFolderId?: string | null
  /** (Part 3) Re-root the app to an ancestor within the share subtree (edit shares). */
  onShareRootChange?: (rootId: string) => void
}

/** The editor kinds the share view mounts from the registry. */
type ShareRegistryEditorChoice = Extract<ShareEditorChoice, { kind: 'text' | 'html-editor' | 'html-preview-only' | 'script' | 'office-docsapi' | 'office-collabora' | 'sqlite-readonly' }>

/**
 * What the share view adds to a registry editor, per kind — its caps, live vs
 * snapshot, and its own wiring. Mounting itself is EditorMount's (#942).
 *   - `readOnly`: what the LINK may do (an edit link co-edits in place) and what
 *     the server allows (SQLite refuses share-kind writes; Collabora's WOPI token
 *     is minted read-only); preview-only is never writable.
 *   - `room` / `live`: the Y.Doc editors join the owner's live room on a live
 *     share (WS token = the share session); office and SQLite have no room.
 */
export function shareEditorProps(
  choice: ShareRegistryEditorChoice,
  ctx: {
    canEdit: boolean
    liveRoom: string
    sessionId: string
    /** The share session's context from its /info (a v2 token seals it, #791). */
    shareContext?: ShareSessionContext | null
    share: FileViewerArgs['share']
    appRunFolderId?: string | null
    onShareRootChange?: (rootId: string) => void
    onOpenFileTarget?: (target: OpenFileTargetWire) => void
    onOpenPdappTarget?: (target: OpenPdappTargetWire) => void
  },
): { readOnly: boolean; room: string; live: boolean; extra: Record<string, unknown> } {
  const { canEdit, liveRoom, sessionId, share } = ctx
  // The share session token IS a valid preview-session token (kind 'share'):
  // passed as previewSessionIdOverride it skips the Drive-authenticated mint
  // (which 403s — the recipient isn't in the owner's tenant).
  const htmlExtra = (canChangeRoot: boolean) => ({
    previewSessionIdOverride: sessionId,
    shareContextOverride: ctx.shareContext ?? undefined,
    parentId: ctx.appRunFolderId ?? undefined,
    shareRootName: share.appFolderName ?? undefined,
    shareCurrentRootId: share.appFolderId ?? undefined,
    shareEntryPath: share.entryPath ?? undefined,
    shareRootOptions: share.rootOptions ?? undefined,
    shareCanChangeRoot: canChangeRoot,
    onShareRootChange: ctx.onShareRootChange,
    onOpenFileTarget: ctx.onOpenFileTarget ? (target: OpenFileTargetWire) => ctx.onOpenFileTarget!(target) : undefined,
    onOpenPdappTarget: ctx.onOpenPdappTarget ? (target: OpenPdappTargetWire) => ctx.onOpenPdappTarget!(target) : undefined,
  })
  switch (choice.kind) {
    case 'text':
      return { readOnly: !canEdit, room: liveRoom, live: true, extra: {} }
    case 'script':
      // No Run: executing the owner's code in the owner's sandbox needs an app
      // identity the share principal lacks. The source still co-edits on an edit link.
      return { readOnly: !canEdit, room: liveRoom, live: true, extra: { hideRun: true } }
    case 'html-editor':
      return { readOnly: !canEdit, room: liveRoom, live: true, extra: htmlExtra(canEdit) }
    case 'html-preview-only':
      // Over the size limit: the last saved page, no live room, on every link.
      // Past the inline ceiling the page is only ever served (never read into the tab).
      return { readOnly: true, room: '', live: false, extra: { ...htmlExtra(false), ...(choice.inlineTooLarge ? { inlineTooLarge: true } : {}) } }
    case 'office-docsapi':
      // The drive's DocsAPI editor in share mode: an edit link edits the owner's
      // file in the owner's session (Save / Discard through the chrome).
      return { readOnly: !canEdit || choice.readOnly === true, room: '', live: false, extra: { documentType: choice.documentType, shareView: true, canViewHistory: share.versionHistory === true } }
    case 'office-collabora':
      // The WOPI mint rides the X-Share-Session header: the server stamps it
      // read-only with a session-scoped WOPISrc (an isolated broker).
      return { readOnly: true, room: '', live: false, extra: {} }
    case 'sqlite-readonly':
      // /api/sqlite refuses every mutation from a share-kind principal.
      return { readOnly: true, room: '', live: false, extra: {} }
  }
}

function renderFileViewer(args: FileViewerArgs) {
  return <ShareFileBody {...args} />
}

const ShareViewerSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 size={20} className="animate-spin themed-text-muted" />
  </div>
)

function ShareFileBody({
  fileIO, file, share, sessionId, shareContext, inlineUrl, onSaveRef, onDiscardRef, onDirtyChange, onOpenFileTarget, onOpenPdappTarget, appRunFolderId, onShareRootChange,
}: FileViewerArgs) {
  // The editor choice — the drive's registry with the share view's caps. When it
  // depends on the bytes (binary sniff / size limit), the probe is the SAME one the
  // chrome's Save gate read: `inlineUrl` is the serve URL it probed.
  const { t: tFiles } = useTranslation('files')
  const connectionLink = share.connectionLink === true
  const firstChoice = chooseShareEditor(file, null, { connectionLink })
  // #1518: a connection's link probes before framing ANY raw choice (`shareRawNeedsProbe`) — a held file's refusal is
  // said in words, never framed.
  const rawProbe = shareRawNeedsProbe(firstChoice, connectionLink)
  const probe = useShareFileProbe(firstChoice.kind === 'probe' || rawProbe ? inlineUrl : null)
  // The raw fallback iframe loads on the session's SERVE token (its content can read its URL).
  const contentUrl = useServeTokenUrl(inlineUrl)
  const sizeChoice: ShareEditorChoice = firstChoice.kind === 'probe' ? chooseShareEditor(file, probe, { connectionLink }) : firstChoice
  // #981 QA 14 (the drive's rule): a csv/tsv with unsaved large-grid edits shows the large grid whatever its size —
  // asked before the read-only preview could hide them.
  const askFragmentEdits = shareNeedsFragmentEditsAnswer(file, sizeChoice)
  const fragmentEdits = useShareFragmentEdits(askFragmentEdits ? file.id : null, sessionId)
  const choice: ShareEditorChoice = !askFragmentEdits
    ? sizeChoice
    : fragmentEdits.pending ? { kind: 'probe' } : chooseShareEditor(file, probe, { hasFragmentEdits: fragmentEdits.hasFragmentEdits, connectionLink })

  // Live vs snapshot, for the Y.Doc editors (HTML / text / scripts).
  //   - LIVE share (`share.liveRoom` set → keyed to the OPENED file): join the
  //     owner's room. An `edit` link CO-EDITS the owner's live doc here; a
  //     `view`/`readcopy` link watches read-only. The WS token is the share
  //     session, which the WS server authorizes against the share grant.
  //   - SNAPSHOT share (`liveRoom` null): empty room → the editor's local mode
  //     loads the latest-saved bytes via FileIO (no WebSocket), read-only.
  // (2026-06-30) `edit` links are ALWAYS live (isShareLive), so in-place edits
  // always flow through the owner's room — never the local-mode FileIO save path
  // (which has no share write endpoint).
  const liveRoom = share.liveRoom ?? ''
  const liveWsToken = share.liveRoom ? sessionId : null
  // #823: the live editor ended by the server (its file went to the bin) comes back when the owner restores it — the
  // session's /info answers 200 again. While binned it answers 404 `file_missing` (a file inside a shared folder) or
  // `share_unavailable` (the link's own root — a file link — binned; also a revoked or expired link, which only
  // means asking every 20 s until the tab is left); anything else: the session is over
  const restoredSeq = useShareEditorRecovery({
    liveRoom,
    check: async () => {
      // raw (#1316): DETECTS whether the session's file came back or the session is over — never dropped by its end
      const res = await fetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/info`, { credentials: 'include' })
      if (res.ok) return 'back'
      const body = await res.json().catch(() => null) as { error?: string } | null
      return res.status === 404 && (body?.error === 'file_missing' || body?.error === 'share_unavailable') ? 'gone' : 'stop'
    },
  })
  // `edit` links co-edit the owner's file in place; everything else is read-only.
  const canEdit = share.permission === 'edit'

  // ── Every editor keys on sessionId ────────────────────────────────────────────
  // All three Y.Doc editors carry `key={sessionId}` so a file switch REMOUNTS them.
  // Without it React reconciles the editor in place: the instance is reused, and its
  // dirty-relay effect (deps [isDirty, onDirtyChange]) does not re-fire when isDirty
  // was false before the switch and false after — so the chrome's per-file dirty key
  // stayed on the PREVIOUS file. HtmlEditor always had the key (it also has to drop a
  // stale app iframe + embed); Markdown and Script did not, which is what made the bug
  // file-type-dependent — the identical action behaved differently for a .html than a
  // .md, invisibly at the call site. Keep them consistent.
  // ──────────────────────────────────────────────────────────────────────────────

  switch (choice.kind) {
    case 'probe':
      return ShareViewerSpinner

    // Read-only viewers (no Y.Doc room, no save): the registry's own preview viewer
    // for the extension. They consume the share FileIO impl directly — load goes
    // through the preview-serve URL the share session authorizes.
    case 'preview': {
      const Viewer = choice.viewer
      return (
        <FileIOProvider impl={fileIO}>
          <Suspense fallback={ShareViewerSpinner}>
            <Viewer key={sessionId} fileId={file.id} fileName={file.name} />
          </Suspense>
        </FileIOProvider>
      )
    }

    case 'text-viewer':
      return (
        <FileIOProvider impl={fileIO}>
          <ReadOnlyText key={sessionId} fileId={file.id} fileName={file.name} />
        </FileIOProvider>
      )

    case 'large-csv':
      // #981: a csv/tsv over the large-CSV threshold — the windowed grid over /api/csv (the share
      // session's auth), read-only on every link.
      return (
        <FileIOProvider impl={fileIO}>
          <LargeCsvGrid key={sessionId} fileId={file.id} fileName={file.name} readOnly />
        </FileIOProvider>
      )

    case 'too-large':
      // Too large even to view: nothing is fetched (the read-only text view and the
      // HTML preview both read the whole file). The top bar's Download gets it.
      return (
        <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="too-large-file">
          <div className="text-center max-w-md px-6">
            <p className="themed-text mb-1">{tFiles('editorHost.tooLargeTitle')}</p>
            <p className="text-sm opacity-60">{tFiles('editorHost.tooLargeBody')}</p>
          </div>
        </div>
      )

    // Every kind below is a REGISTRY editor, mounted by the same rule the drive
    // uses (EditorMount, #942: defaultProps, save hooks only when it may write,
    // the dirty reset, preview-only). What the share view adds — the link's caps,
    // live vs snapshot, the share session's preview id — is the per-kind table
    // `shareEditorProps`; nothing here decides which editor.
    default: {
      const { readOnly, room, extra, live } = shareEditorProps(choice, {
        canEdit, liveRoom, sessionId, shareContext, share, appRunFolderId, onShareRootChange, onOpenFileTarget, onOpenPdappTarget,
      })
      const editable = !readOnly
      const mount = (
        <EditorMount
          registration={choice.registration}
          // Remount on file switch — see "Every editor keys on sessionId" — and after a restore (#823)
          mountKey={`${sessionId}:${restoredSeq}`}
          fileId={file.id}
          fileName={file.name}
          room={room}
          readOnly={readOnly}
          previewOnly={choice.kind === 'html-preview-only'}
          notice={<span className="text-xs themed-text-muted px-2" data-testid="editor-too-large-note">{tFiles('editorHost.tooLargePreviewOnly')}</span>}
          isActive
          onDirtyChange={editable ? onDirtyChange : undefined}
          saveRef={editable ? onSaveRef : undefined}
          discardRef={editable ? onDiscardRef : undefined}
          extra={extra}
          fallback={ShareViewerSpinner}
        />
      )
      return (
        <FileIOProvider impl={fileIO}>
          {live ? <ShareWsTokenProvider token={liveWsToken}>{mount}</ShareWsTokenProvider> : mount}
        </FileIOProvider>
      )
    }

    case 'raw':
      // #1518: a connection's link holds this file back (it holds a keybook value, or is a container that can't be
      // checked) — say so, never frame the refusal's JSON.
      // Never frame before a connection link's probe has answered. Accepted: a clean probe is kept for the URL, so a
      // key bound to this file's value after it answered (before the frame loads) frames the 403 JSON — the bytes are
      // still refused; only the words are lost until the next session URL. Likewise a probe that failed on a network
      // blip answers "binary" and the frame loads — a held file then frames the refusal (re-probed on the next mount).
      if (rawProbe && !probe) return ShareViewerSpinner
      if (probe?.heldSecret) {
        const key = probe.heldSecret === 'held' ? 'shareView.heldSecret' : 'shareView.uncheckableSecret'
        return (
          <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="held-secret-file">
            <div className="text-center max-w-md px-6">
              <p className="themed-text mb-1">{tFiles(`${key}.title`)}</p>
              <p className="text-sm opacity-60">{tFiles(`${key}.body`)}</p>
            </div>
          </div>
        )
      }
      return (
        // Fallback for what nothing in the registry can render here (an
        // unregistered binary type, a Firebird database). /s/* is proxied
        // same-origin with the chrome, so the sandbox MUST NOT combine
        // `allow-scripts` with `allow-same-origin` — that combination
        // would let framed scripts call `parent.document.querySelector(
        // 'iframe').removeAttribute('sandbox')` and escape entirely.
        // What reaches here is application/octet-stream and similar
        // non-executing payloads, so dropping `allow-same-origin` doesn't
        // regress functionality.
        contentUrl ? <iframe
          src={contentUrl}
          sandbox="allow-scripts allow-popups allow-forms allow-downloads"
          referrerPolicy="no-referrer"
          title={file.name}
          className="flex-1 w-full border-0 bg-white"
        /> : <div className="flex-1" />
      )
  }
}

// ─── Account picker (share-view chrome) — mirrors the navbar AvatarMenu ─────────
//
// Same look & structure as the main-app account menu (shared/core-ui AvatarMenu):
// an avatar trigger → header (name + email) → "Switch to" tenant list (Personal +
// orgs, blue check on the active one; multi-email orgs expand to nested email
// rows) → "Log out". Driven by the signed-in user's tenants (my-tenants), so a
// verified-recipient share and a quick-view link share render IDENTICALLY.
// Switching a tenant sets `?tenant=` (the Add/Open-in-Drive target); picking an
// email under an org also writes the active-email override (narrows access on a
// verified share; a harmless no-op on quick-view). #187

// Switching identity mints a NEW share session for the chosen (tenant, email) through the
// EXISTING "Choose your identity" select (POST /share/<token>/choose-identity/select on
// the -api origin, where the session cookie lives): it re-validates the pair against the
// caller's own identities + the link's admission and stores it on the new session — the
// identity every share surface resolves. Never a `?tenant=`/`?orgEmail=` rewrite on the
// SAME session (the chrome would act as one identity while the share resolved as the
// stored one). `pick` carries the one-shot return marker so the chrome restores where
// the visitor was (see the restore effect).
function identitySwitchErrorText(code: string): string {
  switch (code) {
    case 'identity_no_access': return "That identity can't open this share — you're still viewing as before."
    case 'identity_not_owned': return "That identity isn't one of yours — you're still viewing as before."
    default: return "Couldn't switch identity — you're still viewing as before."
  }
}

export function submitIdentitySwitch(token: string, currentSessionId: string, tid: string, email: string, pickMarker: string | null): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${getApiBase()}/share/${encodeURIComponent(token)}/choose-identity/select`
  form.style.display = 'none'
  const field = (name: string, value: string) => {
    const input = document.createElement('input')
    input.type = 'hidden'; input.name = name; input.value = value
    form.appendChild(input)
  }
  field('identity', `${tid}|${email.toLowerCase()}`)
  field('app', window.location.origin)
  // The CURRENT session: the link grant stays keyed on the email verified for it, and a
  // refused pick returns here (`?identity_error=`) instead of dead-ending on the -api host.
  field('session', currentSessionId)
  if (pickMarker) field('pick', pickMarker)
  document.body.appendChild(form)
  submitShareLeave(form, 'share-identity-switched')
}

// Marks the genuine "best access" identity (owner tenant + its primary email) —
// a star instead of a text label, with a "Higher access" tooltip on hover. #187
function BestAccessStar() {
  return (
    <Tooltip content="Higher access" always placement="top" className="inline-flex shrink-0">
      <Star size={12} className="text-amber-400 fill-amber-400" aria-label="Higher access" />
    </Tooltip>
  )
}

// Unsaved-content indicator — same blue dot the Drive tab bar shows on a dirty
// tab (features/workspace/ui/TabBar.tsx). Signals the recipient has unsaved
// edits in the share-view editor.
function DirtyDot() {
  return (
    <span
      className="w-2 h-2 rounded-full shrink-0 bg-blue-400"
      title="Unsaved changes"
      aria-label="Unsaved changes"
    />
  )
}

/**
 * Save + Discard for the open file. ONE component, mounted twice — the desktop
 * top bar and the phone overlay's control row — because they're the same two
 * controls, just in the surface that happens to carry the unsaved dot on that
 * viewport (the top-bar crumb on desktop; the overlay row on a phone, where the
 * crumb is suppressed). The rule is "the dirty indicator and its actions live
 * together", not "each surface reimplements them".
 *
 * Only rendered for an `edit` link — a view-only recipient has nothing to save
 * and nothing to discard, so neither control exists rather than sitting there
 * permanently disabled.
 *
 * Save is a BUTTON, not a menu item: it's the action you take constantly while
 * editing, and on a phone burying it costs a tap plus an aim. Discard is the
 * opposite — rare, destructive, and (see handleDiscard) throws away the OWNER'S
 * unsaved work as well as yours — so it only appears once there is something to
 * discard, and it goes through a confirm.
 */
function EditControls({
  dirty, coverMessage = null, onSave, onDiscard, compact = false,
}: {
  dirty: boolean
  /** #930 / I1 — the open editor covers itself (the conflict "Saved version" panel, the live document hidden
   *  beneath it): its notice, else null. Discard is not offered — it would act on the document the visitor is not
   *  looking at. Save is refused: on desktop it is disabled and its tooltip says why; on a phone (`compact`) a
   *  tooltip never shows and there is no Cmd+S, so it stays TAPPABLE and `onSave` — the chrome's `requestSave` —
   *  answers the tap with the cover's notice and its way back (owner decision, 2026-10-20). */
  coverMessage?: string | null
  onSave: () => void
  onDiscard: () => void
  /** Phone: pin the pair right and shrink them. BOTH keep their label — an
   *  unlabelled ↺ is not a guessable icon, and "discard everything I typed" is
   *  the last control that should rely on a guess. Shrinking (h-8/text-xs, and a
   *  13px glyph) buys the ~50px the word costs, and the file name beside them
   *  truncates anyway, so the row absorbs it. */
  compact?: boolean
}) {
  const btn = compact
    ? 'h-8 px-2 gap-1 text-xs'
    : 'h-7 px-2.5 gap-1.5 text-xs'
  const glyph = compact ? 13 : 12
  return (
    <div className={`flex shrink-0 items-center gap-1.5 ${compact ? 'ml-auto' : ''}`}>
      {dirty && !coverMessage && (
        <button
          type="button"
          onClick={onDiscard}
          aria-label="Discard changes"
          title="Discard unsaved changes and revert to the last saved version"
          className={`themed-btn-secondary flex shrink-0 items-center rounded-lg font-medium whitespace-nowrap ${btn}`}
        >
          <Undo2 size={glyph} />
          <span>Discard</span>
        </button>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || (!!coverMessage && !compact)}
        aria-disabled={coverMessage ? true : undefined}
        aria-label={dirty ? 'Save changes' : 'Saved'}
        // The reason only when there IS work waiting — over a clean editor "close … to save your changes" would imply
        // some (useSaveBlockedNotice's rule).
        title={dirty && coverMessage ? coverMessage : undefined}
        className={`flex shrink-0 items-center rounded-lg font-medium whitespace-nowrap ${btn} ${
          dirty && !coverMessage ? 'themed-btn-primary' : 'themed-text-muted opacity-50'
        }`}
      >
        <Save size={glyph} />
        <span>{dirty ? 'Save' : 'Saved'}</span>
      </button>
    </div>
  )
}

// The "Switch to" selection list — Personal + orgs (multi-email orgs expand to
// nested email rows), a "best access" star fixed to the owner tenant/email, ✓
// tracking `effectiveTenant`/`activeEmail`. Reused by the chrome account dropdown
// (AccountPicker). #187
function AccountChoiceList({
  tenants, userEmail, effectiveTenant, activeEmail, personalEmail = null, bestTenant, bestEmail,
  defaultExpandedOrg, onPick,
}: {
  tenants: TenantInfo[]
  userEmail: string
  /** Tenant whose row shows the ✓ (explicit selection, else best access). */
  effectiveTenant: string | null
  /** Acting org-email (?orgEmail=) — drives the ✓ on email rows; null = primary. */
  activeEmail: string | null
  /** The email the PERSONAL account joined this link as (#794) — its email rows' ✓. */
  personalEmail?: string | null
  /** FIXED "(best access)" designation (owner tenant + its primary email). */
  bestTenant: string | null
  bestEmail: string
  /** Org to auto-expand when the user hasn't toggled one. */
  defaultExpandedOrg: string | null
  onPick: (tid: string, email?: string) => void
}) {
  // `null` = use defaultExpandedOrg (auto-expand the effective org); '' = collapsed.
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null)
  const personal = tenants.find((t) => t.type === 'personal')
  const orgs = tenants.filter((t) => t.type === 'organization')
  if (tenants.length === 0) return null

  return (
    <div className="py-1">
      <div className="px-3 py-1 text-xs themed-text-muted uppercase tracking-wide">Switch to</div>

      {personal && (() => {
        // ONE Personal account; with several verified emails the link accepts, the email it
        // joins this link as is chosen HERE (#794) — the email the link grant, consent and
        // the app's view of you key on. Expanded by default while it is the active account.
        const multi = personal.emails.length > 1
        const expanded = multi && (expandedOrg ?? (effectiveTenant === personal.id ? personal.id : null)) === personal.id
        return (
          <div>
            <button
              type="button"
              onClick={() => (multi
                ? setExpandedOrg(expanded ? '' : personal.id)
                : onPick(personal.id, personal.emails[0]?.email))}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm themed-text hover:themed-bg-tertiary transition-colors"
            >
              <User size={14} className="shrink-0 themed-text-muted" />
              <span className="flex items-center gap-1 flex-1 min-w-0 text-left">
                <span className="truncate">Personal</span>
                {bestTenant === personal.id && <BestAccessStar />}
              </span>
              {effectiveTenant === personal.id && <Check size={14} className="text-blue-400 shrink-0" />}
            </button>
            {expanded && (
              <div className="ml-6 border-l themed-border pl-2" data-testid="personal-email-selector">
                {personal.emails.map((e) => {
                  const el = e.email.toLowerCase()
                  const selected = effectiveTenant === personal.id && personalEmail === el
                  return (
                    <button
                      key={e.email}
                      type="button"
                      onClick={() => onPick(personal.id, e.email)}
                      className="w-full flex items-center gap-2 px-2 py-1 text-xs themed-text-secondary hover:themed-bg-tertiary transition-colors"
                    >
                      <Mail size={12} className="shrink-0 themed-text-muted" />
                      <span className="flex items-center gap-1 flex-1 min-w-0 text-left">
                        <span className="truncate">{e.email}</span>
                      </span>
                      {selected && <Check size={12} className="text-blue-400 shrink-0" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })()}

      {orgs.map((org) => {
        const isActive = effectiveTenant === org.id
        const orgEmails = org.emails ?? []
        if (orgEmails.length > 1) {
          const isExpanded = (expandedOrg ?? defaultExpandedOrg) === org.id
          // This org's primary email — acting as it needs no explicit ?orgEmail=
          // ("act as primary"); the others pin a specific verified email.
          const orgPrimary = (orgEmails.find((e) => e.email.toLowerCase() === userEmail.toLowerCase())?.email ?? orgEmails[0]?.email ?? '').toLowerCase()
          return (
            <div key={org.id}>
              <button
                type="button"
                onClick={() => setExpandedOrg(isExpanded ? '' : org.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm themed-text hover:themed-bg-tertiary transition-colors"
              >
                <Building2 size={14} className="shrink-0 themed-text-muted" />
                <span className="flex items-center gap-1 flex-1 min-w-0 text-left">
                  <span className="truncate">{org.name}</span>
                  {bestTenant === org.id && <BestAccessStar />}
                </span>
                {isActive && <Check size={14} className="text-blue-400 shrink-0" />}
              </button>
              {isExpanded && (
                <div className="ml-6 border-l themed-border pl-2">
                  {orgEmails.map((e) => {
                    const el = e.email.toLowerCase()
                    // "(best access)" is FIXED to the owner tenant's primary email;
                    // the ✓ tracks the current selection (?orgEmail=, or this org's
                    // primary when none is set).
                    const isBestAccess = org.id === bestTenant && el === bestEmail
                    const selected = isActive && (activeEmail ? activeEmail.toLowerCase() === el : el === orgPrimary)
                    return (
                      <button
                        key={e.email}
                        type="button"
                        onClick={() => onPick(org.id, el === orgPrimary ? undefined : e.email)}
                        className="w-full flex items-center gap-2 px-2 py-1 text-xs themed-text-secondary hover:themed-bg-tertiary transition-colors"
                      >
                        <Mail size={12} className="shrink-0 themed-text-muted" />
                        <span className="flex items-center gap-1 flex-1 min-w-0 text-left">
                          <span className="truncate">{e.email}</span>
                          {isBestAccess && <BestAccessStar />}
                        </span>
                        {selected && <Check size={12} className="text-blue-400 shrink-0" />}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        }
        return (
          <button
            key={org.id}
            type="button"
            onClick={() => onPick(org.id)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm themed-text hover:themed-bg-tertiary transition-colors"
          >
            <Building2 size={14} className="shrink-0 themed-text-muted" />
            <span className="flex items-center gap-1 flex-1 min-w-0 text-left">
              <span className="truncate">{org.name}</span>
              {bestTenant === org.id && <BestAccessStar />}
            </span>
            {isActive && <Check size={14} className="text-blue-400 shrink-0" />}
          </button>
        )
      })}
    </div>
  )
}

function AccountPicker({
  userName,
  userEmail,
  tenants,
  activeTenant,
  activeEmail,
  personalEmail = null,
  bestTenant,
  bestEmail,
  onSignOut,
  onPickIdentity,
}: {
  /** Signed-in user's display name + email (Better Auth session) — the menu header. */
  userName: string
  userEmail: string
  /** The user's tenants (fetched once by the chrome, shared with the resolver). */
  tenants: TenantInfo[]
  /** Current selection: tenant (?tenant=) + acting org-email (?orgEmail=, null = primary). */
  activeTenant: string | null
  activeEmail: string | null
  /** The email the PERSONAL account joined this link as (#794). */
  personalEmail?: string | null
  /** Genuine "best access" tenant + email — FIXED designation computed by the chrome
   *  (the same values the resolver resolves against when no override is set). */
  bestTenant: string | null
  bestEmail: string
  /** Log out — revokes the share session + ends the Better Auth session + returns
   *  to the verify gate. Owned by the chrome (it holds the sessionId + shareToken). */
  onSignOut: () => void
  /** A pick switches identity: APP shares re-bind the app session; every other share
   *  mints a new session through the choose-identity select (`submitIdentitySwitch`). */
  onPickIdentity: (tid: string, email?: string) => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const isPhone = useIsPhone()

  useEffect(() => {
    // The phone path is an AccountSheet, which owns its own fog-tap +
    // Escape dismissal. Wiring these window listeners there too would let the
    // opening tap itself fall through and close the sheet on the same gesture.
    if (!open || isPhone) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const id = requestAnimationFrame(() => {
      window.addEventListener('click', handler)
      window.addEventListener('keydown', onEsc)
    })
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('click', handler)
      window.removeEventListener('keydown', onEsc)
    }
  }, [open, isPhone])

  // Sign out — delegated to the chrome (it owns the sessionId + shareToken).
  // Revokes the share session AND ends the Better Auth session, then returns the
  // recipient to the verify gate. Safe HERE (native top-level chrome) — the #187
  // clickjack risk was the in-frame embed bar, not this. #187
  const signOut = () => {
    setOpen(false)
    onSignOut()
  }

  // Effective selection for the ✓ + avatar + auto-expand: the explicit ?tenant=
  // when present, else the best-access tenant — so on first open (no params) the
  // default (best access) shows ticked rather than nothing. The "(best access)"
  // label always tracks bestTenant; only the ✓ falls back to it.
  const effectiveTenant = activeTenant ?? bestTenant
  const effectiveTenantObj = tenants.find((t) => t.id === effectiveTenant)
  const isOrgEffective = effectiveTenantObj?.type === 'organization'
  const defaultExpandedOrg = isOrgEffective ? effectiveTenant : null
  // Avatar mirrors AvatarMenu: org → org initials + org-seeded color; else the user.
  const avatarSeed = isOrgEffective && effectiveTenantObj ? effectiveTenantObj.name : (userEmail || userName || 'user')
  const avatarText = isOrgEffective && effectiveTenantObj
    ? getInitials(effectiveTenantObj.name)
    : getInitials(userName || userEmail || '?')

  const choiceList = (
    <AccountChoiceList
      tenants={tenants}
      userEmail={userEmail}
      effectiveTenant={effectiveTenant}
      activeEmail={activeEmail}
      personalEmail={personalEmail}
      bestTenant={bestTenant}
      bestEmail={bestEmail}
      defaultExpandedOrg={defaultExpandedOrg}
      onPick={(tid, email) => { setOpen(false); onPickIdentity(tid, email) }}
    />
  )

  const avatar = (
    <button
      ref={btnRef}
      type="button"
      onClick={() => setOpen((v) => !v)}
      title="Account"
      className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium text-white shrink-0 transition-[filter] hover:brightness-110 ${avatarBg(avatarSeed)}`}
    >
      {avatarText}
    </button>
  )

  // Phone: THE app's account switcher — the same full-screen frosted card sheet
  // the mobile launcher uses (AccountSheet), not a share-view lookalike.
  // A 256px dropdown anchored to a 28px avatar is a desktop affordance.
  //
  // The share view's identity model is per-(tenant, email) like the launcher's,
  // with one extra: the ★ marks the genuine "best access" identity — the one the
  // resolver resolves against when no ?tenant=/?orgEmail= override is set.
  if (isPhone) {
    const rows: IdentityRow[] = tenants.flatMap((t): IdentityRow[] => {
      if (t.type === 'personal') {
        // ONE Personal account; with several verified emails the link accepts, each email is
        // a row — the email it joins as is chosen here (#794).
        if (t.emails.length > 1) {
          return t.emails.map((e, i) => ({
            key: `${t.id}:${e.email}`,
            tenantId: t.id,
            kind: 'personal' as const,
            email: e.email,
            label: userName || 'Personal',
            sub: e.email,
            tag: 'Personal',
            seed: userEmail || userName || 'user',
            initials: getInitials(userName || userEmail || '?'),
            active: t.id === effectiveTenant && personalEmail === e.email.toLowerCase(),
            best: t.id === bestTenant && i === 0,
          }))
        }
        return [{
          key: t.id,
          tenantId: t.id,
          kind: 'personal',
          ...(t.emails[0] ? { email: t.emails[0].email } : {}),
          label: userName || 'Personal',
          sub: t.emails[0]?.email ?? userEmail,
          tag: 'Personal',
          seed: userEmail || userName || 'user',
          initials: getInitials(userName || userEmail || '?'),
          active: t.id === effectiveTenant,
          best: t.id === bestTenant,
        }]
      }
      // Multi-email orgs switch per EMAIL — each is a distinct permission
      // identity, so they're distinct rows (same as the launcher).
      const emails = t.emails.length > 1 ? t.emails : t.emails.slice(0, 1)
      return emails.map((e) => ({
        key: t.emails.length > 1 ? `${t.id}:${e.email}` : t.id,
        tenantId: t.id,
        kind: 'organization' as const,
        email: e.email,
        label: t.name,
        sub: e.email,
        tag: e.role,
        seed: t.name,
        initials: getInitials(t.name),
        active: t.id === effectiveTenant
          && (t.emails.length > 1 ? (activeEmail ?? bestEmail) === e.email : true),
        best: t.id === bestTenant && (t.emails.length <= 1 || e.email === bestEmail),
      }))
    })

    return (
      <>
        {avatar}
        {open && (
          <AccountSheet
            portal
            rows={rows}
            onClose={() => setOpen(false)}
            onPick={(row) => {
              setOpen(false)
              if (row.active) return
              onPickIdentity(row.tenantId, row.email)
            }}
          >
            {/* No "New organization" here — a share recipient is looking at
                someone else's file; org creation belongs in the app proper. */}
            <LogOutCard onClick={() => { void signOut() }} />
          </AccountSheet>
        )}
      </>
    )
  }

  return (
    <div className="relative">
      {avatar}
      {open && (
        <div
          ref={menuRef}
          className="absolute top-full right-0 mt-1 themed-bg-secondary border themed-border rounded-lg shadow-xl w-64 py-2 z-50"
        >
          {/* User info */}
          <div className="px-3 py-2 border-b themed-border">
            <div className="text-sm font-medium themed-text truncate">{userName || userEmail || 'Account'}</div>
            {userEmail && <div className="text-xs themed-text-muted truncate">{userEmail}</div>}
          </div>

          {/* Tenant switcher (shared list) */}
          {choiceList}

          {/* Actions */}
          <div className="border-t themed-border py-1">
            <button
              type="button"
              onClick={() => { void signOut() }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:themed-bg-tertiary transition-colors"
            >
              <LogOut size={14} className="shrink-0" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}





const pickContextKey = (token: string) => `share-pick-ctx:${token}`

/** A fresh one-shot marker for a /choose-identity detour (URL-safe, 32 chars). */
function newPickMarker(): string {
  try { return crypto.randomUUID().replace(/-/g, '') } catch { return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}` }
}

export function stashPickContext(token: string, marker: string, ctx: ShareLandingContext): void {
  try { sessionStorage.setItem(pickContextKey(token), JSON.stringify({ marker, ctx })) } catch { /* best-effort */ }
}

export function clearPickContext(token: string): void {
  try { sessionStorage.removeItem(pickContextKey(token)) } catch { /* best-effort */ }
}

/** The stashed context for THIS detour — only when the marker matches — read AND cleared
 *  (one-shot). A different / missing marker leaves nothing restored. */
export function takePickContext(token: string, marker: string): ShareLandingContext | null {
  try {
    const raw = sessionStorage.getItem(pickContextKey(token))
    if (!raw) return null
    const stash = JSON.parse(raw) as { marker?: string; ctx?: unknown }
    if (stash?.marker !== marker) return null
    sessionStorage.removeItem(pickContextKey(token))
    return parseLandingContext(stash.ctx)
  } catch {
    return null
  }
}
